/**
 * RouteChef Trip Planner — routechef.com/trip-planner
 * v5: Step-wise right panel (desktop) · UX-only change, zero logic changes
 *
 * Changes from v4:
 *  - Desktop: right panel is ALWAYS visible from start, with 3 numbered step tabs:
 *      Step 1 Routing  (InlineRouteSheet — its own Outbound/Return sub-tabs)
 *      Step 2 Discover (PreferenceScreen + swipeable PlaceDeck image cards)
 *      Step 3 Itinerary (ItineraryPanel)
 *  - Stage-driven auto-switching of the active step tab
 *  - Mobile flow untouched (inline chat components + fullscreen itinerary overlay)
 *  - NO API / consumeSSE / WS / handler changes — pure render relocation
 *
 * Changes from v3:
 *  - Real SVG logo from /logo_new.svg + "Routechef" wordmark matching brand
 *  - Chip filter bar removed
 *  - Place cards: Duolingo-style single stacked card deck with big ✕/✓ buttons
 *  - Preference screen before place cards (optional, skippable)
 *  - Flow fix: after selecting places & tapping Build, agent bubbles + itinerary actually appear
 *  - consumeSSE onPlaces + onItinerary both wired through correctly
 */

import { useState, useEffect, useRef, useCallback } from "react";
import HotelBooking from "./HotelBooking";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Navbar from "./components/Navbar";
import LoginModal from "./components/LoginModal";

import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useAuth } from "./context/AuthContext";

const firebaseConfig = {
  apiKey: "AIzaSyDfdKexK4kTp2mGgceuYTJgsS4_4ba9XOo",
  authDomain: "routechef-b1c5f.firebaseapp.com",
  projectId: "routechef-b1c5f",
  storageBucket: "routechef-b1c5f.appspot.com",
  messagingSenderId: "863459882866",
  appId: "1:863459882866:web:a224e5dcfb66a13ab48feb",
  measurementId: "G-V9KMLS73T7",
};

const app = !getApps().length ? initializeApp(firebaseConfig) : getApp();
const auth = getAuth(app);

/* ── TOKENS ─────────────────────────────────────────────────────────── */
const API = "http://localhost:8000";

// ── Deployed Lambda API ───────────────────────────────────────────────────────
// localhost (npm start) → /development → :development alias ($LATEST)
// production build      → /            → :production alias
const LAMBDA_BASE = "https://dw5wuw157e.execute-api.ap-south-1.amazonaws.com";
const IS_DEV = process.env.NODE_ENV === "development";
const LAMBDA_API = IS_DEV ? `${LAMBDA_BASE}/development` : LAMBDA_BASE;
const USE_LAMBDA = true;
const ACTIVE_API = USE_LAMBDA ? LAMBDA_API : API;

// ── Persistent user identity ──────────────────────────────────────────────────
// Generated once and stored in localStorage. Format: webbot_trip:<random8>
// Used as DynamoDB PK to track chat history across sessions.
function getOrCreateUserId() {
  const KEY = "routechef_user_id";
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      const rand = Math.random().toString(36).slice(2, 10);
      id = `webbot_trip:${rand}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch (_) {
    // Fallback if localStorage is unavailable (incognito strict mode etc.)
    return `webbot_trip:${Math.random().toString(36).slice(2, 10)}`;
  }
}
const USER_ID = getOrCreateUserId();
const PURPLE = "#6B3FA0";
const ORANGE = "#F4560A";
const GREEN = "#00A86B";
const RED = "#E8334A";
const AMBER = "#F59E0B";

/* ── WEBSOCKET ROUTING ───────────────────────────────────────────────── */
const WS_URL = "wss://h5nkqou4f7.execute-api.ap-south-1.amazonaws.com/production";
const WS_API_KEY = "web_dev_1";
// Seat availability check API (train class re-check from route cards)
const AVAIL_API = "https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development";

/* ── Long-running LLM endpoints (/chat, /select-places, /api/intake) go to a
   Lambda Function URL to escape API Gateway's hard ~29s timeout (discovery
   with 15 places and itinerary builds legitimately exceed it). Paste the
   Function URL below once created; while empty, everything falls back to
   ACTIVE_API so nothing breaks. NOTE: Function URLs have no /development
   stage prefix — use the URL exactly as AWS gives it. ── */
const LONG_RUNNING_API = ""; // e.g. "https://xxxxxxxx.lambda-url.ap-south-1.on.aws"
const LONG_ENDPOINTS = ["/chat", "/select-places"];

/* ── Interest → place matching for the discovery deck ──
   Places whose type/name/tags match the user's selected interests are
   shown first; the deck is capped at MAX_DECK_PLACES either way. */
const MAX_DECK_PLACES = 15;
const PREF_TYPE_KEYWORDS = {
  adventure:   ["adventure", "trek", "hike", "hiking", "rafting", "paragliding", "zipline", "sport", "climb", "kayak", "surf", "scuba", "dive", "boating", "camp", "ski"],
  culture:     ["culture", "cultural", "temple", "church", "monastery", "mosque", "museum", "art", "gallery", "heritage", "palace", "fort", "monument", "market", "bazaar"],
  nature:      ["nature", "park", "garden", "lake", "waterfall", "falls", "beach", "hill", "valley", "viewpoint", "forest", "island", "cave", "dam", "river", "peak", "sanctuary"],
  food:        ["food", "cafe", "restaurant", "street", "local", "cuisine", "brewery", "winery", "market", "bakery"],
  relaxation:  ["relax", "spa", "beach", "resort", "promenade", "garden", "lake", "cruise", "sunset", "hot spring"],
  photography: ["photo", "viewpoint", "sunset", "sunrise", "scenic", "panorama", "lighthouse", "bridge", "architecture", "skyline"],
  history:     ["history", "historic", "fort", "palace", "ruins", "memorial", "museum", "heritage", "monument", "tomb", "archae", "caves"],
  wildlife:    ["wildlife", "zoo", "safari", "sanctuary", "national park", "bird", "aquarium", "reserve"],
};
function orderPlacesForInterests(places, interests, cap = MAX_DECK_PLACES) {
  const list = Array.isArray(places) ? places : [];
  if (!Array.isArray(interests) || interests.length === 0) return list.slice(0, cap);
  const kws = interests.flatMap(i => PREF_TYPE_KEYWORDS[i] || []);
  const score = (p) => {
    const hay = `${p.type || ""} ${p.name || ""} ${(Array.isArray(p.tags) ? p.tags.join(" ") : "")}`.toLowerCase();
    let s = 0;
    for (const k of kws) if (hay.includes(k)) s += 1;
    return s;
  };
  return list
    .map((p, i) => ({ p, i, s: score(p) }))
    .sort((a, b) => (b.s - a.s) || (a.i - b.i)) // interest matches first, otherwise stable
    .slice(0, cap)
    .map(x => x.p);
}

/* ── Derive hotel check-in/check-out from the picked routes ──
   Check-in = outbound arrival date; check-out = return departure date,
   falling back to check-in + nights. Returns dd-mm-yyyy strings. */
function deriveStayDates(outboundPicked, returnPicked, nightsVal) {
  const dmy = (d) => [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join("-");
  let cin = null, cout = null;
  const outNodes = outboundPicked?.rawRoute?.nodes;
  const arr = outNodes?.[outNodes.length - 1]?.segment?.dest_arr_dt;
  if (arr) { const d = new Date(arr); if (!isNaN(d)) cin = d; }
  const retNodes = returnPicked?.rawRoute?.nodes;
  const dep = retNodes?.[0]?.segment?.origin_dep_dt;
  if (dep) { const d = new Date(dep); if (!isNaN(d)) cout = d; }
  if (!cin) return null;
  if (!cout || cout <= cin) {
    cout = new Date(cin);
    cout.setDate(cout.getDate() + Math.max(1, nightsVal || 1));
  }
  return { cin: dmy(cin), cout: dmy(cout) };
}

function stashHotelForNavigation(hotel, checkIn, checkOut, selectedRoom = null, images = []) {
  try {
    sessionStorage.setItem("rc_hotel_handoff_" + hotel.hotel_code, JSON.stringify({
      hotel_code: hotel.hotel_code,
      hotel_name: hotel.hotel_name,
      distance_km: hotel.distance_km,
      check_in: checkIn,
      check_out: checkOut,
      selectedRoom,
      images,
      ...hotel,
    }));
  } catch (e) {
  }
}

// Generate a short random session id
function mkSessionId() {
  return Math.random().toString(36).slice(2, 10);
}

/* ── Best-effort country inference from a user-entered place name ──
   Used to pass country_name in /hotels requests. Returns "" when the
   country can't be confidently inferred (key is then omitted). */
const PLACE_COUNTRY_MAP = {
  "dubai": "United Arab Emirates", "abu dhabi": "United Arab Emirates", "sharjah": "United Arab Emirates",
  "singapore": "Singapore",
  "bangkok": "Thailand", "phuket": "Thailand", "pattaya": "Thailand", "krabi": "Thailand", "chiang mai": "Thailand",
  "bali": "Indonesia", "jakarta": "Indonesia",
  "kuala lumpur": "Malaysia", "langkawi": "Malaysia", "penang": "Malaysia", "genting": "Malaysia",
  "kathmandu": "Nepal", "pokhara": "Nepal",
  "colombo": "Sri Lanka", "kandy": "Sri Lanka", "galle": "Sri Lanka", "ella": "Sri Lanka",
  "male": "Maldives", "maldives": "Maldives",
  "paris": "France", "nice": "France", "lyon": "France",
  "london": "United Kingdom", "manchester": "United Kingdom", "edinburgh": "United Kingdom",
  "rome": "Italy", "venice": "Italy", "milan": "Italy", "florence": "Italy",
  "barcelona": "Spain", "madrid": "Spain",
  "amsterdam": "Netherlands",
  "zurich": "Switzerland", "geneva": "Switzerland", "interlaken": "Switzerland", "lucerne": "Switzerland",
  "new york": "United States", "los angeles": "United States", "san francisco": "United States", "las vegas": "United States", "chicago": "United States",
  "tokyo": "Japan", "osaka": "Japan", "kyoto": "Japan",
  "seoul": "South Korea",
  "hong kong": "Hong Kong",
  "sydney": "Australia", "melbourne": "Australia",
  "istanbul": "Turkey", "cappadocia": "Turkey",
  "cairo": "Egypt",
  "doha": "Qatar", "muscat": "Oman", "manama": "Bahrain",
  "baku": "Azerbaijan", "tashkent": "Uzbekistan", "almaty": "Kazakhstan", "tbilisi": "Georgia",
  "ho chi minh": "Vietnam", "hanoi": "Vietnam", "da nang": "Vietnam",
  "phnom penh": "Cambodia", "siem reap": "Cambodia",
  "mauritius": "Mauritius", "seychelles": "Seychelles",
  "thimphu": "Bhutan", "paro": "Bhutan",
  "dhaka": "Bangladesh",
};

const INDIA_PLACES = new Set([
  "delhi", "new delhi", "mumbai", "bengaluru", "bangalore", "chennai", "kolkata", "hyderabad", "pune",
  "goa", "jaipur", "udaipur", "jodhpur", "jaisalmer", "pushkar", "bikaner", "ajmer", "mount abu",
  "agra", "varanasi", "mathura", "vrindavan", "lucknow", "kanpur", "prayagraj", "allahabad", "khajuraho",
  "rishikesh", "haridwar", "dehradun", "mussoorie", "nainital", "jim corbett", "auli", "kedarnath", "badrinath",
  "manali", "shimla", "dharamshala", "mcleodganj", "kasol", "spiti", "dalhousie",
  "leh", "ladakh", "srinagar", "gulmarg", "pahalgam", "jammu", "katra", "vaishno devi",
  "amritsar", "chandigarh", "ahmedabad", "surat", "vadodara", "rann of kutch", "dwarka", "somnath",
  "indore", "bhopal", "ujjain", "gwalior", "nagpur", "raipur",
  "kochi", "cochin", "munnar", "alleppey", "alappuzha", "thekkady", "wayanad", "kovalam", "varkala",
  "trivandrum", "thiruvananthapuram", "kozhikode", "calicut", "thrissur", "kollam", "kottayam", "kumarakom",
  "coimbatore", "ooty", "kodaikanal", "madurai", "rameswaram", "kanyakumari", "pondicherry", "puducherry",
  "thanjavur", "tiruchirappalli", "trichy", "vellore", "salem", "yercaud", "yelagiri",
  "mysore", "mysuru", "coorg", "madikeri", "chikmagalur", "chikmaglur", "hampi", "gokarna", "udupi",
  "mangalore", "mangaluru", "hubli", "belgaum", "sakleshpur", "kabini", "bandipur",
  "darjeeling", "gangtok", "kalimpong", "shillong", "cherrapunji", "guwahati", "kaziranga", "tawang", "ziro",
  "port blair", "andaman", "havelock", "lakshadweep",
  "ranchi", "patna", "bodh gaya", "darbhanga", "bhubaneswar", "puri", "konark",
  "visakhapatnam", "vizag", "araku", "tirupati", "vijayawada",
  "aurangabad", "nashik", "lonavala", "mahabaleshwar", "shirdi", "alibaug", "matheran", "igatpuri",
  "daman", "diu", "silvassa",
]);

function inferCountryFromPlace(name) {
  if (!name) return "";
  const n = String(name).toLowerCase().replace(/[.,()]/g, " ").replace(/\s+/g, " ").trim();
  if (!n) return "";

  // Explicit "india" anywhere in the text
  if (/\bindia\b/.test(n)) return "India";

  // Direct or embedded match against known international destinations
  if (PLACE_COUNTRY_MAP[n]) return PLACE_COUNTRY_MAP[n];
  for (const k of Object.keys(PLACE_COUNTRY_MAP)) {
    if (n === k || n.startsWith(k + " ") || n.endsWith(" " + k) || n.includes(" " + k + " ")) {
      return PLACE_COUNTRY_MAP[k];
    }
  }
  // Country name typed directly ("thailand trip")
  const countries = new Set(Object.values(PLACE_COUNTRY_MAP).map(c => c.toLowerCase()));
  for (const c of countries) {
    if (n === c || n.startsWith(c + " ") || n.endsWith(" " + c) || n.includes(" " + c + " ")) {
      return c.replace(/\b\w/g, ch => ch.toUpperCase());
    }
  }

  // Known Indian destination names
  if (INDIA_PLACES.has(n)) return "India";
  for (const p of INDIA_PLACES) {
    if (n.startsWith(p + " ") || n.endsWith(" " + p)) return "India";
  }

  return ""; // unknown — caller omits country_name
}

// Format date to dd-mm-yyyy
// ── IST DATE UTILITIES ───────────────────────────────────────────────────────
// All date work is in IST (UTC+5:30). We use toLocaleDateString with timeZone
// "Asia/Kolkata" so the browser's local clock is irrelevant.

// Return current IST date parts {d, m, y}
function istParts(offsetDays = 0) {
  const ms = Date.now() + offsetDays * 86400000;
  const str = new Date(ms).toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit", month: "2-digit", year: "numeric",
  });
  // en-IN gives "dd/mm/yyyy"
  const [d, m, y] = str.split("/");
  return { d, m, y };
}

// Format IST date offset as dd-mm-yyyy
function istStr(offsetDays = 0) {
  const { d, m, y } = istParts(offsetDays);
  return `${d}-${m}-${y}`;
}

function todayIST() { return istStr(0); }
function tomorrowIST() { return istStr(1); }
function dayAfterIST() { return istStr(2); }

// Parse dd-mm-yyyy string to a comparable integer YYYYMMDD
function parseDMY(s) {
  const [d, m, y] = s.split("-");
  return parseInt(`${y}${m}${d}`, 10);
}

// Clamp: if resolved date is in the past relative to IST today, return tomorrow
function clampFuture(ddmmyyyy) {
  if (parseDMY(ddmmyyyy) < parseDMY(todayIST())) return tomorrowIST();
  return ddmmyyyy;
}

// Resolve any date expression to dd-mm-yyyy (future-clamped)
function fmtDate(dateStr) {
  if (!dateStr || dateStr.trim() === "") return tomorrowIST();
  const s = dateStr.toLowerCase().trim();

  // Relative keywords
  if (s === "today") return clampFuture(todayIST());
  if (s === "tomorrow" || s === "tmrw" || s === "tom") return tomorrowIST();
  if (s.includes("day after tomorrow") || s === "day after") return dayAfterIST();

  // Remove ordinal suffixes: "20th" → "20"
  const clean = dateStr.replace(/(\d+)(st|nd|rd|th)/gi, "$1").trim();

  // Already dd-mm-yyyy
  const dmyFull = clean.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmyFull) return clampFuture(`${dmyFull[1].padStart(2, "0")}-${dmyFull[2].padStart(2, "0")}-${dmyFull[3]}`);

  // dd-mm (no year) → assume current or next year
  const dmyShort = clean.match(/^(\d{1,2})[\/\-](\d{1,2})$/);
  if (dmyShort) {
    const { y } = istParts(0);
    const candidate = `${dmyShort[1].padStart(2, "0")}-${dmyShort[2].padStart(2, "0")}-${y}`;
    return clampFuture(candidate);
  }

  // ISO yyyy-mm-dd
  const iso = clean.match(/^(\d{4})[\/\-](\d{2})[\/\-](\d{2})$/);
  if (iso) return clampFuture(`${iso[3]}-${iso[2]}-${iso[1]}`);

  // Natural language: "20 July", "July 20", "20 July 2026", "July 20 2026"
  // Try adding current year first, then next year
  const { y: curY } = istParts(0);
  for (const yr of [curY, String(parseInt(curY) + 1)]) {
    try {
      const parsed = new Date(`${clean} ${yr}`);
      if (!isNaN(parsed)) {
        const pStr = new Date(parsed).toLocaleDateString("en-IN", {
          timeZone: "Asia/Kolkata",
          day: "2-digit", month: "2-digit", year: "numeric",
        });
        const [pd, pm, py] = pStr.split("/");
        const result = `${pd}-${pm}-${py}`;
        const clamped = clampFuture(result);
        if (clamped !== tomorrowIST() || result === tomorrowIST()) return clamped;
        // If we got clamped, try next year
      }
    } catch (_) { }
  }

  // console.warn("[fmtDate] could not parse:", dateStr, "→ using tomorrow");
  return tomorrowIST();
}

// Parse duration in minutes to "Xh Ym"
function fmtDuration(mins) {
  if (!mins && mins !== 0) return "—";
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Compute total fare from a route object
function computeFare(route) {
  if (route.total_fare && typeof route.total_fare === "number") return route.total_fare;
  let total = 0;
  for (const node of route.nodes || []) {
    const seg = node.segment;
    const raw = seg.seat_avail_ui?.fare ?? seg.seat_costs?.fare ?? null;
    if (raw !== null && raw !== undefined) {
      const n = Number(raw);
      if (!isNaN(n)) total += n;
    }
  }
  return total || null;
}

// Compute total journey duration: last arrival - first departure (wall clock)
function computeDuration(route) {
  const nodes = route.nodes || [];
  if (nodes.length === 0) return 0;
  const firstDep = nodes[0]?.segment?.origin_dep_dt;
  const lastArr = nodes[nodes.length - 1]?.segment?.dest_arr_dt;
  if (firstDep && lastArr) {
    const ms = new Date(lastArr) - new Date(firstDep);
    if (ms > 0) return Math.round(ms / 60000); // ms → minutes
  }
  // fallback: sum segment durations + transfer commute times
  let mins = 0;
  for (const n of nodes) mins += (n.segment.duration_mins || 0);
  for (const s of route.stops_data || []) mins += (s.commute_time_mins || 0);
  return mins;
}

// Classify route for display
function classifyRoute(route) {
  const types = new Set((route.nodes || []).map(n =>
    n.segment.carrier ? "flight" : "train"
  ));
  if (types.has("flight") && types.has("train")) return { icon: "✈️🚂", label: "Flight + Train", badge: "Fastest", color: PURPLE };
  if (types.has("flight")) return { icon: "✈️", label: "Flight", badge: "Fastest", color: PURPLE };
  return { icon: "🚂", label: "Train", badge: "Budget", color: GREEN };
}

// Compute return date string dd-mm-yyyy given startDate string + numDays offset
function computeReturnDate(startDate, numDays) {
  if (!startDate || !numDays) return tomorrowIST();
  const [d, m, y] = startDate.split("-");
  if (!d || !m || !y) return tomorrowIST();
  const dt = new Date(`${y}-${m}-${d}`);
  if (isNaN(dt)) return tomorrowIST();
  dt.setDate(dt.getDate() + parseInt(numDays, 10));
  return clampFuture(
    `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`
  );
}

// Build WS message(s) based on selected mode
// mode: "train" → modes [1,2] | "flight" → modes [4] | "all" → two msgs: [1,2] then [4]
function buildWsMsgs(info, prefs) {
  const sessionId = mkSessionId();
  const rawDate = fmtDate(info.startDate);
  const date = clampFuture(rawDate);
  // console.log(`[WS] date: "${info.startDate}" → "${date}" (IST today=${todayIST()})`);

  const base = {
    api_key: WS_API_KEY,
    search_session_id: sessionId,
    src: info.source,
    dest: info.destination,
    date,
    pref_train_class: prefs.trainClass || "2A",
    pref_flight_class: prefs.flightClass || "E",
    train_num: "",
    test: true,
    lat_long: 1,
    max_num_stops: prefs.maxStops ?? 1,
    num_adults: 1, num_children: 0, num_infants: 0,
    children_ages: [],
    fetch_avail: 1,
    flight_test: false,
    From: "web_form_search:routechef_planner",
    src_name: info.source,
    dest_name: info.destination,
  };

  const mode = prefs.travelMode || "all";  // "train" | "flight" | "all"

  if (mode === "train") {
    // Single message, modes [1,2] — trains only
    return { msgs: [{ action: "websocket-sample", body: { ...base, modes: [1, 2] } }], sessionId };
  }
  if (mode === "flight") {
    // Single message, modes [4] — flight+train mixed combos only
    return { msgs: [{ action: "websocket-sample", body: { ...base, modes: [4] } }], sessionId };
  }
  // "all" → two messages on same WS, expect TWO end signals
  return {
    msgs: [
      { action: "websocket-sample", body: { ...base, modes: [1, 2] } },   // msg1: train
      { action: "websocket-sample", body: { ...base, modes: [4] } },   // msg2: flight+mixed
    ],
    sessionId,
  };
}

// Open WebSocket, send message(s), collect results.
// For "all" mode: sends msg1 on connect, sends msg2 after first end signal,
// closes socket only after SECOND end signal.
function fetchRoutesViaWS(info, prefs, onRoute) {
  return new Promise((resolve, reject) => {
    const { msgs, sessionId } = buildWsMsgs(info, prefs);
    const isMultiMsg = msgs.length > 1;

    // console.log("[WS] URL:", WS_URL);
    // console.log("[WS] Session:", sessionId);
    // console.log("[WS] mode:", prefs.travelMode || "all", "| messages:", msgs.length);
    msgs.forEach((_m, _i) => { });

    const ws = new WebSocket(WS_URL);
    const routes = [];
    let endSignals = 0;          // count of 201/203 received
    const endTarget = msgs.length; // need this many end signals before closing

    ws.onopen = () => {
      // console.log(`[WS] Connected — sending msg1 (modes ${JSON.stringify(msgs[0].body.modes)})`);
      ws.send(JSON.stringify(msgs[0]));
    };

    ws.onmessage = (ev) => {
      let data;
      try { data = JSON.parse(ev.data); } catch { return; }
      // console.log(`[WS] ← status=${data.status} routes=${data.data?.routes?.length ?? "none"}`);

      // End signal: 201 = Completed, 203 = No routes found
      if (data.status === 201 || data.status === 203) {
        endSignals++;
        // console.log(`[WS] End signal ${endSignals}/${endTarget} (${data.message})`);

        if (isMultiMsg && endSignals === 1) {
          // First stream done — send second message on same socket
          // console.log(`[WS] Sending msg2 (modes ${JSON.stringify(msgs[1].body.modes)})`);
          ws.send(JSON.stringify(msgs[1]));
          return;
        }

        // All streams done
        // console.log(`[WS] All done. Total routes: ${routes.length}`);
        ws.close();
        resolve(routes);
        return;
      }

      if (data.status === 200 && data.data?.routes) {
        for (const route of data.data.routes) {
          if (!route.hide && route.keep) {
            routes.push(route);
            onRoute(route);
          }
        }
      }
    };

    ws.onerror = (err) => {
      // console.error("[WS] Error:", err);
      reject(new Error("WebSocket connection error"));
    };

    ws.onclose = (ev) => {
      // console.log("[WS] Closed:", ev.code, ev.reason || "(clean)");
    };

    // 60s safety timeout
    const timer = setTimeout(() => {
      // console.warn("[WS] Timeout — resolving with", routes.length, "routes so far");
      ws.close();
      resolve(routes);
    }, 60000);

    ws.addEventListener("close", () => clearTimeout(timer));
  });
}

/* ── ROUTING PREFERENCES CONFIG ───────────────────────────────────────── */
const TRAIN_CLASSES = ["SL", "3E", "3A", "2A", "1A"];
const FLIGHT_CLASSES = [
  { code: "E", label: "Economy" },
  { code: "PE", label: "Premium Eco" },
  { code: "B", label: "Business" },
  { code: "F", label: "First" },
];

const RoutingConfig = ({ defaults, onStart, onSkip }) => {
  const [travelMode, setTravelMode] = useState(defaults.travelMode || "all");
  const [trainClass, setTrainClass] = useState(defaults.trainClass || "2A");
  const [flightClass, setFlightClass] = useState(defaults.flightClass || "E");
  const [maxStops, setMaxStops] = useState(defaults.maxStops ?? 1);

  const Pill = ({ active, color, onClick, children }) => (
    <button onClick={onClick} style={{
      padding: "6px 12px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
      fontSize: 12, fontWeight: active ? 700 : 400,
      border: `1.5px solid ${active ? color : "#E0E0E0"}`,
      background: active ? color + "12" : "#fff",
      color: active ? color : "#555", transition: "all 0.12s",
    }}>{children}</button>
  );

  const MODES = [
    { key: "train", label: "🚂 Train", desc: "Train routes only" },
    { key: "flight", label: "✈️ Flight", desc: "Flight + train combos" },
    { key: "all", label: "⚡ All", desc: "Everything — 2 searches" },
  ];

  return (
    <div style={{
      background: "#fff", border: "1px solid #E8E8E8", borderRadius: 16,
      padding: "16px 16px 14px", maxWidth: 360
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#111", marginBottom: 2 }}>
        Route preferences <span style={{ fontWeight: 400, fontSize: 12, color: "#aaa" }}>(optional)</span>
      </div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>Defaults work great — tweak only if needed.</div>

      {/* Travel mode — prominent at top */}
      <div style={{ marginBottom: 14 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 7,
          textTransform: "uppercase", letterSpacing: 0.5
        }}>Mode</div>
        <div style={{ display: "flex", gap: 6 }}>
          {MODES.map(m => (
            <button key={m.key} onClick={() => setTravelMode(m.key)} style={{
              flex: 1, padding: "10px 6px", borderRadius: 12, cursor: "pointer",
              fontFamily: "inherit", fontSize: 12, fontWeight: travelMode === m.key ? 700 : 400,
              border: `2px solid ${travelMode === m.key ? ORANGE : "#E0E0E0"}`,
              background: travelMode === m.key ? ORANGE + "10" : "#FAFAFA",
              color: travelMode === m.key ? ORANGE : "#555",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
              transition: "all 0.12s",
            }}>
              <span style={{ fontSize: 16 }}>{m.label.split(" ")[0]}</span>
              <span>{m.label.split(" ").slice(1).join(" ")}</span>
              <span style={{ fontSize: 9, color: travelMode === m.key ? ORANGE + "99" : "#bbb", fontWeight: 400 }}>
                {m.desc}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Train class — only show when mode includes trains */}
      {(travelMode === "train" || travelMode === "all") && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6,
            textTransform: "uppercase", letterSpacing: 0.5
          }}>🚂 Train Class</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TRAIN_CLASSES.map(c => (
              <Pill key={c} active={trainClass === c} color={GREEN} onClick={() => setTrainClass(c)}>{c}</Pill>
            ))}
          </div>
        </div>
      )}

      {/* Flight class — only show when mode includes flights */}
      {(travelMode === "flight" || travelMode === "all") && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6,
            textTransform: "uppercase", letterSpacing: 0.5
          }}>✈️ Flight Class</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FLIGHT_CLASSES.map(fc => (
              <Pill key={fc.code} active={flightClass === fc.code} color={PURPLE}
                onClick={() => setFlightClass(fc.code)}>
                {fc.label}
              </Pill>
            ))}
          </div>
        </div>
      )}

      {/* Max stops */}
      <div style={{ marginBottom: 16 }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: "#555", marginBottom: 6,
          textTransform: "uppercase", letterSpacing: 0.5
        }}>Max Stops</div>
        <div style={{ display: "flex", gap: 6 }}>
          {[{ v: 0, l: "Non-stop" }, { v: 1, l: "1 stop" }, { v: 2, l: "2 stops" }].map(opt => (
            <Pill key={opt.v} active={maxStops === opt.v} color={ORANGE}
              onClick={() => setMaxStops(opt.v)}>{opt.l}</Pill>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={onSkip} style={{
          flex: 1, padding: "10px 0", background: "#fff", border: "1.5px solid #E0E0E0",
          borderRadius: 11, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, color: "#888",
        }}>Use defaults</button>
        <button onClick={() => onStart({ travelMode, trainClass, flightClass, maxStops })} style={{
          flex: 2, padding: "10px 0", background: ORANGE, border: "none",
          borderRadius: 11, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, color: "#fff",
          boxShadow: `0 2px 12px ${ORANGE}44`,
        }}>
          {travelMode === "all" ? "Search all modes →" : travelMode === "train" ? "Search trains →" : "Search flights →"}
        </button>
      </div>
    </div>
  );
};

/* ── REAL ROUTE CARDS from WS data ────────────────────────────────────── */

// Helper: count available legs
function legAvailCount(route) {
  return (route.nodes || []).filter(n => n.segment.seat_avail_ui?.is_avail).length;
}
// Helper: first departure time as ms
function firstDep(route) {
  const d = route.nodes?.[0]?.segment?.origin_dep_dt;
  return d ? new Date(d).getTime() : Infinity;
}
// Helper: last arrival time as ms
function lastArr(route) {
  const nodes = route.nodes || [];
  const d = nodes[nodes.length - 1]?.segment?.dest_arr_dt;
  return d ? new Date(d).getTime() : Infinity;
}

const SORT_OPTS = [
  { key: "best", label: "Best" },
  { key: "cheapest_avail", label: "Cheapest Avail" },
  { key: "quickest_avail", label: "Quickest Avail" },
  { key: "cheapest", label: "Cheapest" },
  { key: "quickest", label: "Quickest" },
  { key: "depart_early", label: "Depart Early" },
  { key: "reach_early", label: "Reach Early" },
];

// Extract total last-mile km from dest_comment of last leg + origin_comment of first leg
// Handles "is 15 kms from" or "is 15km from" patterns
function extractLastMileKm(route) {
  const nodes = route.nodes || [];
  if (!nodes.length) return 0;
  let total = 0;
  const re = /(\d+(?:\.\d+)?)\s*km/i;

  // Last leg dest comment (distance from station to actual dest)
  const lastSeg = nodes[nodes.length - 1]?.segment;
  const destComment = lastSeg?.dest_comment || lastSeg?.dest_comment_web || "";
  const dm = destComment.match(re);
  if (dm) total += parseFloat(dm[1]);

  // First leg origin comment (distance from actual origin to station)
  const firstSeg = nodes[0]?.segment;
  const origComment = firstSeg?.origin_comment || firstSeg?.origin_comment_web || "";
  const om = origComment.match(re);
  if (om) total += parseFloat(om[1]);

  return total;
}

// Extract km from a comment string like "a is 15kms away from xyz" or "15 km away"
function extractCabDistance(comment) {
  if (!comment) return null;
  const m = comment.match(/(\d+(?:\.\d+)?)\s*km/i);
  return m ? parseFloat(m[1]) : null;
}

// Build concise cab label: "🚕 15 km by cab" or null if no distance
function cabLabel(comment) {
  const km = extractCabDistance(comment);
  if (!km) return null;
  return `🚕 ${km} km by cab`;
}

function applySort(routes, key) {
  const arr = [...routes];
  const avScore = r => {
    const legs = (r.nodes || []).length;
    const avail = legAvailCount(r);
    return avail === legs ? 2 : avail > 0 ? 1 : 0;
  };
  switch (key) {
    case "best":
      // Composite: avail legs ↑, non-stop ↑, last-mile km ↓, duration ↓, cost ↓
      return arr.sort((a, b) => {
        // 1. More available legs first
        const avd = avScore(b) - avScore(a); if (avd) return avd;
        // 2. Fewer stops first
        const stopd = (a.num_stops || 0) - (b.num_stops || 0); if (stopd) return stopd;
        // 3. Less last-mile distance first
        const kmd = extractLastMileKm(a) - extractLastMileKm(b); if (Math.abs(kmd) > 0.5) return kmd;
        // 4. Shorter total duration
        const durd = computeDuration(a) - computeDuration(b); if (durd) return durd;
        // 5. Cheaper fare
        return (computeFare(a) || Infinity) - (computeFare(b) || Infinity);
      });
    case "cheapest_avail":
      return arr.sort((a, b) => {
        const ds = avScore(b) - avScore(a); if (ds) return ds;
        return (computeFare(a) || Infinity) - (computeFare(b) || Infinity);
      });
    case "quickest_avail":
      return arr.sort((a, b) => {
        const ds = avScore(b) - avScore(a); if (ds) return ds;
        return computeDuration(a) - computeDuration(b);
      });
    case "cheapest":
      return arr.sort((a, b) => (computeFare(a) || Infinity) - (computeFare(b) || Infinity));
    case "quickest":
      return arr.sort((a, b) => computeDuration(a) - computeDuration(b));
    case "depart_early":
      return arr.sort((a, b) => firstDep(a) - firstDep(b));
    case "reach_early":
      return arr.sort((a, b) => lastArr(a) - lastArr(b));
    default:
      return arr;
  }
}

const PAGE_SIZE = 2; // show top 2 initially, paginate rest

const LiveRouteCards = ({ routes, selectedId, onSelect, noRoutes, tripInfo }) => {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState("best");

  if (noRoutes) return (
    <div style={{
      background: "#FEF0F2", border: "1px solid #F5C5C5", borderRadius: 14,
      padding: "14px 16px", maxWidth: 420, fontSize: 13, color: "#9F1239"
    }}>
      No routes found for these dates. Try a different date or check source/destination names.
    </div>
  );

  if (routes.length === 0) return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#aaa", fontSize: 13 }}>
      <span style={{ display: "inline-block", animation: "agentBounce 1s ease-in-out infinite" }}>⏳</span>
      Searching routes…
    </div>
  );

  const sorted = applySort(routes, sortKey);
  // Lazy-load: show (page+1)*PAGE_SIZE routes
  const visible = sorted.slice(0, (page + 1) * PAGE_SIZE);
  const hasMore = sorted.length > visible.length;
  const hiddenCount = sorted.length - visible.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 420 }}>
      {/* Sort buttons */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", paddingBottom: 2 }}>
        {SORT_OPTS.map(opt => (
          <button key={opt.key} onClick={() => { setSortKey(opt.key); setPage(0); }} style={{
            padding: "5px 10px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
            fontSize: 11, fontWeight: sortKey === opt.key ? 700 : 400,
            border: `1.5px solid ${sortKey === opt.key ? PURPLE : "#E0E0E0"}`,
            background: sortKey === opt.key ? PURPLE + "0F" : "#fff",
            color: sortKey === opt.key ? PURPLE : "#666", transition: "all 0.12s",
          }}>{opt.label}</button>
        ))}
      </div>
      {visible.map((route, ri) => {
        const picked = selectedId === route.route_id;
        const cls = classifyRoute(route);
        const fare = computeFare(route);
        const durMins = computeDuration(route);
        const nodes = route.nodes || [];

        // Overall availability: all legs available?
        const allAvail = nodes.every(n => n.segment.seat_avail_ui?.is_avail);
        const someAvail = nodes.some(n => n.segment.seat_avail_ui?.is_avail);
        const availColor = allAvail ? GREEN : someAvail ? AMBER : "#aaa";
        const availLabel = allAvail ? "All seats available" : someAvail ? "Partial availability" : "Check availability";
        const availIcon = allAvail ? "●" : someAvail ? "◐" : "○";

        return (
          <div key={route.route_id || ri} onClick={() => !selectedId && onSelect(route)} style={{
            border: `2px solid ${picked ? cls.color : "#E8E8E8"}`,
            background: picked ? cls.color + "0A" : "#fff",
            borderRadius: 16, overflow: "hidden",
            cursor: selectedId ? "default" : "pointer",
            opacity: selectedId && !picked ? 0.4 : 1,
            transition: "all 0.15s",
            boxShadow: picked ? `0 0 0 3px ${cls.color}22` : "none",
          }}>

            {/* ── Header: type + overall avail + fare ── */}
            <div style={{
              padding: "11px 13px 10px", display: "flex", alignItems: "center",
              justifyContent: "space-between", borderBottom: "1px solid #F5F5F5"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>{cls.icon}</span>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>{cls.label}</span>
                    <span style={{
                      fontSize: 10, color: cls.color, background: cls.color + "18",
                      borderRadius: 99, padding: "2px 7px", fontWeight: 600
                    }}>{cls.badge}</span>
                    {/* Overall availability pill */}
                    <span style={{
                      fontSize: 10, color: availColor, background: availColor + "15",
                      borderRadius: 99, padding: "2px 8px", fontWeight: 600, display: "flex", alignItems: "center", gap: 3
                    }}>
                      <span style={{ fontSize: 8 }}>{availIcon}</span>{availLabel}
                    </span>
                    {picked && <span style={{ fontSize: 10, color: GREEN, fontWeight: 700 }}>✓ Selected</span>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#999" }}>{fmtDuration(durMins)} total</span>
                    {tripInfo?.startDate && (() => {
                      // show date from first leg departure if available, else from tripInfo
                      const firstDep = nodes[0]?.segment?.origin_dep_dt;
                      const dateLabel = firstDep
                        ? new Date(firstDep).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })
                        : tripInfo.startDate.split("-").reverse().slice(0, 2).join(" ");
                      return <span style={{
                        fontSize: 11, color: "#aaa", background: "#F5F5F7",
                        borderRadius: 99, padding: "1px 7px"
                      }}>📅 {dateLabel}</span>;
                    })()}
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                {fare
                  ? <div style={{ fontWeight: 800, fontSize: 15, color: "#111" }}>₹{fmtPrice(fare)}</div>
                  : <div style={{ fontSize: 11, color: "#bbb", fontWeight: 600 }}>Check IRCTC</div>}
              </div>
            </div>

            {/* ── Legs ── */}
            <div style={{ padding: "10px 13px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              {nodes.map((node, ni) => {
                const seg = node.segment;
                const isFlight = !!seg.carrier;
                const avl = seg.seat_avail_ui;
                const isAvail = avl?.is_avail;
                const segFare = avl?.fare !== undefined ? Number(avl.fare) : null;
                const stop = route.stops_data?.find(s => s.seq === ni + 1);

                const dep = seg.origin_dep_dt
                  ? new Date(seg.origin_dep_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
                const arr = seg.dest_arr_dt
                  ? new Date(seg.dest_arr_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";

                // src and dest names for this leg
                const legSrc = seg.origin_name || seg.origin_code_iata || seg.origin_code || "";
                const legDest = seg.dest_name || seg.dest_code_iata || seg.dest_code || "";

                return (
                  <div key={node.node_id || ni}>
                    {/* Leg row — two-column: left=journey, right=avail+fare */}
                    <div style={{
                      background: isFlight ? "#F5F0FF" : "#F0F9F4",
                      borderRadius: 10, padding: "9px 11px",
                      display: "flex", alignItems: "flex-start", gap: 10,
                    }}>
                      {/* Mode icon */}
                      <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                        {isFlight ? "✈️" : "🚂"}
                      </span>

                      {/* Journey details */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Service name */}
                        <div style={{ fontWeight: 600, fontSize: 12, color: "#111", marginBottom: 3 }}>
                          {isFlight
                            ? `${seg.carrier} ${seg.flight_num}`
                            : `${(seg.train_name || "").trim()} (${seg.train_num})`}
                        </div>
                        {/* src → dest with times */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#333" }}>{dep}</span>
                          <span style={{
                            fontSize: 10, color: "#999", flex: 1, textAlign: "center",
                            borderTop: "1.5px dashed #E0E0E0", margin: "0 4px"
                          }}></span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#333" }}>{arr}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{
                            fontSize: 11, color: "#555", maxWidth: 90,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                          }}>{legSrc}</span>
                          <span style={{ fontSize: 10, color: "#bbb" }}>→</span>
                          <span style={{
                            fontSize: 11, color: "#555", maxWidth: 90,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                          }}>{legDest}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#bbb", marginTop: 2 }}>
                          {fmtDuration(seg.duration_mins)}
                          {seg.seat_class_ui && <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>{seg.seat_class_ui}</span>}
                          {!isFlight && seg.origin_platform && <span style={{ marginLeft: 6 }}>Pf {seg.origin_platform}</span>}
                          {isFlight && seg.baggage && <span style={{ marginLeft: 6 }}>🧳 {seg.baggage.cabin_baggage}</span>}
                        </div>
                      </div>

                      {/* Availability + fare + Book button — right column */}
                      <div style={{
                        display: "flex", flexDirection: "column", alignItems: "flex-end",
                        gap: 3, flexShrink: 0, minWidth: 70
                      }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 4,
                          background: isAvail ? GREEN + "18" : "#F5F5F5",
                          borderRadius: 99, padding: "3px 8px",
                        }}>
                          <div style={{
                            width: 6, height: 6, borderRadius: "50%",
                            background: isAvail ? GREEN : "#bbb", flexShrink: 0
                          }} />
                          <span style={{
                            fontSize: 10, fontWeight: 700,
                            color: isAvail ? GREEN : "#999"
                          }}>
                            {isAvail ? "Avail" : "Check"}
                          </span>
                        </div>
                        {segFare
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>₹{Math.round(segFare).toLocaleString()}</span>
                          : <span style={{ fontSize: 10, color: "#bbb" }}>—</span>}
                        {seg.booking_link && (
                          <a href={seg.booking_link} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              fontSize: 10, fontWeight: 700, color: "#fff",
                              background: isFlight ? PURPLE : GREEN,
                              borderRadius: 99, padding: "3px 9px",
                              textDecoration: "none", whiteSpace: "nowrap",
                              display: "flex", alignItems: "center", gap: 3,
                            }}>
                            {isFlight ? "✈️ Book" : "🎫 Book"}
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Transfer strip between legs */}
                    {stop && ni < nodes.length - 1 && (
                      <div style={{
                        margin: "4px 0", padding: "5px 10px",
                        background: "#FFFBEB", borderRadius: 8,
                        display: "flex", alignItems: "center", gap: 6,
                        fontSize: 10.5, color: "#92400E",
                      }}>
                        <span>🔄</span>
                        <span style={{ fontWeight: 600 }}>{stop.stop_title}</span>
                        <span style={{ color: "#bbb" }}>·</span>
                        <span>{stop.commute_comment}</span>
                        {stop.exp_time_avl_comment && (
                          <span style={{ color: "#aaa", marginLeft: 4 }}>({stop.exp_time_avl_comment})</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Destination cab strip — distance from last station to actual destination */}
              {(() => {
                const lastSeg = nodes[nodes.length - 1]?.segment;
                const c = cabLabel(lastSeg?.dest_comment || lastSeg?.dest_comment_web);
                return c ? (
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: "#FFF8F0", borderRadius: 8, padding: "5px 10px",
                    fontSize: 10.5, color: "#92400E"
                  }}>
                    <span>{c}</span>
                    <span style={{ color: "#bbb" }}>· to reach destination</span>
                  </div>
                ) : null;
              })()}
            </div>

          </div>
        );
      })}

      {/* Pagination */}
      {hasMore && (
        <button onClick={() => setPage(p => p + 1)} style={{
          background: "#F5F5F7", border: "1.5px solid #E0E0E0", borderRadius: 10,
          padding: "9px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 12,
          fontWeight: 600, color: "#555", width: "100%",
        }}>
          Show {Math.min(PAGE_SIZE, hiddenCount)} more route{hiddenCount > 1 ? "s" : ""} ↓
        </button>
      )}
    </div>
  );
};

/* ── IMAGES ──────────────────────────────────────────────────────────── */
const IMGS = {
  tea: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&q=75",
  beach: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=500&q=75",
  temple: "https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=500&q=75",
  hill: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=500&q=75",
  lake: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=500&q=75",
  wildlife: "https://images.unsplash.com/photo-1562547256-2c5ee93b60b7?w=500&q=75",
  back: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=500&q=75",
  city: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=500&q=75",
};
function placeImg(type = "") {
  const t = type.toLowerCase();
  if (t.includes("tea") || t.includes("estate") || t.includes("plantation")) return IMGS.tea;
  if (t.includes("beach")) return IMGS.beach;
  if (t.includes("temple") || t.includes("heritage") || t.includes("fort") || t.includes("palace")) return IMGS.temple;
  if (t.includes("hill") || t.includes("mountain") || t.includes("view") || t.includes("station") || t.includes("peak")) return IMGS.hill;
  if (t.includes("lake") || t.includes("dam") || t.includes("reservoir") || t.includes("waterfall")) return IMGS.lake;
  if (t.includes("wild") || t.includes("park") || t.includes("forest") || t.includes("national") || t.includes("sanctu")) return IMGS.wildlife;
  if (t.includes("back") || t.includes("cruise") || t.includes("houseboat") || t.includes("river")) return IMGS.back;
  return IMGS.city;
}

/* ── SPARKLE ─────────────────────────────────────────────────────────── */
// const Sparkle = ({ size=18, color=PURPLE }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{flexShrink:0}}>
//     <path d="M12 2 L13.5 9 L20 10 L13.5 11 L12 18 L10.5 11 L4 10 L10.5 9 Z"/>
//     <path d="M19 2 L19.8 5.2 L23 6 L19.8 6.8 L19 10 L18.2 6.8 L15 6 L18.2 5.2 Z" opacity="0.5"/>
//     <path d="M5 14 L5.6 16.4 L8 17 L5.6 17.6 L5 20 L4.4 17.6 L2 17 L4.4 16.4 Z" opacity="0.5"/>
//   </svg>
// );
const Sparkle = ({ size = 18, className = "" }) => (
  <img
    src="/Star1.svg"
    alt="star"
    width={size}
    height={size}
    className={className}
    style={{ flexShrink: 0 }}
  />
);

/* ── LOGO — uses the SVG file + brand wordmark ───────────────────────── */
const Logo = () => (
  <a href="/" style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
    <img src="/logo_new.svg" alt="Routechef Logo"
      style={{ height: 32, width: "auto", objectFit: "contain" }}
      onError={e => { e.target.style.display = "none"; }} />
    <span style={{ fontSize: 20, lineHeight: 1 }}>
      <span style={{ color: "#FF5B15", fontWeight: 600 }}>Route</span>
      <span style={{ color: "#0A0A0A", fontWeight: 400 }}>chef</span>
    </span>
  </a>
);

/* ── CROWD DOTS ──────────────────────────────────────────────────────── */
const CrowdDots = ({ level = 3 }) => {
  const c = level <= 2 ? GREEN : level <= 3 ? AMBER : RED;
  return (
    <span style={{ display: "flex", gap: 3 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: "50%",
          background: i <= level ? c : "#E0E0E0", display: "inline-block"
        }} />
      ))}
    </span>
  );
};

/* ── AGENT BUBBLE ────────────────────────────────────────────────────── */
const AgentBubble = ({ agent, steps, done }) => {
  const [vis, setVis] = useState(0);
  // Active bubbles start collapsed; only show last step live in collapsed form.
  // Done bubbles auto-collapse immediately.
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (vis < steps.length) {
      const t = setTimeout(() => setVis(v => v + 1), 700);
      return () => clearTimeout(t);
    }
  }, [vis, steps.length]);

  // Auto-collapse after done (already collapsed by default, but ensure it)
  useEffect(() => {
    if (done) setCollapsed(true);
  }, [done]);

  const cfg = ({
    routing: { bg: "#EEF3FF", border: "#B3C5F5", dot: "#4466EE", label: "Routing Agent" },
    discovery: { bg: "#F0EBF9", border: "#C9B3E8", dot: PURPLE, label: "Discovery Agent" },
    itinerary: { bg: "#E8F7F2", border: "#A3D9C0", dot: GREEN, label: "Itinerary Agent" },
    accommodation: { bg: "#FEF0EA", border: "#F5C5A3", dot: ORANGE, label: "Accommodation Agent" },
  })[agent] || { bg: "#F5F5F7", border: "#E0E0E0", dot: "#888", label: agent };

  if (collapsed) {
    const lastStep = steps[Math.min(vis, steps.length) - 1] || "";
    return (
      <button onClick={() => setCollapsed(false)} style={{
        display: "flex", alignItems: "center", gap: 7, background: "none",
        border: "none", cursor: "pointer", padding: 0, maxWidth: 380, textAlign: "left",
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%", background: cfg.dot, flexShrink: 0,
          display: "inline-block",
          animation: done ? "none" : "agentPulse 1.4s ease-in-out infinite",
        }} />
        <span style={{ fontSize: 11, color: "#888", lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600, color: cfg.dot }}>{cfg.label}</span>
          {done
            ? <span style={{ color: "#aaa" }}> · {steps.length} steps · tap to expand</span>
            : lastStep
              ? <span style={{ color: "#555" }}> · {lastStep}</span>
              : null}
        </span>
        {done && <span style={{ fontSize: 10, color: GREEN, flexShrink: 0 }}>✓</span>}
      </button>
    );
  }

  return (
    <div style={{
      background: cfg.bg, border: `1px solid ${cfg.border}`,
      borderRadius: 12, padding: "10px 13px", maxWidth: 320, fontSize: 13
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", background: cfg.dot, display: "inline-block",
            boxShadow: done ? "none" : `0 0 0 3px ${cfg.dot}33`,
            animation: done ? "none" : "agentPulse 1.4s ease-in-out infinite"
          }} />
          <span style={{ fontWeight: 700, color: cfg.dot, fontSize: 11, letterSpacing: 0.3 }}>{cfg.label}</span>
          {done && <span style={{ fontSize: 10, color: GREEN }}>✓ done</span>}
        </div>
        {/* Collapse button always visible in expanded state */}
        <button onClick={() => setCollapsed(true)} style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: 10, color: "#bbb", padding: "2px 6px", borderRadius: 6,
          lineHeight: 1,
        }}>▲ collapse</button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {steps.slice(0, vis).map((s, i) => (
          <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
            <span style={{ fontSize: 11, color: (i < vis - 1 || done) ? GREEN : "#bbb", flexShrink: 0 }}>
              {(i < vis - 1 || done) ? "✓" : "⏳"}
            </span>
            <span style={{ color: "#444", lineHeight: 1.5 }}>{s}</span>
          </div>
        ))}
        {!done && vis < steps.length && (
          <div style={{ display: "flex", gap: 4, paddingLeft: 18 }}>
            {[0, 1, 2].map(i => (
              <span key={i} style={{
                width: 5, height: 5, borderRadius: "50%",
                background: cfg.dot, opacity: 0.4, display: "inline-block",
                animation: `agentBounce 1s ${i * 0.2}s ease-in-out infinite`
              }} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── PREFERENCE SCREEN ───────────────────────────────────────────────── */
const PREF_OPTS = [
  { id: "adventure", label: "Adventure", icon: "🧗" },
  { id: "culture", label: "Culture", icon: "🏛️" },
  { id: "nature", label: "Nature", icon: "🌿" },
  { id: "food", label: "Food & Local", icon: "🍽️" },
  { id: "relaxation", label: "Relaxation", icon: "🧘" },
  { id: "photography", label: "Photography", icon: "📷" },
  { id: "history", label: "History", icon: "📜" },
  { id: "wildlife", label: "Wildlife", icon: "🦁" },
];

const PreferenceScreen = ({ destination, onDone }) => {
  const [selected, setSelected] = useState([]);
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  return (
    <div style={{
      background: "#fff", border: "1px solid #E8E8E8", borderRadius: 16,
      padding: "18px 16px 16px", maxWidth: 340,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#111", marginBottom: 4 }}>
        What are you into? <span style={{ fontWeight: 400, fontSize: 13, color: "#aaa" }}>(optional)</span>
      </div>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 14, lineHeight: 1.5 }}>
        Pick any that match your vibe — I'll highlight the best places for you in {destination}.
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {PREF_OPTS.map(p => {
          const on = selected.includes(p.id);
          return (
            <button key={p.id} onClick={() => toggle(p.id)} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 13px", borderRadius: 99, cursor: "pointer",
              fontFamily: "inherit", fontSize: 13, fontWeight: on ? 700 : 400,
              border: `2px solid ${on ? ORANGE : "#E0E0E0"}`,
              background: on ? ORANGE + "10" : "#fff",
              color: on ? ORANGE : "#555",
              transition: "all 0.15s",
            }}>
              <span>{p.icon}</span>{p.label}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => onDone([])} style={{
          flex: 1, padding: "10px 0", background: "#fff",
          border: "1.5px solid #E0E0E0", borderRadius: 11,
          cursor: "pointer", fontFamily: "inherit", fontWeight: 600,
          fontSize: 13, color: "#888",
        }}>Skip for now</button>
        <button onClick={() => onDone(selected)} style={{
          flex: 2, padding: "10px 0", background: ORANGE, border: "none",
          borderRadius: 11, cursor: "pointer", fontFamily: "inherit",
          fontWeight: 700, fontSize: 13, color: "#fff",
        }}>
          {selected.length > 0 ? `Looks great! Show me places →` : "Show me all places →"}
        </button>
      </div>
    </div>
  );
};

/* ── DUOLINGO PLACE DECK ─────────────────────────────────────────────── */
// One card at a time, centred, big photo, big ✕/✓ buttons below.
// Behind it: 2 ghost cards peeking (scale+translateY).

const CrowdLabel = ["", "Very Quiet", "Quiet", "Moderate", "Busy", "Very Busy"];

const PlaceDeck = ({ places, onAdd, onSkip, selectedIds, onRevisit, locked, resetKey }) => {
  const [idx, setIdx] = useState(0);
  const [decided, setDecided] = useState({}); // id → "add"|"skip"
  const [animDir, setAnimDir] = useState(null); // "left"|"right"
  const touchXRef = useRef(null);

  // Reset internal decided state when resetKey changes (Change places tapped)
  useEffect(() => { setDecided({}); setAnimDir(null); }, [resetKey]);

  const pending = places.filter(p => decided[p.id] === undefined);
  const place = pending[0];
  const added = places.filter(p => decided[p.id] === "add").length;

  const decide = (dir, place) => {
    if (!place || animDir || locked) return;
    setAnimDir(dir);
    setTimeout(() => {
      const next = { ...decided, [place.id]: dir === "right" ? "add" : "skip" };
      setDecided(next);
      if (dir === "right") onAdd(place);
      else onSkip(place.id);
      setAnimDir(null);
    }, 320);
  };

  const onTouchStart = e => { touchXRef.current = e.touches[0].clientX; };
  const onTouchEnd = e => {
    if (touchXRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchXRef.current;
    if (Math.abs(dx) > 50) decide(dx > 0 ? "right" : "left", place);
    touchXRef.current = null;
  };

  // Show locked "All done" state while itinerary is building OR when deck is exhausted
  if (!place || locked) {
    return (
      <div style={{
        background: "#fff", border: "1px solid #E8E8E8", borderRadius: 20,
        padding: "32px 20px", textAlign: "center", maxWidth: 340,
        opacity: locked && place ? 0.85 : 1,
      }}>
        {locked && place ? (
          // Still has cards but locked — building in progress
          <>
            <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#111", marginBottom: 6 }}>Building itinerary…</div>
            <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6 }}>
              {added} place{added !== 1 ? "s" : ""} selected. Please wait while we plan your trip.
            </div>
          </>
        ) : (
          // Deck exhausted — show All done
          <>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111", marginBottom: 6 }}>All done!</div>
            <div style={{ fontSize: 13, color: "#888", lineHeight: 1.6, marginBottom: 16 }}>
              {added} place{added !== 1 ? "s" : ""} added to your trip.
              {added === 0 && " Tap any card above to re-review."}
            </div>
            <button onClick={onRevisit} style={{
              padding: "9px 20px", background: PURPLE, border: "none", borderRadius: 99,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13, color: "#fff",
              boxShadow: `0 3px 12px ${PURPLE}33`,
            }}>
              ✏️ Change places
            </button>
          </>
        )}
      </div>
    );
  }

  const img = placeImg(place.type);
  const crowdColor = place.crowd_level <= 2 ? GREEN : place.crowd_level <= 3 ? AMBER : RED;

  return (
    <div style={{ maxWidth: 340, width: "100%", userSelect: "none" }}>
      {/* Progress bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1, display: "flex", gap: 3 }}>
          {places.map(p => (
            <div key={p.id} style={{
              flex: 1, height: 4, borderRadius: 99,
              background: decided[p.id] === "add" ? GREEN
                : decided[p.id] === "skip" ? "#E0E0E0"
                  : p.id === place.id ? ORANGE : "#F0F0F0",
              transition: "background 0.25s",
            }} />
          ))}
        </div>
        <span style={{ fontSize: 11, color: "#aaa", whiteSpace: "nowrap", flexShrink: 0 }}>
          {places.length - pending.length}/{places.length}
        </span>
      </div>

      {/* Stacked ghost cards */}
      <div style={{ position: "relative" }} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {pending.slice(1, 3).map((p, i) => (
          <div key={p.id} style={{
            position: "absolute", inset: 0, borderRadius: 20, overflow: "hidden",
            background: "#fff", border: "1px solid #E8E8E8",
            transform: `scale(${0.96 - i * 0.03}) translateY(${(i + 1) * -8}px)`,
            zIndex: -i - 1,
          }}>
            <img src={placeImg(p.type)} alt="" style={{ width: "100%", height: 160, objectFit: "cover", opacity: 0.35 }} />
          </div>
        ))}

        {/* Active card */}
        <div style={{
          background: "#fff", borderRadius: 20, overflow: "hidden",
          border: "1px solid #E8E8E8", position: "relative",
          boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
          transform: animDir === "right" ? "translateX(110%) rotate(6deg)"
            : animDir === "left" ? "translateX(-110%) rotate(-6deg)" : "none",
          transition: animDir ? "transform 0.32s cubic-bezier(.4,0,.2,1)" : "none",
        }}>
          {/* ADDING / SKIP stamp */}
          {animDir === "right" && (
            <div style={{
              position: "absolute", top: 18, left: 16, zIndex: 10,
              background: GREEN, color: "#fff", padding: "5px 16px",
              borderRadius: 99, fontWeight: 800, fontSize: 14,
              border: "2.5px solid #fff", transform: "rotate(-8deg)"
            }}>ADDING ✓</div>
          )}
          {animDir === "left" && (
            <div style={{
              position: "absolute", top: 18, right: 16, zIndex: 10,
              background: RED, color: "#fff", padding: "5px 16px",
              borderRadius: 99, fontWeight: 800, fontSize: 14,
              border: "2.5px solid #fff", transform: "rotate(8deg)"
            }}>SKIP ✕</div>
          )}

          {/* Photo */}
          <div style={{ position: "relative", height: 160 }}>
            <img src={img} alt={place.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={e => { e.target.src = IMGS.city; }} />
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top,rgba(0,0,0,0.9) 0%,rgba(0,0,0,0) 50%)"
            }} />
            {/* cost badge */}
            <div style={{
              position: "absolute", top: 12, right: 12,
              background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)",
              borderRadius: 99, padding: "4px 11px", border: "1px solid rgba(255,255,255,0.25)"
            }}>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 12 }}>
                {!place.cost_inr || place.cost_inr === 0 ? "Free" : `₹${Number(place.cost_inr).toLocaleString()}`}
              </span>
            </div>
            {/* type badge */}
            <div style={{
              position: "absolute", top: 12, left: 12,
              background: "rgba(0,0,0,0.42)", borderRadius: 99, padding: "4px 11px"
            }}>
              <span style={{ color: "#fff", fontSize: 11 }}>{place.type}</span>
            </div>
            {/* name + meta */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "0 14px 14px" }}>
              <div style={{ fontWeight: 800, fontSize: 20, color: "#fff", lineHeight: 1.15, marginBottom: 5 }}>{place.name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>📍 {place.destination}</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>·</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>⏱ {place.duration_hours}h</span>
                <span style={{ fontSize: 11, color: "#FFD166", fontWeight: 700 }}>★ {place.rating}</span>
              </div>
              {place.vibe_tags?.length > 0 && (
                <div style={{ display: "flex", gap: 5, marginTop: 7, flexWrap: "wrap" }}>
                  {place.vibe_tags.slice(0, 3).map(v => (
                    <span key={v} style={{
                      fontSize: 10, color: "#fff",
                      background: "rgba(255,255,255,0.15)", borderRadius: 99,
                      padding: "2px 9px", border: "1px solid rgba(255,255,255,0.2)"
                    }}>{v}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info panel */}
          <div style={{ padding: "10px 12px 12px" }}>
            {/* Quick stats row */}
            <div style={{ display: "flex", gap: 5, marginBottom: 9, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 10, background: "#F8F8F8", borderRadius: 99,
                padding: "3px 9px", color: "#555", fontWeight: 600
              }}>
                {CrowdLabel[place.crowd_level || 3]} crowd
              </span>
              <span style={{
                fontSize: 10, background: "#F8F8F8", borderRadius: 99,
                padding: "3px 9px", color: "#555", fontWeight: 600
              }}>
                ⏱ {place.duration_hours}h
              </span>
              {place.best_time && (
                <span style={{
                  fontSize: 10, background: "#F8F8F8", borderRadius: 99,
                  padding: "3px 9px", color: "#555", fontWeight: 600
                }}>
                  🕗 {place.best_time.split("(")[0].trim()}
                </span>
              )}
            </div>

            {/* Go if / Skip if — two columns */}
            {(place.go_if?.length > 0 || place.skip_if?.length > 0) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
                {place.go_if?.length > 0 && (
                  <div style={{ background: "#E8F7F2", borderRadius: 8, padding: "7px 9px" }}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: GREEN,
                      marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5
                    }}>
                      ✓ Go if
                    </div>
                    {place.go_if.slice(0, 2).map((t, i) => (
                      <div key={i} style={{
                        fontSize: 10, color: "#065F46",
                        lineHeight: 1.45, marginBottom: i < place.go_if.slice(0, 2).length - 1 ? 3 : 0
                      }}>
                        · {t}
                      </div>
                    ))}
                  </div>
                )}
                {place.skip_if?.length > 0 && (
                  <div style={{ background: "#FEF0F2", borderRadius: 8, padding: "7px 9px" }}>
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: RED,
                      marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5
                    }}>
                      ✕ Skip if
                    </div>
                    {place.skip_if.slice(0, 2).map((t, i) => (
                      <div key={i} style={{
                        fontSize: 10, color: "#9F1239",
                        lineHeight: 1.45, marginBottom: i < place.skip_if.slice(0, 2).length - 1 ? 3 : 0
                      }}>
                        · {t}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Insider tip */}
            {place.insider_tip && (
              <div style={{
                background: `${ORANGE}0F`, border: `1px solid ${ORANGE}22`,
                borderRadius: 8, padding: "6px 9px"
              }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: ORANGE }}>💡 </span>
                <span style={{ fontSize: 11, color: "#555", lineHeight: 1.4 }}>
                  {place.insider_tip.length > 110
                    ? place.insider_tip.slice(0, 110) + "…"
                    : place.insider_tip}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action buttons row */}
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <button onClick={() => decide("left", place)}
          onMouseDown={e => e.currentTarget.style.transform = "scale(0.96)"}
          onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
          style={{
            flex: 1, padding: "11px 0",
            background: "#fff", border: `2px solid ${RED}22`, borderRadius: 12,
            cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13,
            color: RED, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            transition: "transform 0.1s", boxShadow: `0 2px 8px ${RED}18`,
          }}>
          <span style={{ fontSize: 16 }}>✕</span> Skip
        </button>
        <button onClick={() => decide("right", place)}
          onMouseDown={e => e.currentTarget.style.transform = "scale(0.96)"}
          onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
          style={{
            flex: 2, padding: "11px 0",
            background: ORANGE, border: "none", borderRadius: 12,
            cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14,
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            transition: "transform 0.1s", boxShadow: `0 4px 16px ${ORANGE}44`,
          }}>
          <span style={{ fontSize: 18 }}>✓</span> Add to trip
          {added > 0 && <span style={{
            fontSize: 11, background: "rgba(255,255,255,0.25)",
            borderRadius: 99, padding: "1px 7px"
          }}>{added}</span>}
        </button>
      </div>
      <div style={{ textAlign: "center", marginTop: 6, fontSize: 10, color: "#ccc" }}>
        swipe left to skip · swipe right to add
      </div>
    </div>
  );
};

/* ── BOTTOM SHEETS ───────────────────────────────────────────────────── */
const Sheet = ({ title, sub, onClose, children }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 20,
    display: "flex", alignItems: "flex-end", justifyContent: "center",
    background: "rgba(0,0,0,0.4)", backdropFilter: "blur(4px)",
  }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{
      background: "#fff", borderRadius: "20px 20px 0 0",
      width: "100%", maxWidth: 500, maxHeight: "82vh",
      display: "flex", flexDirection: "column",
      animation: "sheetUp 0.28s cubic-bezier(.22,.68,0,.99)",
    }}>
      <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 0" }}>
        <div style={{ width: 36, height: 4, borderRadius: 99, background: "#E0E0E0" }} />
      </div>
      <div style={{ padding: "12px 20px 14px", borderBottom: "1px solid #F0F0F0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111" }}>{title}</div>
            {sub && <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>{sub}</div>}
          </div>
          <button onClick={onClose} style={{
            background: "#F5F5F5", border: "none", borderRadius: "50%",
            width: 30, height: 30, cursor: "pointer", fontSize: 15, color: "#999"
          }}>✕</button>
        </div>
      </div>
      <div style={{ overflowY: "auto", padding: "14px 20px 32px", flex: 1 }}>{children}</div>
    </div>
  </div>
);

const MEAL_ICON = { Room_Only: "🛏", BreakFast: "🍳", Half_Board: "🍽", Full_Board: "🍱", All_Inclusive: "🎉" };
const MEAL_LABEL = { Room_Only: "Room only", BreakFast: "Breakfast incl.", Half_Board: "Half board", Full_Board: "Full board", All_Inclusive: "All inclusive" };
const _fmtDate = d => [String(d.getDate()).padStart(2, "0"), String(d.getMonth() + 1).padStart(2, "0"), d.getFullYear()].join("-");
/* ── Price formatter: shows decimals only when non-zero, never .00 ── */
const fmtPrice = (n) => {
  if (!n && n !== 0) return "";
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "";
  const hasDecimal = (num % 1) !== 0;
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  });
};


/* v5: hotel list extracted from HotelSheet so it can render inline in the
   right-panel Hotels tab. HotelSheet below wraps it in the modal Sheet —
   selection behavior is byte-identical to legacy. */
const HotelListInline = ({ hotels, selectedHotel, onSelect }) => {
  const [expandedHotel, setExpandedHotel] = useState(selectedHotel?.hotel_code || null);
  const [localSelected, setLocalSelected] = useState(selectedHotel || null); // track selection locally

  const handleSelect = (hotel, room) => {
    const hotelLabel = hotel.hotel_name || `Hotel ${hotel.hotel_code}`;
    const roomShort = room.name?.split(",")[0] || "";
    const hotel_name = hotelLabel; // show hotel name in itinerary, room in subtitle
    const built = {
      ...hotel,
      hotel_name,
      hotel_code: hotel.hotel_code,
      selected_room: room,
      selected_room_label: roomShort, // for subtitle display
      price_per_night_inr: room.total_fare,
      location_label: hotel.distance_km != null ? `${Number(hotel.distance_km).toFixed(1)}km away` : "",
    };
    setLocalSelected(built);
    onSelect(built);
  };

  return (
    <>
      {hotels.length === 0 ? (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🏨</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#333" }}>No hotels found</div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>Try adjusting the dates or location</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {hotels.map(h => {
            const isExpanded = expandedHotel === h.hotel_code;
            const isSelected = (localSelected?.hotel_code || selectedHotel?.hotel_code) === h.hotel_code;
            const rooms = h.rooms || [];
            const cheapestRoom = rooms[0];
            const hotelLabel = h.hotel_name || h.best_room_name || `Hotel ${h.hotel_code}`;
            return (
              <div key={h.hotel_code} style={{
                border: `2px solid ${isSelected ? ORANGE : isExpanded ? "#E0D0FF" : "#E8E8E8"}`,
                borderRadius: 14, overflow: "hidden", background: "#fff",
                boxShadow: isSelected ? `0 0 0 3px ${ORANGE}22` : "none",
              }}>
                {/* Hotel header — click to expand */}
                <div onClick={() => setExpandedHotel(isExpanded ? null : h.hotel_code)}
                  style={{ display: "flex", gap: 12, padding: "11px 12px", alignItems: "center", cursor: "pointer" }}>
                  <div style={{ fontSize: 24, flexShrink: 0 }}>🏨</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#111", lineHeight: 1.3 }}>
                      {h.hotel_name || cheapestRoom?.name?.split(",")[0] || `Hotel ${h.hotel_code}`}
                      {isSelected && <span style={{ marginLeft: 6, fontSize: 10, color: ORANGE, fontWeight: 700 }}>✓ Selected</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap", alignItems: "center" }}>
                      {h.distance_km != null && <span style={{ fontSize: 11, color: "#888" }}>📍 {Number(h.distance_km).toFixed(1)}km</span>}
                      {rooms.length > 0 && <span style={{ fontSize: 11, color: "#888" }}>{rooms.length} room type{rooms.length !== 1 ? "s" : ""}</span>}
                      {cheapestRoom?.is_refundable && <span style={{ fontSize: 10, color: "#22C55E", fontWeight: 600 }}>✓ Refundable</span>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {cheapestRoom && <div style={{ fontWeight: 800, fontSize: 14, color: ORANGE }}>
                      ₹{fmtPrice(cheapestRoom.total_fare)}
                    </div>}
                    {cheapestRoom && <div style={{ fontSize: 10, color: "#bbb" }}>from/stay</div>}
                  </div>
                  <span style={{ fontSize: 14, color: "#bbb", flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* Room list */}
                {isExpanded && (
                  <div style={{ borderTop: "1px solid #F0F0F0", padding: "8px 12px 12px" }}>
                    {rooms.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#aaa", padding: "8px 0" }}>No room info available</div>
                    ) : (
                      <>
                        <div style={{
                          fontSize: 11, color: "#888", marginBottom: 8, fontWeight: 600,
                          letterSpacing: 0.5, textTransform: "uppercase"
                        }}>Select a room</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {rooms.map((room, i) => {
                            const selCode = localSelected?.selected_room?.booking_code || selectedHotel?.selected_room?.booking_code;
                            const isRoomSelected = isSelected && selCode === room.booking_code;
                            return (
                              <div key={room.booking_code || i} onClick={() => handleSelect(h, room)} style={{
                                border: `1.5px solid ${isRoomSelected ? ORANGE : "#E8E8E8"}`,
                                background: isRoomSelected ? `${ORANGE}08` : "#FAFAFA",
                                borderRadius: 10, padding: "10px 12px", cursor: "pointer", transition: "all 0.12s",
                              }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 12, color: "#111", lineHeight: 1.4 }}>
                                      {room.name || "Room"}
                                    </div>
                                    <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                                      <span style={{ fontSize: 11, color: "#666" }}>
                                        {MEAL_ICON[room.meal_type] || "🛏"} {MEAL_LABEL[room.meal_type] || room.meal_type || "Room only"}
                                      </span>
                                      {room.is_refundable && <span style={{ fontSize: 10, color: "#22C55E", fontWeight: 600 }}>✓ Free cancel</span>}
                                      {room.promotions?.length > 0 && <span style={{ fontSize: 10, color: PURPLE, fontWeight: 600 }}>🏷 {room.promotions[0]}</span>}
                                    </div>
                                    {room.inclusion && <div style={{ fontSize: 10, color: "#888", marginTop: 3, lineHeight: 1.4 }}>
                                      {room.inclusion.split(",").slice(0, 3).join(" · ")}
                                    </div>}
                                  </div>
                                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                                    <div style={{ fontWeight: 800, fontSize: 14, color: isRoomSelected ? ORANGE : "#111" }}>
                                      ₹{fmtPrice(room.total_fare || 0)}
                                    </div>
                                    <div style={{ fontSize: 10, color: "#aaa" }}>total</div>
                                    {room.base_per_night > 0 && <div style={{ fontSize: 10, color: "#888" }}>
                                      ≈₹{fmtPrice(room.base_per_night)}/n
                                    </div>}
                                  </div>
                                </div>
                                {isRoomSelected && <div style={{ marginTop: 6, fontSize: 10, color: ORANGE, fontWeight: 700 }}>✓ Selected</div>}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const HotelSheet = ({ hotels, selectedHotel, onSelect, onClose }) => (
  <Sheet title="Choose Hotel & Room" sub={`${hotels.length} hotel${hotels.length !== 1 ? "s" : ""} available`} onClose={onClose}>
    <HotelListInline hotels={hotels} selectedHotel={selectedHotel} onSelect={onSelect} />
  </Sheet>
);






const LegPill = ({ seg, onSeatClassChange = null }) => {
  const isFlight = !!seg.carrier;
  const avl = seg.seat_avail_ui;
  // ── Seat class bar state (trains only) ──
  const [pendingClass, setPendingClass] = useState(null);
  const [failedClass, setFailedClass] = useState(null);
  const failTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(failTimerRef.current), []);
  const activeClass = avl?.seat_class || seg.seat_class_ui || null;
  // "GN" is the general/unreserved quota, not a bookable seat class — exclude it
  const seatClasses = (!isFlight && Array.isArray(seg.seat_classes) ? seg.seat_classes : [])
    .filter(c => String(c).toUpperCase() !== "GN");
  const showClassBar = !isFlight && seatClasses.length > 0 && typeof onSeatClassChange === "function";

  const pickClass = async (cls) => {
    if (pendingClass || cls === activeClass) return;
    setFailedClass(null);
    setPendingClass(cls);
    try {
      await onSeatClassChange(cls); // parent updates route state on success
    } catch (_) {
      // Keep previous class selected; flag the failed pill briefly
      setFailedClass(cls);
      toast.error(`Availability check failed — keeping ${activeClass || "current class"}`, { position: "top-center" });
      clearTimeout(failTimerRef.current);
      failTimerRef.current = setTimeout(() => setFailedClass(null), 3000);
    } finally {
      setPendingClass(null);
    }
  };
  const isAvail = avl?.is_avail;
  const dep = seg.origin_dep_dt ? new Date(seg.origin_dep_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  const arr = seg.dest_arr_dt ? new Date(seg.dest_arr_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  const legSrc = seg.origin_name || seg.origin_code_iata || seg.origin_code || "";
  const legDest = seg.dest_name || seg.dest_code_iata || seg.dest_code || "";
  const fare = avl?.fare !== undefined ? Number(avl.fare) : null;

  return (
  <div>
    <div style={{
      background: isFlight ? "#F5F0FF" : "#F0F9F4",
      borderRadius: 9, padding: "8px 10px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <span style={{ fontSize: 14, flexShrink: 0 }}>{isFlight ? "✈️" : "🚂"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 11, color: "#111", marginBottom: 2 }}>
          {isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} (${seg.train_num})`}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#333" }}>{dep}</span>
          <span style={{ fontSize: 9, color: "#ccc", flex: 1, borderTop: "1px dashed #ddd", margin: "0 3px" }} />
          <span style={{ fontSize: 10, fontWeight: 700, color: "#333" }}>{arr}</span>
        </div>
        <div style={{ fontSize: 10, color: "#777", marginTop: 1 }}>
          {legSrc} → {legDest}
          <span style={{ marginLeft: 6, color: "#bbb" }}>{fmtDuration(seg.duration_mins)}</span>
          {seg.seat_class_ui && <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>{seg.seat_class_ui}</span>}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 3,
          background: isAvail ? GREEN + "18" : "#F5F5F5",
          borderRadius: 99, padding: "2px 7px",
        }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: isAvail ? GREEN : "#bbb" }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: isAvail ? GREEN : "#999" }}>
            {isAvail ? "Avail" : "Check"}
          </span>
        </div>
        {fare && <span style={{ fontSize: 10, fontWeight: 700, color: "#111" }}>₹{fmtPrice(fare)}</span>}
      </div>
    </div>

    {/* ── Seat class bar — horizontal scroll, active class shows quota + avail ── */}
    {showClassBar && (
      <div style={{
        display: "flex", gap: 6, marginTop: 5, overflowX: "auto",
        WebkitOverflowScrolling: "touch", paddingBottom: 2,
        scrollbarWidth: "none",
      }}>
        {seatClasses.map(cls => {
          const isActive = cls === activeClass;
          const isPending = cls === pendingClass;
          const isFailed = cls === failedClass;
          const availColor = avl?.is_avail ? GREEN : RED;
          return (
            <button
              key={cls}
              onClick={() => pickClass(cls)}
              disabled={!!pendingClass}
              style={{
                flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 5,
                padding: "4px 10px", borderRadius: 99, fontFamily: "inherit",
                fontSize: 10, fontWeight: 700, whiteSpace: "nowrap",
                cursor: pendingClass ? "default" : "pointer",
                border: isActive ? `1.5px solid ${availColor}` : "1.5px solid #E4E4E4",
                background: isActive ? availColor + "14" : "#fff",
                color: isActive ? availColor : "#666",
                opacity: pendingClass && !isPending ? 0.5 : 1,
                transition: "all 0.15s",
              }}
            >
              {cls}
              {isActive && avl?.quota && (
                <span style={{ fontWeight: 600, opacity: 0.85 }}>· {avl.quota}</span>
              )}
              {isActive && avl?.avl && (
                <span style={{ fontWeight: 600, opacity: 0.85, maxWidth: 86, overflow: "hidden", textOverflow: "ellipsis" }}>
                  · {avl.avl}
                </span>
              )}
              {isPending && (
                <span style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  border: "2px solid #ccc", borderTopColor: ORANGE,
                  display: "inline-block", animation: "rcspin 0.7s linear infinite",
                }} />
              )}
              {isFailed && <span title="Availability check failed">⚠️</span>}
            </button>
          );
        })}
      </div>
    )}
  </div>
  );
};

// Reusable section of route cards inside RouteSheet
const RouteSection = ({ title, color, routes, currentId, onSelect, PAGE_SIZE = 3, onSeatAvail = null }) => {
  const [sortKey, setSortKey] = useState("best");
  const [page, setPage] = useState(0);
  const sorted = applySort(routes || [], sortKey);
  const visible = sorted.slice(0, (page + 1) * PAGE_SIZE);
  const hasMore = sorted.length > visible.length;

  return (
    <div>
      {/* Section header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{ width: 3, height: 18, borderRadius: 2, background: color, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>{title}</span>
        <span style={{ fontSize: 11, color: "#aaa" }}>({(routes || []).length} found)</span>
      </div>

      {/* Sort chips */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        {[
          { key: "cheapest_avail", label: "Cheapest Avail" },
          { key: "quickest_avail", label: "Quickest Avail" },
          { key: "cheapest", label: "Cheapest" },
          { key: "quickest", label: "Quickest" },
          { key: "depart_early", label: "Depart Early" },
          { key: "reach_early", label: "Reach Early" },
        ].map(opt => (
          <button key={opt.key} onClick={() => { setSortKey(opt.key); setPage(0); }} style={{
            padding: "3px 8px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
            fontSize: 10, fontWeight: sortKey === opt.key ? 700 : 500,
            border: `1.5px solid ${sortKey === opt.key ? color : "#E0E0E0"}`,
            background: sortKey === opt.key ? color + "12" : "#fff",
            color: sortKey === opt.key ? color : "#777",
          }}>{opt.label}</button>
        ))}
      </div>

      {/* Route cards */}
      {visible.length === 0 ? (
        <div style={{ textAlign: "center", color: "#bbb", padding: "16px 0", fontSize: 12 }}>
          No routes found — search again with different dates or mode.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map((route, ri) => {
            const cls = classifyRoute(route);
            const fare = computeFare(route);
            const dur = computeDuration(route);
            const nodes = route.nodes || [];
            const isCur = currentId === route.route_id;
            const allAvail = nodes.every(n => n.segment.seat_avail_ui?.is_avail);
            const someAvail = nodes.some(n => n.segment.seat_avail_ui?.is_avail);
            const avColor = allAvail ? GREEN : someAvail ? AMBER : "#bbb";
            const avLabel = allAvail ? "All avail" : someAvail ? "Partial" : "Check";

            return (
              <div key={route.route_id || ri} onClick={() => onSelect(route)} style={{
                border: `2px solid ${isCur ? cls.color : "#E8E8E8"}`,
                background: isCur ? cls.color + "0A" : "#fff",
                borderRadius: 13, overflow: "hidden", cursor: "pointer",
                boxShadow: isCur ? `0 0 0 3px ${cls.color}22` : "none",
                transition: "all 0.14s",
              }}>
                <div style={{
                  padding: "9px 12px 8px", display: "flex", alignItems: "center",
                  justifyContent: "space-between", borderBottom: "1px solid #F5F5F5"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ fontSize: 17 }}>{cls.icon}</span>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 12, color: "#111" }}>{cls.label}</span>
                        <span style={{
                          fontSize: 9, color: cls.color, background: cls.color + "18",
                          borderRadius: 99, padding: "1px 6px", fontWeight: 600
                        }}>{cls.badge}</span>
                        <span style={{
                          fontSize: 9, color: avColor, background: avColor + "15",
                          borderRadius: 99, padding: "1px 6px", fontWeight: 600
                        }}>{avLabel}</span>
                        {isCur && <span style={{ fontSize: 9, color: GREEN, fontWeight: 700 }}>✓ Selected</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, color: "#999" }}>{fmtDuration(dur)}</span>
                        {(() => {
                          const dep = route.nodes?.[0]?.segment?.origin_dep_dt;
                          if (!dep) return null;
                          const d = new Date(dep).toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
                          return <span style={{
                            fontSize: 10, color: "#bbb", background: "#F5F5F7",
                            borderRadius: 99, padding: "1px 6px"
                          }}>📅 {d}</span>;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {fare
                      ? <div style={{ fontWeight: 800, fontSize: 14, color: "#111" }}>₹{fmtPrice(fare)}</div>
                      : <div style={{ fontSize: 10, color: "#bbb" }}>Check IRCTC</div>}
                  </div>
                </div>
                <div style={{ padding: "7px 10px 9px", display: "flex", flexDirection: "column", gap: 5 }}>
                  {/* Origin cab strip */}
                  {cabLabel(nodes[0]?.segment?.origin_comment || nodes[0]?.segment?.origin_comment_web) && (
                    <div style={{
                      fontSize: 9.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 5, padding: "2px 8px", display: "inline-block"
                    }}>
                      {cabLabel(nodes[0]?.segment?.origin_comment || nodes[0]?.segment?.origin_comment_web)} · to departure point
                    </div>
                  )}
                  {nodes.map((node, ni) => (
                    <div key={node.node_id || ni}>
                      <LegPill
                        seg={node.segment}
                        onSeatClassChange={onSeatAvail
                          ? (cls) => onSeatAvail(route.route_id, node.node_id, node.segment, cls)
                          : null}
                      />
                      {ni < nodes.length - 1 && route.stops_data?.[ni] && (
                        <div style={{
                          margin: "3px 0 0 22px", fontSize: 9, color: "#92400E",
                          background: "#FFFBEB", borderRadius: 5, padding: "2px 7px", display: "inline-block"
                        }}>
                          🔄 {route.stops_data[ni].stop_title}
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Destination cab strip */}
                  {cabLabel(nodes[nodes.length - 1]?.segment?.dest_comment || nodes[nodes.length - 1]?.segment?.dest_comment_web) && (
                    <div style={{
                      fontSize: 9.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 5, padding: "2px 8px", display: "inline-block"
                    }}>
                      {cabLabel(nodes[nodes.length - 1]?.segment?.dest_comment || nodes[nodes.length - 1]?.segment?.dest_comment_web)} · to destination
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {hasMore && (
        <button onClick={() => setPage(p => p + 1)} style={{
          marginTop: 8, width: "100%", padding: "8px 0",
          background: "#F5F5F7", border: "1.5px solid #E0E0E0", borderRadius: 9,
          cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 600, color: "#666",
        }}>
          Show {Math.min(PAGE_SIZE, sorted.length - visible.length)} more ↓
        </button>
      )}
    </div>
  );
};

const RouteSheet = ({ routes, returnRoutes = [], currentOutbound, currentReturn, onSelectOutbound, onSelectReturn, onClose, initialTab = "outbound", returnSearching = false, sheetNote = null, onSeatAvail = null }) => {
  const [tab, setTab] = useState(initialTab);

  // When initialTab changes (e.g. opened from nights change), switch to it
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  return (
    <Sheet title="Edit Routes" sub={null} onClose={onClose}>
      {/* Context note — shown when nights changed */}
      {sheetNote && (
        <div style={{
          background: `${ORANGE}12`, border: `1px solid ${ORANGE}40`,
          borderRadius: 10, padding: "10px 14px", marginBottom: 12,
          display: "flex", alignItems: "flex-start", gap: 8,
        }}>
          <span style={{ fontSize: 16, flexShrink: 0 }}>📅</span>
          <div style={{ fontSize: 12, color: "#7A3500", lineHeight: 1.5 }}>{sheetNote}</div>
        </div>
      )}

      {/* Tab switcher */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 16,
        background: "#F5F5F7", borderRadius: 12, padding: 3
      }}>
        {[
          { k: "outbound", l: "🚀 Outbound", count: (routes || []).length },
          { k: "return", l: "🔄 Return", count: returnSearching ? "…" : (returnRoutes || []).length },
        ].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            flex: 1, padding: "9px 12px", borderRadius: 10, cursor: "pointer",
            fontFamily: "inherit", fontSize: 12, fontWeight: tab === t.k ? 700 : 500,
            border: "none",
            background: tab === t.k ? "#fff" : "transparent",
            color: tab === t.k ? "#111" : "#888",
            boxShadow: tab === t.k ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
            transition: "all 0.15s",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            {t.l}
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: tab === t.k ? (t.k === "outbound" ? PURPLE : ORANGE) : "#E0E0E0",
              color: tab === t.k ? "#fff" : "#888",
              borderRadius: 99, padding: "1px 7px",
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Active section */}
      {tab === "outbound" ? (
        <RouteSection
          title="" color={PURPLE}
          routes={routes}
          currentId={currentOutbound?.id}
          onSelect={r => { onSelectOutbound(r); }}
          onSeatAvail={onSeatAvail}
        />
      ) : returnSearching ? (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "32px 16px", gap: 14
        }}>
          <div style={{ display: "flex", gap: 6 }}>
            {[0, 1, 2].map(i => (
              <span key={i} style={{
                width: 10, height: 10, borderRadius: "50%", background: ORANGE,
                display: "inline-block",
                animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
              }} />
            ))}
          </div>
          <div style={{ fontWeight: 600, fontSize: 13, color: "#555" }}>
            Searching return routes for new date…
          </div>
          <div style={{ fontSize: 11, color: "#aaa", textAlign: "center", maxWidth: 240 }}>
            Finding trains and flights back for your updated schedule
          </div>
        </div>
      ) : (
        <RouteSection
          title="" color={ORANGE}
          routes={returnRoutes}
          currentId={currentReturn?.id}
          onSelect={r => { onSelectReturn(r); }}
          onSeatAvail={onSeatAvail}
        />
      )}
    </Sheet>
  );
};

/* ── Inline route sheet — same tabs but embedded in chat, no fixed overlay ── */
const Spinner = ({ color = "#888" }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 5, padding: "12px 0", color: "#888", fontSize: 12 }}>
    {[0, 1, 2].map(i => (
      <span key={i} style={{
        width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block",
        opacity: 0.4, animation: `agentBounce 1s ${i * 0.2}s ease-in-out infinite`
      }} />
    ))}
    <span style={{ marginLeft: 4 }}>Searching…</span>
  </div>
);

const InlineRouteSheet = ({
  routes, returnRoutes = [], selectedRouteId, outboundDone, returnDone,
  onSelect, onSelectReturn, onSkipReturn, noRoutes, tripInfo,
  defaultTab = "outbound", returnSearching = false,
  fullWidth = false,             // v5: stretch to full panel width (right-side widget)
  selectedReturnRouteId = null,  // v5: highlight the picked return route
  hideSkip = false,              // v5: edit mode — hide skip buttons
  onSeatAvail = null,            // v6: seat-class availability re-check (trains)
}) => {
  const [tab, setTab] = useState(defaultTab);

  // Auto-switch to return tab when outbound route is selected
  useEffect(() => {
    if (selectedRouteId) setTab("return");
  }, [selectedRouteId]);

  // Switch to defaultTab if it changes externally (e.g. nights-change flow)
  useEffect(() => { setTab(defaultTab); }, [defaultTab]);

  const tabs = [
    { k: "outbound", l: "🚀 Outbound", count: (routes || []).length, done: outboundDone, color: PURPLE },
    { k: "return", l: "🔄 Return", count: returnSearching ? "…" : (returnRoutes || []).length, done: returnDone || returnSearching, color: ORANGE },
  ];

  return (
    <div className="inline-route-sheet" style={fullWidth ? { width: "100%" } : { maxWidth: 520 }}>
      {/* Tab switcher */}
      <div style={{
        display: "flex", gap: 0, marginBottom: 10,
        background: "#F5F5F7", borderRadius: 12, padding: 3
      }}>
        {tabs.map((t, i) => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            flex: 1, padding: "8px 10px", borderRadius: 10, cursor: "pointer",
            fontFamily: "inherit", fontSize: 12, fontWeight: tab === t.k ? 700 : 500,
            border: "none",
            background: tab === t.k ? "#fff" : "transparent",
            color: tab === t.k ? "#111" : "#888",
            boxShadow: tab === t.k ? "0 1px 4px rgba(0,0,0,0.10)" : "none",
            transition: "all 0.15s",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            {t.l}
            {t.done ? (
              <span style={{
                fontSize: 10, fontWeight: 700,
                background: tab === t.k ? t.color : "#E0E0E0",
                color: tab === t.k ? "#fff" : "#888",
                borderRadius: 99, padding: "1px 6px",
              }}>{t.count}</span>
            ) : (
              /* Loading spinner pill */
              <span style={{
                display: "inline-flex", gap: 2, alignItems: "center",
                background: tab === t.k ? t.color + "22" : "#E8E8E8",
                borderRadius: 99, padding: "2px 7px",
              }}>
                {[0, 1, 2].map(j => (
                  <span key={j} style={{
                    width: 4, height: 4, borderRadius: "50%",
                    background: tab === t.k ? t.color : "#aaa", display: "inline-block",
                    animation: `agentBounce 0.9s ${j * 0.15}s ease-in-out infinite`,
                  }} />
                ))}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Outbound tab */}
      {tab === "outbound" && (
        <div>
          {!outboundDone && routes.length === 0 && <Spinner color={PURPLE} />}
          {routes.length > 0 && (
            <RouteSection
              title="" color={PURPLE}
              routes={routes}
              currentId={selectedRouteId}
              onSelect={onSelect}
              onSeatAvail={onSeatAvail}
            />
          )}
          {outboundDone && routes.length === 0 && (
            <div style={{ fontSize: 13, color: "#E8334A", padding: "10px 0" }}>
              No outbound routes found. You can skip and still plan your trip.
            </div>
          )}
          {outboundDone && !hideSkip && (
            <button onClick={() => { onSelect(null); }} style={{
              marginTop: 8, width: "100%", padding: "9px 0",
              background: "#F5F5F7", border: "1.5px solid #E0E0E0", borderRadius: 10,
              cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "#666",
            }}>Skip outbound →</button>
          )}
        </div>
      )}

      {/* Return tab */}
      {tab === "return" && (
        <div>
          {returnSearching && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 0", gap: 12 }}>
              <div style={{ display: "flex", gap: 5 }}>
                {[0, 1, 2].map(i => (
                  <span key={i} style={{
                    width: 9, height: 9, borderRadius: "50%", background: ORANGE,
                    display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`
                  }} />
                ))}
              </div>
              <div style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>Searching return routes for new date…</div>
            </div>
          )}
          {!returnSearching && !returnDone && returnRoutes.length === 0 && <Spinner color={ORANGE} />}
          {returnRoutes.length > 0 && (
            <RouteSection
              title="" color={ORANGE}
              routes={returnRoutes}
              currentId={selectedReturnRouteId}
              onSelect={onSelectReturn}
              onSeatAvail={onSeatAvail}
            />
          )}
          {!returnSearching && returnDone && returnRoutes.length === 0 && (
            <div style={{ fontSize: 13, color: "#999", padding: "6px 0" }}>No return routes found.</div>
          )}
          {!hideSkip && (
            <button onClick={onSkipReturn} style={{
              marginTop: 8, width: "100%", padding: "9px 0",
              background: "#F5F5F7", border: "1.5px solid #E0E0E0", borderRadius: 10,
              cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "#666",
            }}>Skip return route →</button>
          )}
        </div>
      )}
    </div>
  );
};

const DaysSheet = ({ nights, onChange, onClose }) => {
  const [n, setN] = useState(nights);
  return (
    <Sheet title="Trip Duration" onClose={onClose}>
      <div style={{ textAlign: "center", padding: "16px 0 20px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 32, marginBottom: 24 }}>
          <button onClick={() => setN(v => Math.max(1, v - 1))} style={{
            width: 48, height: 48, borderRadius: "50%", border: "2px solid #E0E0E0",
            background: "#F8F8F8", fontSize: 24, cursor: "pointer", color: "#111", fontFamily: "inherit"
          }}>−</button>
          <div>
            <div style={{ fontSize: 60, fontWeight: 900, color: "#111", lineHeight: 1 }}>{n}</div>
            <div style={{ fontSize: 14, color: "#888", fontWeight: 600 }}>nights</div>
          </div>
          <button onClick={() => setN(v => v + 1)} style={{
            width: 48, height: 48, borderRadius: "50%", border: `2px solid ${ORANGE}`,
            background: "#FEF0EA", fontSize: 24, cursor: "pointer", color: ORANGE, fontFamily: "inherit"
          }}>+</button>
        </div>
        <button onClick={() => { onChange(n); onClose(); }} style={{
          width: "100%", padding: "13px 0", background: ORANGE, border: "none",
          borderRadius: 12, color: "#fff", fontWeight: 700, fontSize: 14,
          cursor: "pointer", fontFamily: "inherit"
        }}>Confirm {n} nights</button>
      </div>
    </Sheet>
  );
};

/* ── TIMELINE ────────────────────────────────────────────────────────── */
const TLNode = ({ icon, color, last }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 28, flexShrink: 0 }}>
    <div style={{
      width: 28, height: 28, borderRadius: "50%", background: color + "18",
      border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13
    }}>
      {icon}
    </div>
    {!last && <div style={{
      width: 2, flex: 1, minHeight: 12,
      background: `repeating-linear-gradient(to bottom,${color}40 0,${color}40 4px,transparent 4px,transparent 8px)`,
      margin: "3px 0"
    }} />}
  </div>
);

const TLRow = ({ icon, color, label, sub, right, onEdit, onRemove, last }) => (
  <div style={{ display: "flex", gap: 10 }}>
    <TLNode icon={icon} color={color} last={last} />
    <div style={{
      flex: 1, paddingBottom: last ? 0 : 12, display: "flex",
      alignItems: "flex-start", justifyContent: "space-between", paddingTop: 4
    }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#111" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, marginLeft: 8, flexShrink: 0 }}>
        {right && <span style={{ fontSize: 12, fontWeight: 700, color: "#111" }}>{right}</span>}
        <div style={{ display: "flex", gap: 4 }}>
          {onEdit && (
            <button onClick={onEdit} style={{
              fontSize: 10, color: PURPLE, background: "#F0EBF9",
              border: "none", borderRadius: 99, padding: "3px 9px", cursor: "pointer",
              fontFamily: "inherit", fontWeight: 600
            }}>Edit</button>
          )}
          {onRemove && (
            <button onClick={onRemove} style={{
              fontSize: 10, color: RED, background: "#FEF0F2",
              border: "none", borderRadius: 99, padding: "3px 9px", cursor: "pointer",
              fontFamily: "inherit", fontWeight: 600
            }}>Remove</button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const DayRow = ({ day, expanded, onToggle, last, date }) => (
  <div style={{ display: "flex", gap: 10 }}>
    <TLNode icon="☀️" color={ORANGE} last={last} />
    <div style={{ flex: 1, paddingBottom: last ? 0 : 10 }}>
      <button onClick={onToggle} style={{
        width: "100%", background: "#F8F8F8", border: "1px solid #E8E8E8",
        borderRadius: 10, padding: "8px 11px", cursor: "pointer", fontFamily: "inherit",
        display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: expanded ? 7 : 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>Day {day.day_number}</span>
          {date && <span style={{
            fontSize: 10, color: ORANGE, background: ORANGE + "18", borderRadius: 99,
            padding: "2px 8px", fontWeight: 700
          }}>{date}</span>}
          {day.area_summary && <span style={{ fontSize: 11, color: "#888" }}>{day.area_summary}</span>}
          <span style={{ fontSize: 11, color: "#bbb" }}>· {day.place_visits?.length || 0} places</span>
        </div>
        <span style={{
          fontSize: 11, color: "#bbb", display: "inline-block",
          transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s"
        }}>▾</span>
      </button>
      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(day.place_visits || []).map((v, pi) => (
            <div key={pi} style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#F8F8F8", borderRadius: 10, padding: "7px 9px", border: "1px solid #E8E8E8"
            }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: PURPLE, flexShrink: 0 }} />
              <img src={placeImg(v.place?.type)} alt={v.place?.name}
                style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0 }}
                onError={e => { e.target.src = IMGS.city; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontWeight: 600, fontSize: 12, color: "#111",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                }}>{v.place?.name}</div>
                <div style={{ fontSize: 10, color: "#888" }}>{v.start_time}–{v.end_time} · {v.travel_from_prev}</div>
              </div>
            </div>
          ))}
          {day.day_notes && <div style={{ fontSize: 11, color: "#888", padding: "4px 4px 0" }}>ℹ️ {day.day_notes}</div>}
        </div>
      )}
    </div>
  </div>
);


/* ── ITINERARY SHARE UTILITIES ───────────────────────────────────────── */

// Compress a string using gzip via CompressionStream, return base64url string
async function compressToBase64(str) {
  const bytes = new TextEncoder().encode(str);
  const cs = new CompressionStream("gzip");
  const writer = cs.writable.getWriter();
  writer.write(bytes);
  writer.close();
  const out = [];
  const reader = cs.readable.getReader();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    out.push(...value);
  }
  // Convert to base64url (URL-safe, no padding issues)
  const b64 = btoa(String.fromCharCode(...out));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Decompress base64url string back to original string
async function decompressFromBase64(b64url) {
  // Restore standard base64
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  const ds = new DecompressionStream("gzip");
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();
  const chunks = [];
  const reader = ds.readable.getReader();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const total = chunks.reduce((a, c) => a + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.length; }
  return new TextDecoder().decode(result);
}

// Build a SLIM shareable payload — only what the share page needs, nothing extra
function buildSharePayload({ tripState, tripInfo, route, returnRoute, selectedHotel, hasStay, nights, allHotels }) {
  // Slim tripState: only selected places + itinerary + key metadata
  const selectedIds = new Set(tripState?.selected_place_ids || []);
  const slimPlaces = (tripState?.all_places || [])
    .filter(p => selectedIds.has(p.id))
    .map(p => ({
      id: p.id, name: p.name, type: p.type, destination: p.destination,
      duration_hours: p.duration_hours, cost_inr: p.cost_inr,
      insider_tip: p.insider_tip, rating: p.rating, lat: p.lat, lng: p.lng,
    }));

  // Slim itinerary: keep only fields the share page renders
  const slimItinerary = (tripState?.itinerary || []).map(day => ({
    day_number: day.day_number,
    area_summary: day.area_summary,
    day_notes: day.day_notes,
    place_visits: (day.place_visits || []).map(v => ({
      start_time: v.start_time,
      end_time: v.end_time,
      travel_from_prev: v.travel_from_prev,
      notes: v.notes,
      place: v.place ? {
        id: v.place.id,
        name: v.place.name,
        type: v.place.type,
        insider_tip: v.place.insider_tip,
      } : null,
    })),
  }));

  // Slim route: keep display fields + rawRoute nodes (for leg detail) but drop heavy nested arrays
  const slimRoute = route ? {
    id: route.id,
    icon: route.icon,
    label: route.label,
    badge: route.badge,
    color: route.color,
    sub: route.sub,
    detail: route.detail,
    price: route.price,
    bookingLink: route.bookingLink,
    rawRoute: route.rawRoute ? {
      route_id: route.rawRoute.route_id,
      num_stops: route.rawRoute.num_stops,
      stops_data: route.rawRoute.stops_data,
      nodes: (route.rawRoute.nodes || []).map(n => ({
        node_id: n.node_id,
        segment: {
          train_name: n.segment.train_name,
          train_num: n.segment.train_num,
          carrier: n.segment.carrier,
          flight_num: n.segment.flight_num,
          origin_name: n.segment.origin_name,
          origin_code_iata: n.segment.origin_code_iata,
          dest_name: n.segment.dest_name,
          dest_code_iata: n.segment.dest_code_iata,
          origin_dep_dt: n.segment.origin_dep_dt,
          dest_arr_dt: n.segment.dest_arr_dt,
          duration_mins: n.segment.duration_mins,
          seat_class_ui: n.segment.seat_class_ui,
          seat_avail_ui: n.segment.seat_avail_ui,
          baggage: n.segment.baggage,
          origin_platform: n.segment.origin_platform,
          booking_link: n.segment.booking_link,
        },
      })),
    } : null,
  } : null;

  // Slim hotel
  const slimHotel = hasStay && selectedHotel ? {
    hotel_code: selectedHotel.hotel_code,
    hotel_name: selectedHotel.hotel_name,
    hotel_rating: selectedHotel.hotel_rating,
    city_name: selectedHotel.city_name,
    hotel_area: selectedHotel.hotel_area,
    price_per_night_inr: selectedHotel.price_per_night_inr,
    primary_image: selectedHotel.primary_image,
    location_label: selectedHotel.location_label,
    distance_km: selectedHotel.distance_km,
    // Full rooms array for booking modal
    rooms: (selectedHotel.rooms || []).map(r => ({
      name: r.name,
      booking_code: r.booking_code || r.BookingCode || "",
      total_fare: r.total_fare || r.TotalFare || 0,
      total_tax: r.total_tax || r.TotalTax || 0,
      base_per_night: r.base_per_night || (r.DayRates?.[0]?.[0]?.BasePrice) || 0,
      meal_type: r.meal_type || r.MealType || "Room_Only",
      is_refundable: r.is_refundable ?? r.IsRefundable ?? false,
      inclusion: r.inclusion || r.Inclusion || "",
      promotions: r.promotions || r.RoomPromotion || [],
      cancel_policies: r.cancel_policies || r.CancelPolicies || [],
    })),
    selected_room: selectedHotel.selected_room ? {
      name: selectedHotel.selected_room.name,
      total_fare: selectedHotel.selected_room.total_fare || selectedHotel.selected_room.TotalFare || 0,
      total_tax: selectedHotel.selected_room.total_tax || selectedHotel.selected_room.TotalTax || 0,
      base_per_night: selectedHotel.selected_room.base_per_night || 0,
      meal_type: selectedHotel.selected_room.meal_type || selectedHotel.selected_room.MealType || "",
      is_refundable: selectedHotel.selected_room.is_refundable ?? selectedHotel.selected_room.IsRefundable ?? false,
      booking_code: selectedHotel.selected_room.booking_code || selectedHotel.selected_room.BookingCode || "",
    } : null,
  } : null;

  // Slim allHotels for the hotel picker in TripShare
  const slimAllHotels = (allHotels || []).slice(0, 15).map(h => ({
    hotel_code: h.hotel_code,
    hotel_name: h.hotel_name,
    distance_km: h.distance_km,
    location_label: h.location_label,
    rooms: (h.rooms || []).map(r => ({
      name: r.name,
      booking_code: r.booking_code || r.BookingCode || "",
      total_fare: r.total_fare || r.TotalFare || 0,
      total_tax: r.total_tax || r.TotalTax || 0,
      base_per_night: r.base_per_night || (r.DayRates?.[0]?.[0]?.BasePrice) || 0,
      meal_type: r.meal_type || r.MealType || "Room_Only",
      is_refundable: r.is_refundable ?? r.IsRefundable ?? false,
      inclusion: r.inclusion || r.Inclusion || "",
      promotions: r.promotions || r.RoomPromotion || [],
    })),
  }));

  // Slim return route — same shape as slimRoute
  const slimReturnRoute = returnRoute ? {
    id: returnRoute.id,
    icon: returnRoute.icon,
    label: returnRoute.label,
    badge: returnRoute.badge,
    color: returnRoute.color,
    sub: returnRoute.sub,
    detail: returnRoute.detail,
    price: returnRoute.price,
    bookingLink: returnRoute.bookingLink,
    rawRoute: returnRoute.rawRoute ? {
      route_id: returnRoute.rawRoute.route_id,
      num_stops: returnRoute.rawRoute.num_stops,
      stops_data: returnRoute.rawRoute.stops_data,
      nodes: (returnRoute.rawRoute.nodes || []).map(n => ({
        node_id: n.node_id,
        segment: {
          train_name: n.segment.train_name,
          train_num: n.segment.train_num,
          carrier: n.segment.carrier,
          flight_num: n.segment.flight_num,
          origin_name: n.segment.origin_name,
          origin_code_iata: n.segment.origin_code_iata,
          dest_name: n.segment.dest_name,
          dest_code_iata: n.segment.dest_code_iata,
          origin_dep_dt: n.segment.origin_dep_dt,
          dest_arr_dt: n.segment.dest_arr_dt,
          duration_mins: n.segment.duration_mins,
          seat_class_ui: n.segment.seat_class_ui,
          seat_avail_ui: n.segment.seat_avail_ui,
          baggage: n.segment.baggage,
          origin_platform: n.segment.origin_platform,
          booking_link: n.segment.booking_link,
        },
      })),
    } : null,
  } : null;

  return {
    v: 2,  // version 2 = compressed slim payload
    tripState: {
      primary_destination: tripState?.primary_destination,
      start_date: tripState?.start_date,
      num_days: tripState?.num_days,
      selected_place_ids: tripState?.selected_place_ids,
      itinerary_complete: true,
      all_places: slimPlaces,
      itinerary: slimItinerary,
    },
    tripInfo,
    route: slimRoute,
    returnRoute: slimReturnRoute,
    hotel: slimHotel,
    allHotels: slimAllHotels,
    nights,
    createdAt: new Date().toISOString(),
  };
}

// Pack: slim → JSON → gzip → base64url (async, returns string)
async function packTrip(payload) {
  try {
    const json = JSON.stringify(payload);
    // console.log(`[share] JSON size: ${json.length} chars`);
    const compressed = await compressToBase64(json);
    // console.log(`[share] compressed base64 size: ${compressed.length} chars`);
    return compressed;
  } catch (e) {
    // console.error("[share] pack error:", e);
    return null;
  }
}

// Unpack: base64url → gzip decompress → JSON parse (async)
async function unpackTrip(encoded) {
  try {
    // Try gzip decompression first (v2 payloads)
    const json = await decompressFromBase64(encoded);
    return JSON.parse(json);
  } catch (e) {
    // Fallback: try legacy uncompressed base64 (v1 payloads)
    try {
      return JSON.parse(decodeURIComponent(escape(atob(encoded))));
    } catch (e2) {
      // console.error("[share] unpack error:", e2);
      return null;
    }
  }
}

const SHORT_LINK_API = "https://f2qq5dfp52.execute-api.ap-south-1.amazonaws.com/default/create_short_link";

// Build the long URL with compressed base64url param (async)
async function buildLongUrl(payload) {
  const encoded = await packTrip(payload);
  if (!encoded) return null;
  return `${window.location.origin}/trip-share?d=${encoded}`;
}

// POST the long URL to RouteChef short link API; fallback to long URL if it fails
async function createShortLink(longUrl, idToken) {
  try {
    const resp = await fetch(SHORT_LINK_API, {
      method: "POST",
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ type: "trip", url: longUrl }),
    });
    if (!resp.ok) throw new Error(`Short link API ${resp.status}`);
    const data = await resp.json();
    // console.log("[share] short link response:", data);
    // Response shape: { message, data: { short_url, type, expiry_minutes } }
    const payload = data.data || data;  // handle both nested and flat
    const short = payload.short_url || payload.shortUrl || payload.url || payload.link || payload.id
      || data.short_url || data.shortUrl || data.url || data.link || data.id;
    // console.log("[share] extracted short URL:", short);
    if (short) return short.startsWith("http") ? short : `https://routechef.com/s/${short}`;
    throw new Error("no url in response");
  } catch (e) {
    // console.warn("[share] short link failed, using long URL:", e.message);
    return longUrl; // graceful fallback
  }
}



/* ── EARLY ACCESS SHEET ──────────────────────────────────────────────── */
const EarlyAccessSheet = ({ dest, onClose, idToken }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // "idle"|"submitting"|"done"|"error"
  const [errMsg, setErrMsg] = useState("");

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async () => {
    if (!isValid || status === "submitting") return;
    setStatus("submitting");
    setErrMsg("");
    try {
      let eaTok = idToken || null;
      try { if (auth.currentUser) eaTok = await auth.currentUser.getIdToken(); } catch (_) { }
      const resp = await fetch(`${ACTIVE_API}/early-access`, {
        method: "POST",
        headers: getAuthHeaders(eaTok),
        body: JSON.stringify({
          email,
          destination: dest || "",
          user_id: USER_ID || "",
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setStatus("done");
      } else {
        setErrMsg(data.error || "Something went wrong — please try again.");
        setStatus("error");
      }
    } catch (e) {
      setErrMsg("Network error — please try again.");
      setStatus("error");
    }
  };

  return (
    <Sheet title="" onClose={onClose}>
      {status === "done" ? (
        /* ── Success state ── */
        <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>🎉</div>
          <div style={{ fontWeight: 800, fontSize: 18, color: "#111", marginBottom: 8 }}>
            You're on the list!
          </div>
          <div style={{ fontSize: 13, color: "#666", lineHeight: 1.65, maxWidth: 260, margin: "0 auto" }}>
            We'll email you at <strong>{email}</strong> the moment one-click booking goes live.
            <br />
            We're building fast — expect to hear from us very soon.
          </div>
          <button onClick={onClose} style={{
            marginTop: 24, padding: "12px 36px", background: ORANGE,
            border: "none", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
            fontWeight: 700, fontSize: 14, color: "#fff",
            boxShadow: `0 4px 16px ${ORANGE}44`,
          }}>
            Back to itinerary
          </button>
        </div>
      ) : (
        /* ── Input state ── */
        <div>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg,${PURPLE} 0%,#8B4DC8 60%,${ORANGE} 100%)`,
            borderRadius: 14, padding: "18px 18px 16px", marginBottom: 20,
          }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>✈️🚂🏨</div>
            <div style={{ fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 4 }}>
              Book your entire {dest} trip in one tap
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", lineHeight: 1.6 }}>
              Flights · trains · hotels · all in one checkout.
              We're putting the finishing touches on it.
            </div>
          </div>

          {/* What you get */}
          <div style={{ marginBottom: 18, display: "flex", flexDirection: "column", gap: 8 }}>
            {[
              ["⚡", "Priority access before public launch"],
              ["🎁", "Flat ₹200 off your first booking"],
              ["📬", "Launch notification the moment it's live"],
            ].map(([icon, text]) => (
              <div key={text} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{icon}</span>
                <span style={{ fontSize: 12, color: "#444" }}>{text}</span>
              </div>
            ))}
          </div>

          {/* Email input */}
          <div style={{ marginBottom: 12 }}>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (status === "error") setStatus("idle"); }}
              onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}
              placeholder="your@email.com"
              style={{
                width: "100%", padding: "12px 14px",
                border: `2px solid ${status === "error" ? RED : email && !isValid ? "#F59E0B" : isValid ? GREEN : "#E0E0E0"}`,
                borderRadius: 10, fontSize: 14, fontFamily: "inherit",
                outline: "none", boxSizing: "border-box",
                transition: "border-color 0.15s", color: "#111",
                background: "#FAFAFA",
              }}
            />
            {email && !isValid && (
              <div style={{ fontSize: 11, color: AMBER, marginTop: 4, paddingLeft: 2 }}>
                Enter a valid email address
              </div>
            )}
            {errMsg && (
              <div style={{ fontSize: 11, color: RED, marginTop: 4, paddingLeft: 2 }}>{errMsg}</div>
            )}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={!isValid || status === "submitting"}
            style={{
              width: "100%", padding: "13px 0",
              background: isValid ? ORANGE : "#E0E0E0",
              border: "none", borderRadius: 11, cursor: isValid ? "pointer" : "default",
              fontFamily: "inherit", fontWeight: 700, fontSize: 14,
              color: isValid ? "#fff" : "#aaa",
              transition: "all 0.15s",
              boxShadow: isValid ? `0 4px 16px ${ORANGE}44` : "none",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {status === "submitting"
              ? <><span style={{ display: "inline-block", animation: "agentBounce 1s ease-in-out infinite" }}>⏳</span><span>Signing you up…</span></>
              : "Get early access →"}
          </button>

          <div style={{ textAlign: "center", fontSize: 10, color: "#bbb", marginTop: 10 }}>
            No spam. Unsubscribe anytime. routechef.com
          </div>
        </div>
      )}
    </Sheet>
  );
};

/* ── ITINERARY PANEL ─────────────────────────────────────────────────── */
/* ── Route timing helpers ─────────────────────────────────────────────── */
function routeArrival(route) {
  const nodes = route?.rawRoute?.nodes;
  if (!nodes?.length) return null;
  return nodes[nodes.length - 1]?.segment?.dest_arr_dt || null;
}
function routeDeparture(route) {
  return route?.rawRoute?.nodes?.[0]?.segment?.origin_dep_dt || null;
}
function computeAvailableDays(outboundRoute, returnRoute, fallbackNights) {
  const arrival = routeArrival(outboundRoute);
  const departure = routeDeparture(returnRoute);
  if (!arrival || !departure) return fallbackNights;
  const a = new Date(arrival), d = new Date(departure);
  const aDay = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(1, Math.round((dDay - aDay) / 86400000));
}
function addDays(dateStr, n) {
  // dateStr = ISO or dd-mm-yyyy; returns "Weekday D Mon" label
  if (!dateStr) return "";
  let d;
  if (dateStr.includes("T") || dateStr.includes("-")) {
    d = new Date(dateStr);
  }
  if (!d || isNaN(d)) return "";
  d = new Date(d.getTime() + n * 86400000);
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtSegDateTime(dtStr) {
  if (!dtStr) return "";
  const d = new Date(dtStr);
  const date = d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  return `${date} · ${time}`;
}

/**
 * Given a list of raw WS return routes and the number of nights,
 * find the route whose departure date best matches outboundArrival + nights.
 * Returns a formatted pickedRouteState object, or null (clear route), or undefined (no change needed).
 */
function findMatchingReturnRoute(returnRoutes, outboundRoute, newNights, currentReturnRoute) {
  if (!returnRoutes?.length) return undefined; // no alternatives available

  // Compute expected return date
  const arrivalDt = routeArrival(outboundRoute);
  if (!arrivalDt) return undefined;

  const arrDate = new Date(arrivalDt);
  const expectedDate = new Date(arrDate);
  expectedDate.setDate(expectedDate.getDate() + newNights);
  const expectedDay = expectedDate.toISOString().slice(0, 10); // YYYY-MM-DD

  // Check if current return route already matches
  const currentDep = currentReturnRoute?.rawRoute?.nodes?.[0]?.segment?.origin_dep_dt;
  if (currentDep && currentDep.slice(0, 10) === expectedDay) return undefined; // already correct

  // Find best matching route from the list
  let best = null;
  let bestDiff = Infinity;
  for (const r of returnRoutes) {
    const dep = r.nodes?.[0]?.segment?.origin_dep_dt;
    if (!dep) continue;
    const diff = Math.abs(new Date(dep).getTime() - expectedDate.getTime());
    // Only consider routes within 1 day of expected (86400000 ms)
    if (diff < bestDiff && diff <= 86400000) {
      bestDiff = diff;
      best = r;
    }
  }

  if (!best) {
    // No route found on the new date — clear return route so user re-picks
    // console.log(`[nights] no return route found for ${expectedDay} — clearing`);
    return null;
  }

  // Format into pickedRouteState shape
  const cls = classifyRoute(best);
  const fare = computeFare(best);
  const nodes = best.nodes || [];
  const firstSeg = nodes[0]?.segment;
  const lastSeg = nodes[nodes.length - 1]?.segment;
  const depStr = firstSeg?.origin_dep_dt ? new Date(firstSeg.origin_dep_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  const arrStr = lastSeg?.dest_arr_dt ? new Date(lastSeg.dest_arr_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "";
  const from = firstSeg?.origin_name || firstSeg?.origin_code_iata || "";
  const to = lastSeg?.dest_name || lastSeg?.dest_code_iata || "";
  const sub = `${from} → ${to}`;
  const bookLink = best.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;

  // console.log(`[nights] auto-switched return route to ${expectedDay}: ${cls.label} ${sub}`);
  return {
    id: best.route_id, icon: cls.icon, label: cls.label, badge: cls.badge,
    color: cls.color, sub, detail: `${depStr} → ${arrStr}`, price: fare || 0,
    bookingLink: bookLink, rawRoute: best
  };
}

const ItineraryPanel = ({
  tripState, nights, route, returnRoute, selectedHotel, hasStay,
  allHotels, onHotelChange, onRouteChange, onReturnRouteChange, onNightsChange,
  onRemoveStay, onAddStay, onRemoveRoute, onFetchHotels, onBookHotel, tripInfo, wsRoutes = [], returnRoutes = [], routingPrefs,
  onRefreshItinerary,
  isRefreshing,
  onNightsChangeOpenSheet,
  returnSearching,
  routeSheetNote,
  routeSheetInitTab,
  onRouteSheetClose,
  onEditRoute = null,   // v5: when provided, Edit route jumps to right-panel Routing tab
  onEditHotel = null,   // v5: when provided, Edit hotel jumps to right-panel Hotels tab
  onSeatAvail = null,   // v6: seat-class availability re-check (trains)
  onCompactChange = null, // v6: notify chrome when the itinerary is scrolled (mobile hides headers)
}) => {
  const [sheet, setSheet] = useState(null);
  // v6: compact the orange header once the user scrolls the itinerary —
  // frees vertical space (especially on mobile) while keeping the summary pinned
  const [compactHeader, setCompactHeader] = useState(false);
  useEffect(() => { if (onCompactChange) onCompactChange(compactHeader); }, [compactHeader, onCompactChange]);
  const [expandDay, setExpandDay] = useState(null);
  const [taxExpanded, setTaxExpanded] = useState(false);
  const [shareState, setShareState] = useState("idle"); // "idle"|"loading"|"copied"|"error"

  const handleShare = async () => {
    if (shareState === "loading") return;
    setShareState("loading");
    try {
      const payload = buildSharePayload({ tripState, tripInfo, route, returnRoute, selectedHotel, hasStay, nights, allHotels });
      const longUrl = await buildLongUrl(payload);
      if (!longUrl) { setShareState("error"); return; }
      // Fresh Firebase token (was previously receiving the click event by mistake)
      let shareTok = null;
      try { if (auth.currentUser) shareTok = await auth.currentUser.getIdToken(); } catch (_) { }
      const shortUrl = await createShortLink(longUrl, shareTok);
      // console.log("[share] final URL:", shortUrl);
      await navigator.clipboard.writeText(shortUrl);
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 3000);
    } catch (e) {
      // console.error("[share] error:", e);
      setShareState("error");
      setTimeout(() => setShareState("idle"), 2500);
    }
  };

  const dest = tripState?.primary_destination || tripInfo?.destination || "Destination";
  const source = tripInfo?.source || "Home";
  const itinerary = tripState?.itinerary || [];
  // Hotel pricing — use base_per_night for display, TotalFare for total
  // NOTE: TotalFare from TBO already includes hotel taxes — don't add TotalTax again
  const selRoom = selectedHotel?.selected_room;
  const basePriceNight = selRoom?.base_per_night
    || (selRoom?.DayRates?.[0]?.[0]?.BasePrice)
    || 0;
  const hotelTotalFare = selRoom ? (selRoom.total_fare || selRoom.TotalFare || 0) : 0;
  const hotelTotalTax = selRoom ? (selRoom.total_tax || selRoom.TotalTax || 0) : 0;
  const rcPlatformFee = 249;
  const rcGST = Math.round(rcPlatformFee * 0.18);
  const hotelPrice = (hasStay && selectedHotel && selectedHotel.hotel_code !== "ph")
    ? (hotelTotalFare || selectedHotel.price_per_night_inr || 0)
    : 0;
  const routePrice = route?.price || 0;
  const returnPrice = returnRoute?.price || 0;
  // Grand total: routes + hotel base (TotalFare) + hotel tax (TotalTax) + RC platform fee + GST
  const total = routePrice + returnPrice + hotelPrice + (hotelPrice > 0 ? hotelTotalTax + rcPlatformFee + rcGST : 0);

  // Compute Day 1 base date from outbound arrival (or tripInfo start date)
  const arrival = routeArrival(route);
  const day1Base = arrival || (tripInfo?.startDate
    ? (() => { const [d, m, y] = tripInfo.startDate.split("-"); return `${y}-${m}-${d}`; })()
    : null);
  const getDayDate = (dayIndex) => addDays(day1Base, dayIndex);

  if (!tripState?.itinerary_complete) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", height: "100%", gap: 16, padding: 40
      }}>
        <div style={{ fontSize: 48 }}>🗺️</div>
        <div style={{ fontWeight: 600, fontSize: 16, color: "#888" }}>Your itinerary will appear here</div>
        <div style={{ fontSize: 13, color: "#bbb", textAlign: "center", maxWidth: 260, lineHeight: 1.6 }}>
          Tell me your destination and dates, pick a route, then select places to visit.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Ticket header — warm sunrise gradient ── */}
      <div style={{
        background: "linear-gradient(135deg,#F4560A 0%,#E8450A 60%,#F7931E 100%)",
        padding: compactHeader ? "9px 20px 10px" : "16px 20px 18px",
        position: "relative", overflow: "hidden", flexShrink: 0,
        transition: "padding 0.25s ease",
      }}>
        {/* Decorative circles (ticket punch holes) */}
        <div style={{
          position: "absolute", left: -10, top: "50%", transform: "translateY(-50%)",
          width: 20, height: 20, borderRadius: "50%", background: "#F5F5F7"
        }} />
        <div style={{
          position: "absolute", right: -10, top: "50%", transform: "translateY(-50%)",
          width: 20, height: 20, borderRadius: "50%", background: "#F5F5F7"
        }} />
        {/* Subtle texture lines */}
        <div style={{
          position: "absolute", inset: 0, opacity: 0.06,
          backgroundImage: "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
          backgroundSize: "12px 12px"
        }} />


        <div style={{
          fontSize: 9, color: "rgba(255,255,255,0.7)", fontWeight: 700,
          letterSpacing: 2, textTransform: "uppercase", marginBottom: 8, position: "relative",
          display: compactHeader ? "none" : "block",
        }}>Your Trip Plan ✦</div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", position: "relative" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", fontWeight: 600, letterSpacing: 1, display: compactHeader ? "none" : "block" }}>FROM</div>
                <div style={{
                  fontWeight: 900, fontSize: compactHeader ? 17 : 26, color: "#fff", lineHeight: 1, transition: "font-size 0.25s ease",
                  textShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                  {source.substring(0, 3).toUpperCase()}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <div style={{ width: 40, height: 1.5, background: "rgba(255,255,255,0.4)", borderRadius: 99 }} />
                <span style={{ fontSize: 14 }}>✈️</span>
                <div style={{ width: 40, height: 1.5, background: "rgba(255,255,255,0.4)", borderRadius: 99 }} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", fontWeight: 600, letterSpacing: 1, display: compactHeader ? "none" : "block" }}>TO</div>
                <div style={{
                  fontWeight: 900, fontSize: compactHeader ? 17 : 26, color: "#fff", lineHeight: 1, transition: "font-size 0.25s ease",
                  textShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                  {dest.substring(0, 3).toUpperCase()}
                </div>
              </div>
            </div>
            <div style={{ display: compactHeader ? "none" : "flex", gap: 6, marginTop: 9 }}>
              {[`📅 ${nights} nights`, `📍 ${(tripState.selected_place_ids?.length ?? tripState.selected_places?.length) || 0} places`].map(t => (
                <span key={t} style={{
                  fontSize: 10, color: "rgba(255,255,255,0.9)",
                  background: "rgba(255,255,255,0.2)", borderRadius: 99,
                  padding: "3px 10px", fontWeight: 600, backdropFilter: "blur(4px)"
                }}>{t}</span>
              ))}
            </div>
          </div>
          <div style={{ textAlign: "right", marginRight: 14 }}>
            <div style={{
              fontSize: 9, color: "rgba(255,255,255,0.65)", fontWeight: 700,
              letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 2
            }}>Est. Total</div>
            <div style={{
              fontWeight: 900, fontSize: compactHeader ? 18 : 28, color: "#fff", lineHeight: 1, transition: "font-size 0.25s ease",
              textShadow: "0 2px 12px rgba(0,0,0,0.2)"
            }}>₹{fmtPrice(total)}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 3, display: compactHeader ? "none" : "block" }}>per person</div>
          </div>
        </div>
      </div>

      {/* Quick edit strip — route, hotel, and ± nights */}
      <div style={{ display: "flex", borderBottom: "1px solid #F0F0F0", background: "#FAFAFA", flexShrink: 0 }}>
        {[
          { key: "route", icon: route?.id === "flight" ? "✈️" : "🚂", label: route?.label || "Route", color: route?.color || GREEN },
          { key: "hotel", icon: "🏨", label: hasStay ? (selectedHotel?.hotel_name?.split(" ")[0] || "Stay") : "No stay", color: AMBER },
        ].map((item, i) => (
          <button key={item.key} onClick={() => {
            // Prefer jumping to the right-panel tab over the legacy popup sheet
            if (item.key === "route" && onEditRoute) return onEditRoute();
            if (item.key === "hotel" && onEditHotel) return onEditHotel();
            setSheet(item.key);
          }} style={{
            flex: 1, padding: "9px 4px", border: "none",
            borderRight: "1px solid #F0F0F0",
            background: "transparent", cursor: "pointer", fontFamily: "inherit",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
          }}>
            <span style={{ fontSize: 15 }}>{item.icon}</span>
            <span style={{
              fontSize: 10, fontWeight: 700, color: item.color,
              maxWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
            }}>{item.label}</span>
            <span style={{ fontSize: 9, color: "#bbb" }}>edit</span>
          </button>
        ))}
        {/* ± nights buttons */}
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: "6px 10px", gap: 2, borderLeft: "1px solid #F0F0F0"
        }}>
          <div style={{ fontSize: 9, color: "#bbb", fontWeight: 700, letterSpacing: 0.5 }}>NIGHTS</div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <button onClick={() => {
              const n = Math.max(1, nights - 1);
              onNightsChange(n);
              onNightsChangeOpenSheet && onNightsChangeOpenSheet(n);
            }} style={{
              width: 22, height: 22, borderRadius: "50%", border: "1px solid #E0E0E0",
              background: "#fff", cursor: "pointer", fontSize: 14, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#555"
            }}>−</button>
            <span style={{ fontWeight: 800, fontSize: 14, color: "#111", minWidth: 16, textAlign: "center" }}>{nights}</span>
            <button onClick={() => {
              const n = nights + 1;
              onNightsChange(n);
              onNightsChangeOpenSheet && onNightsChangeOpenSheet(n);
            }} style={{
              width: 22, height: 22, borderRadius: "50%", border: "1px solid #E0E0E0",
              background: "#fff", cursor: "pointer", fontSize: 14, lineHeight: 1,
              display: "flex", alignItems: "center", justifyContent: "center", color: "#555"
            }}>+</button>
          </div>
        </div>
      </div>

      {/* Timeline body */}
      <div
        onScroll={e => {
          const compact = e.currentTarget.scrollTop > 40;
          setCompactHeader(prev => prev === compact ? prev : compact);
        }}
        style={{ flex: 1, overflowY: "auto", padding: "12px 16px 16px", position: "relative" }}
      >

        {/* Refreshing overlay */}
        {isRefreshing && (
          <div style={{
            position: "absolute", inset: 0, zIndex: 10,
            background: "rgba(255,255,255,0.85)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: 14, backdropFilter: "blur(3px)", borderRadius: 8,
          }}>
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2].map(i => (
                <span key={i} style={{
                  width: 10, height: 10, borderRadius: "50%", background: ORANGE, display: "inline-block",
                  animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                }} />
              ))}
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#333" }}>Rebuilding your itinerary…</div>
            <div style={{ fontSize: 12, color: "#888" }}>Redistributing places across days</div>
          </div>
        )}
        {/* ── Outbound route — expanded with segments + booking links ── */}
        {route ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <TLNode icon={route.icon || "🚂"} color={route.color || GREEN} last={false} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Header row — label + date left, price right */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>{route.label}</span>
                    <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>{route.sub}</span>
                    {route.rawRoute?.nodes?.[0]?.segment?.origin_dep_dt && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, color: PURPLE, background: PURPLE + "15",
                        borderRadius: 99, padding: "2px 8px", fontWeight: 700
                      }}>
                        {fmtSegDateTime(route.rawRoute.nodes[0].segment.origin_dep_dt).split("·")[0].trim()}
                      </span>
                    )}
                  </div>
                  {route.price > 0 && (
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#111", flexShrink: 0 }}>₹{fmtPrice(route.price)}</span>
                  )}
                </div>
                {/* Action buttons row */}
                <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
                  <button onClick={() => onEditRoute ? onEditRoute() : setSheet("route")} style={{ fontSize: 10, color: PURPLE, background: "#F0EBF9", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Edit</button>
                  <button onClick={onRemoveRoute} style={{ fontSize: 10, color: "#E8334A", background: "#FEF0F2", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Remove</button>
                </div>
                {/* Segments */}
                {(route.rawRoute?.nodes || []).map((node, ni) => {
                  const seg = node.segment;
                  const isFlight = !!seg.carrier;
                  const depFull = seg.origin_dep_dt ? fmtSegDateTime(seg.origin_dep_dt) : "";
                  const arrFull = seg.dest_arr_dt ? fmtSegDateTime(seg.dest_arr_dt) : "";
                  const src = seg.origin_name || seg.origin_code_iata || "";
                  const dst = seg.dest_name || seg.dest_code_iata || "";
                  return (
                    <div key={ni} style={{
                      background: isFlight ? "#F5F0FF" : "#F0F9F4",
                      borderRadius: 9, padding: "7px 10px", marginBottom: 5,
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ fontSize: 13, flexShrink: 0 }}>{isFlight ? "✈️" : "🚂"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#111" }}>
                          {isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} #${seg.train_num}`}
                        </div>
                        <div style={{ fontSize: 10, color: "#666" }}>
                          {src} → {dst}
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                          {depFull} – {arrFull.split("·")[1]?.trim() || arrFull}
                          {seg.seat_class_ui && <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>{seg.seat_class_ui}</span>}
                        </div>
                      </div>
                      {seg.booking_link && (
                        <a href={seg.booking_link} target="_blank" rel="noopener noreferrer"
                          style={{
                            fontSize: 10, fontWeight: 700, color: "#fff",
                            background: isFlight ? PURPLE : GREEN,
                            borderRadius: 99, padding: "4px 10px",
                            textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0
                          }}>
                          {isFlight ? "✈️ Book" : "🎫 Book"}
                        </a>
                      )}
                    </div>
                  );
                })}
                {/* Fallback booking link if no per-segment links */}
                {!(route.rawRoute?.nodes || []).some(n => n.segment.booking_link) && route.bookingLink && (
                  <a href={route.bookingLink} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
                      fontWeight: 700, color: "#fff", background: route.color || PURPLE,
                      borderRadius: 99, padding: "5px 12px", textDecoration: "none", marginTop: 4
                    }}>
                    🎫 Book tickets
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <TLNode icon="🚂" color="#E0E0E0" last={false} />
            <div style={{ flex: 1, paddingTop: 4 }}>
              <div style={{ fontSize: 12, color: "#aaa" }}>No route selected</div>
              <button onClick={() => onEditRoute ? onEditRoute() : setSheet("route")} style={{
                fontSize: 11, color: PURPLE,
                background: "#F0EBF9", border: "none", borderRadius: 99, padding: "3px 9px",
                cursor: "pointer", fontFamily: "inherit", fontWeight: 600, marginTop: 4
              }}>Add route</button>
            </div>
          </div>
        )}

        {hasStay && selectedHotel ? (
          <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "flex-start" }}>
            <TLNode icon="🏨" color={AMBER} last={false} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "#111", lineHeight: 1.3, flex: 1 }}>
                  {selectedHotel.hotel_name}
                </div>
                {(hotelTotalFare > 0 || selectedHotel.price_per_night_inr > 0) && (
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: AMBER }}>
                      ₹{fmtPrice(hotelTotalFare || selectedHotel.price_per_night_inr || 0)}
                    </div>
                    <div style={{ fontSize: 9, color: "#bbb" }}>room charge</div>
                  </div>
                )}
              </div>
              {selectedHotel.selected_room?.name && (
                <div style={{ fontSize: 11, color: "#666", marginTop: 1, lineHeight: 1.3 }}>
                  {selectedHotel.selected_room.name.split(",")[0]}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                {selectedHotel.selected_room?.meal_type && selectedHotel.selected_room.meal_type !== "Room_Only" ? (
                  <span style={{ fontSize: 10, color: "#22C55E", fontWeight: 700, background: "#F0FDF4", borderRadius: 99, padding: "2px 7px" }}>
                    🍳 {selectedHotel.selected_room.meal_type === "BreakFast" || selectedHotel.selected_room.meal_type === "Breakfast_For_2"
                      ? "Breakfast" : selectedHotel.selected_room.meal_type === "Half_Board"
                        ? "Half board" : "Meals incl."}
                  </span>
                ) : selectedHotel.selected_room?.meal_type === "Room_Only" ? (
                  <span style={{ fontSize: 10, color: "#aaa", background: "#F5F5F7", borderRadius: 99, padding: "2px 7px" }}>🛏 Room only</span>
                ) : null}
                {(selectedHotel.location_label || selectedHotel.hotel_area) && (
                  <span style={{ fontSize: 10, color: "#aaa" }}>📍 {selectedHotel.location_label || selectedHotel.hotel_area}</span>
                )}
              </div>
              <div style={{ display: "flex", gap: 5, marginTop: 8, flexWrap: "wrap" }}>
                <button onClick={() => { if (onEditHotel) { onEditHotel(); } else { setSheet("hotel"); onFetchHotels && onFetchHotels(); } }} style={{ fontSize: 10, color: PURPLE, background: "#F0EBF9", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Edit</button>
                <button onClick={onRemoveStay} style={{ fontSize: 10, color: "#E8334A", background: "#FEF0F2", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Remove</button>
                {selectedHotel.hotel_code && (
                  <button onClick={onBookHotel} style={{ fontSize: 10, color: "#fff", background: ORANGE, border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>🏨 Book</button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <TLNode icon="🏨" color="#E0E0E0" last={false} />
            <div style={{ flex: 1, paddingTop: 4 }}>
              <div style={{ fontSize: 12, color: "#aaa" }}>No accommodation</div>
              <button onClick={onAddStay} style={{
                fontSize: 11, color: AMBER,
                background: "#FFFBEB", border: "none", borderRadius: 99, padding: "3px 9px",
                cursor: "pointer", fontFamily: "inherit", fontWeight: 600, marginTop: 4
              }}>Add hotel</button>
            </div>
          </div>
        )}

        {itinerary.map((day, di) => (
          <DayRow key={di} day={day} last={false}
            date={getDayDate(di)}
            expanded={expandDay === di}
            onToggle={() => setExpandDay(expandDay === di ? null : di)} />
        ))}

        {/* ── Return route — expanded with segments + booking links ── */}
        {returnRoute ? (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <TLNode icon={returnRoute.icon || "🚂"} color={returnRoute.color || GREEN} last />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>{returnRoute.label}</span>
                    <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>{returnRoute.sub || `${dest.substring(0, 3).toUpperCase()} → ${source.substring(0, 3).toUpperCase()}`}</span>
                    {returnRoute.rawRoute?.nodes?.[0]?.segment?.origin_dep_dt && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, color: ORANGE, background: ORANGE + "15",
                        borderRadius: 99, padding: "2px 8px", fontWeight: 700
                      }}>
                        {fmtSegDateTime(returnRoute.rawRoute.nodes[0].segment.origin_dep_dt).split("·")[0].trim()}
                      </span>
                    )}
                  </div>
                  {returnRoute.price > 0 && (
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#111", flexShrink: 0 }}>₹{fmtPrice(returnRoute.price)}</span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
                  <button onClick={() => onEditRoute ? onEditRoute() : setSheet("route")} style={{ fontSize: 10, color: PURPLE, background: "#F0EBF9", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Edit</button>
                  <button onClick={() => onReturnRouteChange && onReturnRouteChange(null)} style={{ fontSize: 10, color: "#E8334A", background: "#FEF0F2", border: "none", borderRadius: 99, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>Remove</button>
                </div>
                {(returnRoute.rawRoute?.nodes || []).map((node, ni) => {
                  const seg = node.segment;
                  const isFlight = !!seg.carrier;
                  const depFull = seg.origin_dep_dt ? fmtSegDateTime(seg.origin_dep_dt) : "";
                  const arrFull = seg.dest_arr_dt ? fmtSegDateTime(seg.dest_arr_dt) : "";
                  const src = seg.origin_name || seg.origin_code_iata || "";
                  const dst = seg.dest_name || seg.dest_code_iata || "";
                  return (
                    <div key={ni} style={{
                      background: isFlight ? "#F5F0FF" : "#F0F9F4",
                      borderRadius: 9, padding: "7px 10px", marginBottom: 5,
                      display: "flex", alignItems: "center", gap: 8,
                    }}>
                      <span style={{ fontSize: 13, flexShrink: 0 }}>{isFlight ? "✈️" : "🚂"}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "#111" }}>
                          {isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} #${seg.train_num}`}
                        </div>
                        <div style={{ fontSize: 10, color: "#666" }}>
                          {src} → {dst}
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                          {depFull} – {arrFull.split("·")[1]?.trim() || arrFull}
                          {seg.seat_class_ui && <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>{seg.seat_class_ui}</span>}
                        </div>
                      </div>
                      {seg.booking_link && (
                        <a href={seg.booking_link} target="_blank" rel="noopener noreferrer"
                          style={{
                            fontSize: 10, fontWeight: 700, color: "#fff",
                            background: isFlight ? PURPLE : GREEN,
                            borderRadius: 99, padding: "4px 10px",
                            textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0
                          }}>
                          {isFlight ? "✈️ Book" : "🎫 Book"}
                        </a>
                      )}
                    </div>
                  );
                })}
                {!(returnRoute.rawRoute?.nodes || []).some(n => n.segment.booking_link) && returnRoute.bookingLink && (
                  <a href={returnRoute.bookingLink} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
                      fontWeight: 700, color: "#fff", background: returnRoute.color || PURPLE,
                      borderRadius: 99, padding: "5px 12px", textDecoration: "none", marginTop: 4
                    }}>
                    🎫 Book return tickets
                  </a>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
            <TLNode icon="🚂" color="#E0E0E0" last />
            <div style={{ flex: 1, paddingTop: 4 }}>
              <div style={{ fontSize: 12, color: "#aaa" }}>Return journey</div>
              <button onClick={() => onEditRoute ? onEditRoute() : setSheet("route")} style={{
                fontSize: 11, color: PURPLE,
                background: "#F0EBF9", border: "none", borderRadius: 99, padding: "3px 9px",
                cursor: "pointer", fontFamily: "inherit", fontWeight: 600, marginTop: 4
              }}>
                Pick return route
              </button>
            </div>
          </div>
        )}

        {/* ── Cost summary — clean white card, warm accent ── */}
        <div style={{
          margin: "14px 0 0",
          background: "#fff",
          borderRadius: 18,
          border: "1px solid #F0EDE8",
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(244,86,10,0.08)",
        }}>
          {/* Warm accent top bar */}
          <div style={{
            height: 4,
            background: "linear-gradient(90deg,#F4560A,#E8450A,#F7931E)",
          }} />

          {/* Line items */}
          <div style={{ padding: "14px 16px 12px", display: "flex", flexDirection: "column", gap: 9 }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#999",
              letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 2
            }}>Cost Breakdown</div>
            {route && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>{route.icon || "🚂"}</span>
                  <span style={{ fontSize: 12, color: "#444", fontWeight: 500 }}>
                    {route.label}
                    <span style={{ fontSize: 11, marginLeft: 5, color: "#bbb" }}>outbound</span>
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
                  {route.price > 0 ? `₹${fmtPrice(route.price)}` : "—"}
                </span>
              </div>
            )}
            {returnRoute && returnRoute.price > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>{returnRoute.icon || "🔄"}</span>
                  <span style={{ fontSize: 12, color: "#444", fontWeight: 500 }}>
                    {returnRoute.label}
                    <span style={{ fontSize: 11, marginLeft: 5, color: "#bbb" }}>return</span>
                  </span>
                </div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
                  ₹{fmtPrice(returnRoute.price)}
                </span>
              </div>
            )}
            {hasStay && selectedHotel && (() => {
              const [taxOpen, setTaxOpen] = [taxExpanded, setTaxExpanded];
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 15 }}>🏨</span>
                      <span style={{ fontSize: 12, color: "#444", fontWeight: 500 }}>
                        {selectedHotel.hotel_name?.split(" ").slice(0, 3).join(" ")}
                        <span style={{ fontSize: 11, marginLeft: 5, color: "#bbb" }}>{nights}n</span>
                      </span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
                      {hotelTotalFare > 0 ? `₹${fmtPrice(hotelTotalFare)}` : "TBD"}
                    </span>
                  </div>
                  {/* Taxes & fees row — expandable */}
                  {hotelTotalFare > 0 && (
                    <div>
                      <div onClick={() => setTaxExpanded(!taxExpanded)}
                        style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer",
                          padding: "4px 0", borderTop: "1px dashed #F0F0F0"
                        }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13 }}>🧾</span>
                          <span style={{ fontSize: 11, color: "#888" }}>Taxes & fees</span>
                          <span style={{ fontSize: 10, color: "#bbb" }}>{taxExpanded ? "▲" : "▼"}</span>
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#666" }}>
                          ₹{fmtPrice(hotelTotalTax + rcPlatformFee + rcGST)}
                        </span>
                      </div>
                      {taxExpanded && (
                        <div style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 3, marginBottom: 4 }}>
                          {hotelTotalTax > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#666" }}>
                              <span>Hotel tax</span>
                              <span>₹{fmtPrice(hotelTotalTax)}</span>
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#666" }}>
                            <span>Platform fee</span>
                            <span>₹{rcPlatformFee}</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#666" }}>
                            <span>GST on platform fee (18%)</span>
                            <span>₹{rcGST}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Total row — warm background */}
          <div style={{
            margin: "0 12px 12px",
            background: "linear-gradient(135deg,#FFF4EE 0%,#FFF8F0 100%)",
            borderRadius: 12, padding: "12px 14px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            border: "1px solid #FFE0CC",
          }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: "#888",
                letterSpacing: 1, textTransform: "uppercase", marginBottom: 2
              }}>Estimated Total</div>
              <div style={{ fontSize: 10, color: "#bbb" }}>per person · excl. activities</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontWeight: 900, fontSize: 30,
                background: "linear-gradient(135deg,#F4560A,#F7931E)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                lineHeight: 1
              }}>
                ₹{fmtPrice(total)}
              </div>
            </div>
          </div>
        </div>

        {/* ── Share + PDF + Book CTAs ── */}
        <div style={{ margin: "12px 0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Share + PDF row */}
          <div style={{ display: "flex", gap: 8 }}>
            {/* Share */}
            <button onClick={handleShare} disabled={shareState === "loading"} style={{
              flex: 1, padding: "11px 0",
              background: shareState === "copied" ? "#E8F7F2"
                : shareState === "error" ? "#FEF0F2"
                  : "#F5F5F7",
              border: `1.5px solid ${shareState === "copied" ? GREEN : shareState === "error" ? RED : "#E8E8E8"}`,
              borderRadius: 12,
              cursor: shareState === "loading" ? "default" : "pointer",
              fontFamily: "inherit", fontWeight: 600, fontSize: 13,
              color: shareState === "copied" ? GREEN : shareState === "error" ? RED : "#666",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.2s",
            }}>
              {shareState === "loading" && <><span style={{ animation: "agentBounce 1s ease-in-out infinite", display: "inline-block" }}>⏳</span><span>Creating link…</span></>}
              {shareState === "copied" && <><span>✓</span><span>Copied!</span></>}
              {shareState === "error" && <><span>⚠️</span><span>Try again</span></>}
              {shareState === "idle" && <><span style={{ fontSize: 15 }}>🔗</span><span>Share</span></>}
            </button>

            {/* PDF download */}
            <button onClick={() => {
              const days = itinerary;
              const hotel = hasStay && selectedHotel && selectedHotel.hotel_code !== "ph" ? selectedHotel : null;

              // ── Helper: segment row with booking link ──────────────────────────
              const segHtml = (seg, stopRow = "") => {
                const isFlight = !!seg.carrier;
                const dep = seg.origin_dep_dt ? new Date(seg.origin_dep_dt) : null;
                const arr = seg.dest_arr_dt ? new Date(seg.dest_arr_dt) : null;
                const fmtT = d => d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—";
                const fmtD = d => d ? d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "";
                const fare = seg.seat_avail_ui?.fare;
                const id = isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} #${seg.train_num}`;
                const from = seg.origin_name || seg.origin_code_iata || "";
                const to = seg.dest_name || seg.dest_code_iata || "";
                const dur = seg.duration_mins ? `${Math.floor(seg.duration_mins / 60)}h${seg.duration_mins % 60 > 0 ? " " + seg.duration_mins % 60 + "m" : ""}` : "";
                const bookBtn = seg.booking_link
                  ? `<a href="${seg.booking_link}" style="display:inline-block;font-size:11px;font-weight:700;color:#fff;background:${isFlight ? "#6B3FA0" : "#00A86B"};border-radius:99px;padding:4px 12px;text-decoration:none;white-space:nowrap;flex-shrink:0">${isFlight ? "✈️ Book flight" : "🎫 Book train"}</a>`
                  : "";
                return `
                <div style="background:${isFlight ? "#F5F0FF" : "#F0F9F4"};border-radius:9px;padding:10px 12px;margin-bottom:6px;display:flex;align-items:center;gap:10px">
                  <span style="font-size:16px;flex-shrink:0">${isFlight ? "✈️" : "🚂"}</span>
                  <div style="flex:1;min-width:0">
                    <div style="font-weight:700;font-size:13px;color:#111">${id}</div>
                    <div style="font-size:11px;color:#555;margin-top:2px">${from} → ${to}</div>
                    <div style="font-size:10px;color:#888;margin-top:1px">${fmtD(dep)} · ${fmtT(dep)} – ${fmtT(arr)}${seg.seat_class_ui ? " · <b>" + seg.seat_class_ui + "</b>" : ""}${dur ? " · " + dur : ""}</div>
                    ${seg.origin_platform ? `<div style="font-size:10px;color:#bbb;margin-top:1px">Platform ${seg.origin_platform}${seg.dest_platform ? " → " + seg.dest_platform : ""}</div>` : ""}
                  </div>
                  <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0">
                    ${fare ? `<div style="font-weight:800;font-size:13px;color:#111">₹${fmtPrice(fare)}</div>` : ""}
                    ${bookBtn}
                  </div>
                </div>${stopRow}`;
              };

              const routeSection = (rawRoute, label, tag) => {
                if (!rawRoute?.nodes) return "";
                const nodes = rawRoute.nodes || [];
                const stops = rawRoute.stops_data || [];
                const segs = nodes.map((n, ni) => {
                  const stop = stops.find(s => s.seq === ni + 1);
                  const stopRow = stop && ni < nodes.length - 1
                    ? `<div style="margin:2px 0 5px 28px;font-size:10px;color:#92400E;background:#FFF8F0;border-radius:6px;padding:4px 10px;display:inline-block">🔄 ${stop.stop_title}${stop.commute_comment ? " · " + stop.commute_comment : ""}</div>`
                    : "";
                  return segHtml(n.segment, stopRow);
                }).join("");
                return `
                <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #E8E8E8;overflow:hidden;page-break-inside:avoid">
                  <div style="padding:12px 16px;background:linear-gradient(135deg,#6B3FA0,#9B6DD0);display:flex;align-items:center;gap:10px">
                    <div style="font-weight:800;font-size:14px;color:#fff">${label}</div>
                    <div style="font-size:11px;color:rgba(255,255,255,0.7)">${tag}</div>
                  </div>
                  <div style="padding:12px 14px">${segs}</div>
                </div>`;
              };

              // ── Helper: place visit row ─────────────────────────────────────────
              const placeRow = v => {
                const place = v.place || {};
                return `
                <div style="display:flex;gap:10px;margin-bottom:10px;align-items:flex-start">
                  <div style="width:8px;height:8px;border-radius:50%;background:#F4560A;margin-top:5px;flex-shrink:0"></div>
                  <div style="flex:1;background:#FAFAF8;border-radius:8px;padding:10px 12px;border:1px solid #F0EDE8">
                    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px">
                      <div style="font-weight:700;font-size:13px;color:#111">${place.name || v.place_name || ""}</div>
                      ${(v.start_time || v.end_time) ? `<div style="font-size:10px;color:#fff;background:#F4560A;border-radius:99px;padding:2px 8px;white-space:nowrap;flex-shrink:0">${v.start_time || ""}${v.end_time ? "–" + v.end_time : ""}</div>` : ""}
                    </div>
                    ${place.type ? `<div style="font-size:10px;color:#E8450A;margin-top:3px;font-weight:600">${place.type}</div>` : ""}
                    ${v.travel_from_prev && v.travel_from_prev !== "Start of day" ? `<div style="font-size:10px;color:#bbb;margin-top:3px">🚗 ${v.travel_from_prev}</div>` : ""}
                    ${place.insider_tip ? `<div style="font-size:10px;color:#555;margin-top:6px;background:#FFF4EE;border-radius:6px;padding:5px 8px;border-left:3px solid #F4560A">💡 ${place.insider_tip.slice(0, 120)}${place.insider_tip.length > 120 ? "…" : ""}</div>` : ""}
                  </div>
                </div>`;
              };

              // ── Hotel section ──────────────────────────────────────────────────
              const hRoom = hotel?.selected_room;
              const hTotalFare = hRoom?.total_fare || hRoom?.TotalFare || 0;
              const hTotalTax = hRoom?.total_tax || hRoom?.TotalTax || 0;
              const hBaseNight = hRoom?.base_per_night || 0;
              const hPlatFee = 249;
              const hGST = Math.round(hPlatFee * 0.18);
              const hGrandTotal = hTotalFare + hTotalTax + hPlatFee + hGST;
              const roomName = hRoom?.name?.split(",")[0] || "";
              const hotelSection = hotel ? `
              <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #E8E8E8;overflow:hidden;page-break-inside:avoid">
                <div style="padding:12px 16px;background:linear-gradient(135deg,#F59E0B,#FCD34D);display:flex;align-items:center;gap:10px">
                  <span style="font-size:18px">🏨</span>
                  <div style="font-weight:800;font-size:14px;color:#fff">Accommodation · ${nights} nights</div>
                </div>
                <div style="padding:12px 16px">
                  <div style="font-weight:700;font-size:15px;color:#111">${hotel.hotel_name}</div>
                  ${roomName ? `<div style="font-size:12px;color:#555;margin-top:2px">${roomName}</div>` : ""}
                  ${hotel.hotel_area || hotel.city_name ? `<div style="font-size:11px;color:#888;margin-top:2px">📍 ${hotel.hotel_area || hotel.city_name}${hotel.hotel_rating ? ' · ' + '★'.repeat(Math.min(hotel.hotel_rating || 3, 5)) : ''}</div>` : ""}
                  ${hBaseNight > 0 ? `<div style="font-size:12px;color:#F59E0B;font-weight:700;margin-top:6px">₹${fmtPrice(hBaseNight)}/night</div>` : ""}
                  ${hTotalFare > 0 ? `
                  <div style="margin-top:10px;background:#F9F9F9;border-radius:8px;padding:10px 12px;font-size:11px">
                    <div style="display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #EBEBEB"><span style="color:#666">Room charge</span><span style="font-weight:600">₹${fmtPrice(hTotalFare)}</span></div>
                    <div style="display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px solid #EBEBEB"><span style="color:#666">Taxes &amp; fees</span><span style="font-weight:600">₹${fmtPrice(hTotalTax + hPlatFee + hGST)}</span></div>
                    <div style="display:flex;justify-content:space-between;padding-top:6px"><span style="font-weight:700;color:#111">Total</span><span style="font-weight:800;color:#F4560A;font-size:13px">₹${fmtPrice(hGrandTotal)}</span></div>
                  </div>` : ""}
                  ${hotel.hotel_code ? `<div style="margin-top:10px"><a href="https://routechef.com/trip-planner" style="display:inline-block;font-size:12px;font-weight:700;color:#fff;background:#F4560A;border-radius:99px;padding:6px 16px;text-decoration:none">🏨 Book Hotel</a></div>` : ""}
                </div>
              </div>` : "";

              // ── Day sections ───────────────────────────────────────────────────
              const daySections = days.map((day, di) => {
                const visits = day.place_visits || [];
                const visitRows = visits.map(placeRow).join("");
                // Insert hotel after Day 1 header (check-in is typically first night)
                const hotelInsert = di === 0 && hotel ? hotelSection : "";
                return `
                <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #F0EDE8;overflow:hidden;page-break-inside:avoid">
                  <div style="padding:12px 16px;background:linear-gradient(135deg,#FFF4EE,#FFF8F0);border-bottom:1px solid #F0EDE8;display:flex;align-items:center;gap:12px">
                    <div style="width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#F4560A,#F7931E);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;color:#fff;flex-shrink:0">${di + 1}</div>
                    <div>
                      <div style="font-weight:700;font-size:13px;color:#111">Day ${day.day_number}${day.area_summary ? " · " + day.area_summary : ""}</div>
                      <div style="font-size:11px;color:#aaa">${visits.length} place${visits.length !== 1 ? "s" : ""}</div>
                    </div>
                  </div>
                  ${visits.length > 0 ? `<div style="padding:12px 14px">${visitRows}</div>` : `<div style="padding:12px 16px;color:#bbb;font-size:12px">Free day — explore at your own pace</div>`}
                  ${day.day_notes ? `<div style="margin:0 14px 12px;font-size:11px;color:#888;background:#F5F5F7;border-radius:7px;padding:6px 10px">ℹ️ ${day.day_notes}</div>` : ""}
                </div>${hotelInsert}`;
              }).join("");

              // ── Cost breakdown ─────────────────────────────────────────────────
              const costHtml = total > 0 ? `
              <div style="background:#fff;border-radius:12px;border:1px solid #F0EDE8;overflow:hidden;margin-bottom:20px">
                <div style="height:4px;background:linear-gradient(90deg,#F4560A,#E8450A,#F7931E)"></div>
                <div style="padding:14px 16px">
                  <div style="font-size:10px;font-weight:700;color:#999;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:10px">Cost Breakdown</div>
                  ${route ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">${route.icon || "🚂"} ${route.label} <span style="font-size:10px;color:#aaa">outbound</span></span><span style="font-size:13px;font-weight:700">${route.price > 0 ? "₹" + fmtPrice(route.price) : "—"}</span></div>` : ""}
                  ${returnRoute && returnRoute.price > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">${returnRoute.icon || "🔄"} ${returnRoute.label} <span style="font-size:10px;color:#aaa">return</span></span><span style="font-size:13px;font-weight:700">₹${fmtPrice(returnRoute.price)}</span></div>` : ""}
                  ${hotel && hTotalFare > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">🏨 ${hotel.hotel_name?.split(" ").slice(0, 3).join(" ") || ""} <span style="font-size:10px;color:#aaa">${nights}n</span></span><span style="font-size:13px;font-weight:700">₹${fmtPrice(hTotalFare)}</span></div>` : ""}
                  ${hotel && (hTotalTax + hPlatFee + hGST) > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#888">🧾 Taxes &amp; fees</span><span style="font-size:12px;color:#666">₹${fmtPrice(hTotalTax + hPlatFee + hGST)}</span></div>` : ""}
                  <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px;padding:10px 12px;background:#FFF4EE;border-radius:8px;border:1px solid #FFE0CC">
                    <div>
                      <div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:1px">Estimated Total</div>
                      <div style="font-size:10px;color:#bbb">per person · excl. activities</div>
                    </div>
                    <div style="font-size:26px;font-weight:900;color:#F4560A">₹${fmtPrice(total)}</div>
                  </div>
                </div>
              </div>` : "";

              // ── Assemble: outbound → hotel(day1) → days → return → cost ────────
              const html = `<!DOCTYPE html><html><head>
              <meta charset="utf-8"/>
              <title>${dest} Itinerary — RouteChef</title>
              <style>
                *{box-sizing:border-box;margin:0;padding:0}
                body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#111;padding:28px 32px}
                a{color:inherit}
                @media print{body{padding:16px}@page{margin:12mm}.no-print{display:none}}
              </style>
            </head><body>
              <!-- Header -->
              <div style="margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #F4560A">
                <div style="font-size:22px;font-weight:900;color:#111;margin-bottom:4px">${source.toUpperCase()} → ${dest.toUpperCase()}</div>
                <div style="font-size:13px;color:#888">${nights} nights · ${days.length} days · ${tripState?.selected_place_ids?.length || days.reduce((a, d) => a + (d.place_visits?.length || 0), 0)} places${tripInfo?.startDate ? " · From " + tripInfo.startDate : ""}</div>
                <div style="font-size:11px;color:#bbb;margin-top:4px">Generated by RouteChef AI · routechef.com</div>
              </div>

              <!-- Outbound journey -->
              ${route?.rawRoute ? routeSection(route.rawRoute, `${route.icon || "🚂"} ${route.label}`, "Outbound journey · " + route.sub) : ""}

              <!-- Days + hotel after day 1 -->
              ${daySections}

              <!-- Return journey -->
              ${returnRoute?.rawRoute ? routeSection(returnRoute.rawRoute, `${returnRoute.icon || "🔄"} ${returnRoute.label}`, "Return journey · " + (returnRoute.sub || "")) : ""}

              <!-- Cost breakdown -->
              ${costHtml}

              <div style="font-size:10px;color:#ccc;text-align:center;margin-top:16px">
                AI-powered itinerary · Not financial or travel advice · routechef.com
              </div>
            </body></html>`;
              const win = window.open("", "_blank");
              win.document.write(html);
              win.document.close();
              win.onload = () => { win.focus(); win.print(); };
              setTimeout(() => { try { win.focus(); win.print(); } catch (e) { } }, 800);
            }} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "9px 14px",
              background: "linear-gradient(135deg,#F4560A,#F7931E)",
              border: "none", borderRadius: 99, cursor: "pointer",
              fontFamily: "inherit", fontWeight: 700, fontSize: 12, color: "#fff",
              boxShadow: "0 3px 12px rgba(244,86,10,0.35)", flexShrink: 0,
            }}>
              ⬇️ PDF
            </button>
          </div>{/* end share+PDF row */}

          {/* Book / Early access — the primary CTA */}
          <button onClick={() => setSheet("earlyaccess")} style={{
            width: "100%", padding: "14px 20px",
            background: "linear-gradient(135deg,#F4560A 0%,#F7931E 100%)",
            border: "none", borderRadius: 12, cursor: "pointer", fontFamily: "inherit",
            boxShadow: "0 6px 24px rgba(244,86,10,0.40)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            transition: "transform 0.12s,box-shadow 0.12s",
          }}
            onMouseDown={e => { e.currentTarget.style.transform = "scale(0.98)"; e.currentTarget.style.boxShadow = "0 3px 12px rgba(244,86,10,0.35)"; }}
            onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(244,86,10,0.40)"; }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: "#fff" }}>
                Book this trip
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                One-click · launching soon · get early access
              </div>
            </div>
            <div style={{
              background: "rgba(255,255,255,0.22)", borderRadius: 99,
              padding: "6px 14px", fontSize: 12, fontWeight: 800, color: "#fff", flexShrink: 0,
            }}>Get access →</div>
          </button>
        </div>
      </div>

      {sheet === "earlyaccess" && (
        <EarlyAccessSheet dest={dest} onClose={() => setSheet(null)} />
      )}
      {sheet === "hotel" && (
        <HotelSheet hotels={allHotels} selectedHotel={selectedHotel}
          onSelect={h => { onHotelChange(h); setSheet(null); }} onClose={() => setSheet(null)} />
      )}
      {sheet === "route" && (
        <RouteSheet
          onSeatAvail={onSeatAvail}
          routes={wsRoutes}
          returnRoutes={returnRoutes}
          currentOutbound={route}
          currentReturn={returnRoute}
          initialTab={routeSheetInitTab || "outbound"}
          returnSearching={returnSearching}
          sheetNote={routeSheetNote}
          onSelectOutbound={r => {
            const cls = classifyRoute(r);
            const fare = computeFare(r);
            const dur = computeDuration(r);
            const nodes = r.nodes || [];
            const first = nodes[0]?.segment;
            const last = nodes[nodes.length - 1]?.segment;
            const bookLink = r.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;
            onRouteChange({
              id: r.route_id, icon: cls.icon, label: cls.label, badge: cls.badge,
              color: cls.color, price: fare || 0, bookingLink: bookLink,
              sub: `${first?.origin_name || first?.origin_code_iata || ""} → ${last?.dest_name || last?.dest_code_iata || ""}`,
              detail: `${fmtDuration(dur)} · ${r.num_stops || 0} stop${(r.num_stops || 0) !== 1 ? "s" : ""}`,
              rawRoute: r,
            });
            setSheet(null);
          }}
          onSelectReturn={r => {
            if (!onReturnRouteChange) return;
            const cls = classifyRoute(r);
            const fare = computeFare(r);
            const dur = computeDuration(r);
            const nodes = r.nodes || [];
            const first = nodes[0]?.segment;
            const last = nodes[nodes.length - 1]?.segment;
            const bookLink = r.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;
            const sub = `${first?.origin_name || first?.origin_code_iata || ""} → ${last?.dest_name || last?.dest_code_iata || ""}`;
            onReturnRouteChange({
              id: r.route_id, icon: cls.icon, label: cls.label, badge: cls.badge,
              color: cls.color, sub, price: fare || 0, bookingLink: bookLink,
              detail: `${fmtDuration(dur)} · ${r.num_stops || 0} stop${(r.num_stops || 0) !== 1 ? "s" : ""}`,
              rawRoute: r,
            });
            setSheet(null);
            // onReturnRouteChange → handleRouteChange → refreshItinerary fires automatically
          }}
          onClose={() => { setSheet(null); onRouteSheetClose && onRouteSheetClose(); }}
        />
      )}
      {/* DaysSheet disabled — nights not editable */}
    </div>
  );
};


/* ── TypingText — character-by-character typing animation ── */
const TypingText = ({ text, speed = 18, onDone }) => {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!text) { setDisplayed(""); setDone(true); onDone?.(); return; }
    setDisplayed("");
    setDone(false);
    let i = 0;
    const id = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(id);
        setDone(true);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(id);
  }, [text]);   // eslint-disable-line

  return (
    <span style={{ whiteSpace: "pre-line" }}>
      {displayed}
      {!done && (
        <span style={{
          display: "inline-block", width: 2, height: "1em",
          background: "#6B3FA0", marginLeft: 2, verticalAlign: "text-bottom",
          animation: "cursorBlink 0.7s step-end infinite",
        }} />
      )}
    </span>
  );
};

// Helper to add auth headers
function getAuthHeaders(idToken) {
  const headers = { "Content-Type": "application/json" };
  if (idToken) {
    headers["Authorization"] = `Bearer ${idToken}`;
  } else {
  }
  return headers;
}

/* ════════════════════════════════════════════════════════════════════
   MAIN APP
════════════════════════════════════════════════════════════════════ */
export default function TripPlanner() {

  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Hero handoff (home page "Create a Trip" / trip text search): initiator sentence
  // arrives via location.state and is fired through the normal intake path below.
  const [pendingInitiator, setPendingInitiator] = useState(null);
  // Hotels tab search status: "idle" | "loading" | "done" | "error"
  // (UI-only — reflects the /hotels request lifecycle in the right-panel Hotels tab)
  const [hotelSearchStatus, setHotelSearchStatus] = useState("idle");
  // True while the AI place-discovery pipeline is running (prefs submitted,
  // places not yet arrived) — drives the Discovery tab loader
  const [discoveringPlaces, setDiscoveringPlaces] = useState(false);
  // Interests the user picked on the preference screen (for deck ordering)
  const lastPrefsRef = useRef([]);
  const { idToken } = useAuth();

  /* ── Auth: always send a fresh Firebase token on every API request ──
     idToken from context loads asynchronously and several useCallbacks
     memoize a stale (null) value; this ref + firebase currentUser lookup
     guarantees the latest valid token at call time (auto-refreshes too). */
  const idTokenRef = useRef(idToken);
  useEffect(() => { idTokenRef.current = idToken; }, [idToken]);
  const freshIdToken = useCallback(async () => {
    try {
      if (auth.currentUser) return await auth.currentUser.getIdToken();
    } catch (_) { }
    return idTokenRef.current;
  }, []);
  const freshAuthHeaders = useCallback(async () => getAuthHeaders(await freshIdToken()), [freshIdToken]);

  /* ── state ── */
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [inputVal, setInputVal] = useState("");

  // agent bubbles [{id,agent,steps[],done}]
  const [bubbles, setBubbles] = useState([]);
  const bubbleIdRef = useRef(0);
  const seqRef = useRef(0); // monotonic counter — orders msgs + bubbles chronologically

  // stage: "intake"|"routing"|"prefs"|"places"|"building"|"done"
  const [stage, setStage] = useState("intake");

  // ── v5 UX: right-panel step tab (UI state — no logic impact) ──
  // "routing" | "discovery" | "hotels" | "itinerary"
  const [activeStep, setActiveStep] = useState("routing");
  // Panel visibility: desktop side panel / mobile fullscreen overlay.
  // Desktop starts open; mobile starts closed and auto-opens as the trip progresses.
  const [panelOpen, setPanelOpen] = useState(() => window.innerWidth > 600);
  const [tripInfo, setTripInfo] = useState({ destination: "", source: "", numDays: 0, startDate: "" });
  const intakeHistRef = useRef([]);

  // routing config + live WS routes
  const [showRoutingConfig, setShowRoutingConfig] = useState(false);
  const [routingPrefs, setRoutingPrefs] = useState({ travelMode: "all", trainClass: "2A", flightClass: "E", maxStops: 1 });
  const [liveRoutes, setLiveRoutes] = useState([]);
  const [returnRoutes, setReturnRoutes] = useState([]);
  const [noRoutes, setNoRoutes] = useState(false);
  const [outboundDone, setOutboundDone] = useState(false);
  const [returnDone, setReturnDone] = useState(false);
  const [returnSearching, setReturnSearching] = useState(false); // for route sheet loader
  const [routeSheetNote, setRouteSheetNote] = useState(null);  // banner shown in route sheet
  const [routeSheetInitTab, setRouteSheetInitTab] = useState("outbound"); // which tab opens first

  // Responsive: track if we're on mobile
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);
  // Itinerary scrolled (compact) — on mobile, hide the step tabs + "Your Itinerary"
  // strip too so the day list gets the full screen
  const [itinScrolled, setItinScrolled] = useState(false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── v5 UX: auto-advance the step tab + panel visibility as the trip stage moves ──
  useEffect(() => {
    if (["routing_config", "routing", "awaitingReturn", "awaitingReturnChange"].includes(stage)) {
      setActiveStep("routing");
    } else if (["prefs", "places", "building"].includes(stage)) {
      setActiveStep("discovery");
    } else if (stage === "done") {
      setActiveStep("itinerary");
    }
    // Mobile: close the overlay while building (keep agent bubbles visible in chat,
    // mirroring legacy setShowItinerary(false) during build); otherwise auto-open
    // whenever the stage advances so the user lands on the step that needs them.
    if (stage === "building") {
      if (isMobile) setPanelOpen(false);
    } else if (stage !== "intake") {
      setPanelOpen(true);
    }
  }, [stage, isMobile]);

  // Toggle itinerary panel visibility (user can close and reopen)
  const [showItinerary, setShowItinerary] = useState(true);

  // Mobile drag-to-resize: chat panel height in vh (25 = bottom quarter, 50 = half)
  const [chatHeightVh, setChatHeightVh] = useState(25);
  const dragStateRef = useRef(null); // {startY, startVh}
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [pickedRoute, setPickedRoute] = useState(null);
  const [awaitingReturn, setAwaitingReturn] = useState(false);  // waiting for return pick

  // places
  const [discoveredPlaces, setDiscoveredPlaces] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBuildBtn, setShowBuildBtn] = useState(false);
  const [deckResetKey, setDeckResetKey] = useState(0);  // increment to remount PlaceDeck
  const [pipelineStarted, setPipelineStarted] = useState(false); // discovery fired
  const [itineraryStarted, setItineraryStarted] = useState(false); // build fired

  // itinerary
  const [tripState, setTripState] = useState(null);
  const [nights, setNights] = useState(3);
  const [sessionId, setSessionId] = useState("");  // Lambda session tracking
  const userId = USER_ID;  // stable across renders, from localStorage
  const [pickedRouteState, setPickedRouteState] = useState(null);
  const [pickedReturnRouteState, setPickedReturnRouteState] = useState(null);

  // hotels
  const [allHotels, setAllHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showBooking, setShowBooking] = useState(false);

  /* ── Auth/Login state ── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [userClickSignIn, setUserClickSignIn] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);
  const recaptchaContainerId = "recaptcha-container";

  // Save hotel to trip-state whenever selectedHotel changes
  // No stage guard — hotel is set during "building" stage, before "done"
  useEffect(() => {
    if (!selectedHotel || !USE_LAMBDA || !USER_ID) return;
    const timer = setTimeout(async () => {
      fetch(`${ACTIVE_API}/trip-state`, {
        method: "POST",
        headers: await freshAuthHeaders(),
        body: JSON.stringify({
          user_id: USER_ID,
          session_id: sessionId,
          trip_payload: {
            tripInfo, nights,
            route: pickedRouteState,
            returnRoute: pickedReturnRouteState,
            selectedHotel,
            allHotels,     // save full hotel list for restore
            hasStay: true,
            liveRoutes: liveRoutes.slice(0, 10),
            returnRoutes: returnRoutes.slice(0, 10),
            tripState: {
              ...(tripState || {}),
              all_places: (tripState?.all_places?.length > 0) ? tripState.all_places : discoveredPlaces,
              selected_place_ids: tripState?.selected_place_ids?.length > 0 ? tripState.selected_place_ids : selectedIds,
            },
          },
        }),
      }).catch(() => { });
    }, 500);
    return () => clearTimeout(timer);
  }, [selectedHotel]);
  const [hasStay, setHasStay] = useState(false); // false until real hotels found

  const chatEndRef = useRef(null);
  const discoveryFiredRef = useRef(false);  // prevent double-fire

  /* auto-scroll */
  /* ── helpers ── */
  const fmtTime = () => new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  const addBot = t => setMsgs(m => {
    // Mark all previous bot messages as not typing, add new one with typing flag
    const prev = m.map(msg => msg.typing ? { ...msg, typing: false } : msg);
    return [...prev, { role: "bot", text: t, ts: fmtTime(), typing: true, seq: ++seqRef.current }];
  });
  const addUser = t => setMsgs(m => [...m, { role: "user", text: t, ts: fmtTime(), seq: ++seqRef.current }]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, bubbles, showBuildBtn, stage]);

  /* greeting + history restore on reload */
  useEffect(() => {
    const greeting = "Hey! I'm RouteChef AI 🗺️ Which destination are you thinking of? Could be a hill station, beach, heritage city — anywhere!";

    /* ── Hero handoff: initiator from home page (Create a Trip tab) ──
       History restore below still runs so the past conversation shows,
       then the initiator is queued (fired via the effect below) and sent
       as a normal user message through handleIntake. The old /trip-state
       itinerary restore is skipped to avoid racing the new trip's reset. */
    const heroInit = (routerLocation.state?.fromHero && routerLocation.state?.initiator)
      ? String(routerLocation.state.initiator)
      : null;
    if (heroInit) {
      // Clear router state so a page refresh doesn't re-fire the initiator
      window.history.replaceState({}, "");
    }

    const params = new URLSearchParams(window.location.search);
    const encoded = params.get("restore");
    if (!heroInit && encoded) {
      setMsgs([{ role: "bot", text: greeting, ts: fmtTime(), seq: ++seqRef.current }]);
      intakeHistRef.current = [];
      unpackTrip(encoded).then(data => {
        if (!data?.tripState?.itinerary_complete) return;
        setTripState(data.tripState);
        if (data.tripInfo) setTripInfo(data.tripInfo);
        if (data.nights) setNights(data.nights);
        if (data.route) {
          setPickedRouteState(data.route);
          setLiveRoutes(data.liveRoutes?.length > 0 ? data.liveRoutes : data.route.rawRoute ? [data.route.rawRoute] : []);
        }
        if (data.returnRoute) {
          setPickedReturnRouteState(data.returnRoute);
          setReturnRoutes(data.returnRoutes?.length > 0 ? data.returnRoutes : data.returnRoute.rawRoute ? [data.returnRoute.rawRoute] : []);
        }
        if (data.hotel) {
          setSelectedHotel(data.hotel);
          setHasStay(true);
          setAllHotels([data.hotel]);
        }
        setStage("done");
        // Restore place state so rebuilds and re-selection work
        if (data.tripState.all_places?.length > 0)
          setDiscoveredPlaces(data.tripState.all_places);
        if (data.tripState.selected_place_ids?.length > 0)
          setSelectedIds(data.tripState.selected_place_ids);
        const dest = data.tripState.primary_destination || data.tripInfo?.destination || "your destination";
        setTimeout(() => {
          addBot(`Welcome back! Your ${dest} itinerary is loaded on the right. Ask me to change anything, add places, or tweak the plan.`);
        }, 300);
      }).catch(_e => { });
      return;
    }

    if (USE_LAMBDA && USER_ID) {
      (async () => {
      // Fresh Firebase token for the restore calls (context token may not be ready at mount)
      const authHdrs = await freshAuthHeaders();
      fetch(`${ACTIVE_API}/history?user_id=${encodeURIComponent(USER_ID)}&limit=60`, { headers: authHdrs })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const history = data?.history || [];
          if (history.length > 0) {
            const recent = history.slice(-30);
            const restored = recent.map(h => ({
              role: h.role === "user" ? "user" : "bot",
              text: h.message,
              ts: h.ts ? new Date(h.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : fmtTime(),
              seq: ++seqRef.current,
            }));
            setMsgs([
              { role: "bot", text: greeting, ts: fmtTime(), seq: ++seqRef.current },
              { role: "bot", text: "📋 Here's our recent conversation:", ts: fmtTime(), seq: ++seqRef.current },
              ...restored,
              { role: "bot", text: "Pick up where we left off, or start a new trip!", ts: fmtTime(), seq: ++seqRef.current },
            ]);
            intakeHistRef.current = recent
              .filter(h => h.role === "user" || h.role === "bot")
              .map(h => ({ role: h.role === "user" ? "user" : "assistant", content: h.message }));

            // Try to restore full itinerary state from /trip-state (saved on itinerary completion)
            // Skipped on hero handoff — a new trip is about to start and would be reset anyway
            if (USE_LAMBDA && !heroInit) {
              fetch(`${ACTIVE_API}/trip-state?user_id=${encodeURIComponent(USER_ID)}`, { headers: authHdrs })
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                  if (!data?.found || !data?.payload?.tripState?.itinerary_complete) return;
                  const p = data.payload;
                  setTripState(p.tripState);
                  if (p.nights) setNights(p.nights);
                  if (p.tripInfo) setTripInfo(p.tripInfo);
                  if (p.route) {
                    setPickedRouteState(p.route);
                    // Restore full route list if saved, otherwise just the picked route
                    setLiveRoutes(p.liveRoutes?.length > 0 ? p.liveRoutes : p.route.rawRoute ? [p.route.rawRoute] : []);
                  }
                  if (p.returnRoute) {
                    setPickedReturnRouteState(p.returnRoute);
                    setReturnRoutes(p.returnRoutes?.length > 0 ? p.returnRoutes : p.returnRoute.rawRoute ? [p.returnRoute.rawRoute] : []);
                  }
                  if (p.selectedHotel) {
                    setSelectedHotel(p.selectedHotel);
                    setHasStay(true);
                    // Restore full hotel list if saved, else just the selected one
                    setAllHotels(p.allHotels?.length > 0 ? p.allHotels : [p.selectedHotel]);
                  } else if (p.tripState?.stays?.length > 0) {
                    const stay = p.tripState.stays[0];
                    const hotel = {
                      hotel_code: stay.hotel_code || "stay_0",
                      hotel_name: stay.hotel_name,
                      hotel_rating: stay.stars || stay.hotel_rating || 3,
                      price_per_night_inr: stay.price_per_night_inr || 0,
                      city_name: stay.hotel_area || stay.city_name || "",
                      address: stay.hotel_area || "",
                      location_label: stay.hotel_area || "",
                      primary_image: stay.primary_image || "",
                      strategic_reason: stay.strategic_reason || "",
                      lat: stay.lat, lng: stay.lng,
                    };
                    setSelectedHotel(hotel);
                    setAllHotels([hotel]);
                    setHasStay(true);
                  }
                  setStage("done");
                  setShowItinerary(true);
                  // Also restore place state so ± nights and re-discovery work
                  if (p.tripState.all_places?.length > 0)
                    setDiscoveredPlaces(p.tripState.all_places);
                  if (p.tripState.selected_place_ids?.length > 0)
                    setSelectedIds(p.tripState.selected_place_ids);
                  const dest = p.tripState.primary_destination || p.tripInfo?.destination || "your destination";
                  setTimeout(() => addBot(
                    `Welcome back! Your ${dest} itinerary is ready on the right ✨ Ask me to change anything.`
                  ), 200);
                })
                .catch(() => { });
            }
          } else {
            setMsgs([{ role: "bot", text: greeting, ts: fmtTime(), seq: ++seqRef.current }]);
            intakeHistRef.current = [];
          }
        })
        .catch(() => {
          setMsgs([{ role: "bot", text: greeting, ts: fmtTime(), seq: ++seqRef.current }]);
          intakeHistRef.current = [];
        })
        .finally(() => {
          // Hero handoff: history (or its failure) is settled — now send the
          // initiator from the user's side through the normal intake path
          if (heroInit) setPendingInitiator(heroInit);
        });
      })();
    } else {
      setMsgs([{ role: "bot", text: greeting, ts: fmtTime(), seq: ++seqRef.current }]);
      intakeHistRef.current = [];
      if (heroInit) setPendingInitiator(heroInit);
    }
  }, []);

  /* ── bubble helpers ── */
  const startBubble = useCallback(agent => {
    const id = ++bubbleIdRef.current;
    setBubbles(b => [...b, { id, agent, steps: [], done: false, seq: ++seqRef.current }]);
    return id;
  }, []);

  const stepBubble = useCallback((id, step) => {
    setBubbles(b => b.map(x => x.id === id ? { ...x, steps: [...x.steps, step] } : x));
  }, []);

  const doneBubble = useCallback(id => {
    setBubbles(b => b.map(x => x.id === id ? { ...x, done: true } : x));
  }, []);

  /* ── consumeSSE / Lambda batch handler ── */
  const consumeSSE = useCallback(async (endpoint, body, onPlaces) => {
    const reqBody = { ...body, session_id: sessionId, user_id: userId };

    if (USE_LAMBDA) {
      const longBase = LONG_RUNNING_API && LONG_ENDPOINTS.includes(endpoint) ? LONG_RUNNING_API : ACTIVE_API;
      const resp = await fetch(`${longBase}${endpoint}`, {
        method: "POST",
        headers: await freshAuthHeaders(),
        body: JSON.stringify(reqBody),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();

      if (json.session_id && json.session_id !== sessionId) {
        setSessionId(json.session_id);
      }

      const activeBubbles = {};
      for (const ev of (json.events || [])) {
        if (ev.type === "agent_start") {
          activeBubbles[ev.agent] = startBubble(ev.agent);
        } else if (ev.type === "agent_step") {
          const bId = activeBubbles[ev.agent];
          if (bId !== undefined) stepBubble(bId, ev.message);
        } else if (ev.type === "agent_done") {
          const bId = activeBubbles[ev.agent];
          if (bId !== undefined) { doneBubble(bId); delete activeBubbles[ev.agent]; }
        } else if (ev.type === "bot_message") {
          addBot(ev.message);
        } else if (ev.type === "state_update") {
          if (ev.data?.places && onPlaces) onPlaces(ev.data.places);
        }
      }
      Object.values(activeBubbles).forEach(bId => doneBubble(bId));

      // Only update tripState from chat responses during building/itinerary stages
      // to avoid showing the right panel prematurely from intermediate /chat calls
      if (json.state?.itinerary_complete) setTripState(json.state);
      return json;
    }

    // Local SSE mode
    const resp = await fetch(`${ACTIVE_API}${endpoint}`, {
      method: "POST",
      headers: await freshAuthHeaders(),
      body: JSON.stringify(body),
    });
    if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

    const reader = resp.body.getReader();
    const dec = new TextDecoder();
    let buf = "";
    const activeBubbles = {};

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop();
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        try {
          const ev = JSON.parse(raw);
          if (ev.type === "done") return;
          if (ev.type === "agent_start") {
            activeBubbles[ev.agent] = startBubble(ev.agent);
          } else if (ev.type === "agent_step") {
            const bId = activeBubbles[ev.agent];
            if (bId !== undefined) stepBubble(bId, ev.message);
          } else if (ev.type === "agent_done") {
            const bId = activeBubbles[ev.agent];
            if (bId !== undefined) { doneBubble(bId); delete activeBubbles[ev.agent]; }
          } else if (ev.type === "bot_message") {
            addBot(ev.message);
          } else if (ev.type === "state_update") {
            if (ev.data?.places && onPlaces) onPlaces(ev.data.places);
          }
        } catch (_) { }
      }
    }
    Object.values(activeBubbles).forEach(bId => doneBubble(bId));
  }, [sessionId, userId, stepBubble, doneBubble]);

  /* ── fetch hotels ── */
  const fetchHotels = useCallback(async (destination, checkIn, checkOut) => {
    setHotelSearchStatus("loading");
    try {
      // Compute check-in/check-out from tripInfo if not passed
      let cin = checkIn, cout = checkOut;
      if (!cin && tripInfo?.startDate) cin = tripInfo.startDate; // dd-mm-yyyy
      if (!cout && cin && nights) {
        const [d, m, y] = cin.split("-").map(Number);
        const co = new Date(y, m - 1, d);
        co.setDate(co.getDate() + nights);
        cout = [String(co.getDate()).padStart(2, "0"), String(co.getMonth() + 1).padStart(2, "0"), co.getFullYear()].join("-");
      }

      const countryName = inferCountryFromPlace(destination);
      const resp = await fetch(`${ACTIVE_API}/hotels`, {
        method: "POST",
        headers: await freshAuthHeaders(),
        body: JSON.stringify({
          place_name: destination,
          country_name: countryName || "", // always present — backend requires the field
          check_in: cin || "",
          check_out: cout || "",
          radius_km: 20,
          user_id: userId,
        }),
      });
      if (!resp.ok) { setAllHotels([]); setHasStay(false); setHotelSearchStatus("error"); return; }
      const data = await resp.json();
      const hotels = (data.hotels || [])
        .sort((a, b) => (a.distance_km || 99) - (b.distance_km || 99));
      setAllHotels(hotels);
      setHotelSearchStatus("done");
      if (hotels.length > 0 && !selectedHotel) {
        const best = hotels[0];
        const bestRoom = best.rooms?.[0] || null;
        const autoSelected = {
          ...best,
          hotel_name: bestRoom?.name || best.best_room_name || `Hotel ${best.hotel_code}`,
          hotel_code: best.hotel_code,
          selected_room: bestRoom,
          price_per_night_inr: bestRoom?.total_fare || best.price_per_night_inr || 0,
          location_label: best.distance_km != null ? `${Number(best.distance_km).toFixed(1)}km away` : "",
        };
        setSelectedHotel(autoSelected);
        setHasStay(true);
      }
    } catch (e) {
      // Don't clear allHotels on error
      setHotelSearchStatus("error");
    }
  }, [userId, tripInfo, nights, selectedHotel]);

  /* ── build itinerary ── */
  const buildItinerary = useCallback(async () => {
    setBusy(true);
    setShowBuildBtn(false);
    setItineraryStarted(true);
    setShowItinerary(false); // focus user on chat while building
    const count = selectedIds.length;
    const label = count === 0 ? "a relaxed stay" : `${count} place${count > 1 ? "s" : ""}`;
    addBot(`Building your ${nights}-night plan for ${label}…`);
    setStage("building");

    try {
      const result = await consumeSSE("/select-places", {
        place_ids: selectedIds,
        trip_payload: {
          tripInfo,
          nights,
          route: pickedRouteState,
          returnRoute: pickedReturnRouteState,
          selectedHotel,
          hasStay,
          liveRoutes: liveRoutes.slice(0, 20),
          returnRoutes: returnRoutes.slice(0, 20),
          tripState: {
            ...(tripState || {}),
            all_places: (tripState?.all_places?.length > 0)
              ? tripState.all_places
              : discoveredPlaces,
          },
        },
      }, null);
      const s = result?.state || (USE_LAMBDA ? null : await fetch(`${ACTIVE_API}/state?session_id=${sessionId}`, { headers: await freshAuthHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null));
      // console.log("[Itinerary] state:", s?.itinerary_complete, "places:", s?.selected_places?.length);
      setTripState(s);
      setNights(s.num_days || nights);

      // Fetch TBO hotels — returns list directly to avoid stale closure
      const dest = s.primary_destination || tripInfo.destination;
      let tboHotels = [];
      if (dest) {
        setHotelSearchStatus("loading"); // Hotels tab shows live search status during build
        try {
          const cin = s.start_date ? _fmtDate(new Date(s.start_date)) : "";
          const cout = s.start_date && s.num_days
            ? _fmtDate(new Date(new Date(s.start_date).getTime() + s.num_days * 86400000))
            : "";
          const countryName = inferCountryFromPlace(dest);
          const resp = await fetch(`${ACTIVE_API}/hotels`, {
            method: "POST",
            headers: await freshAuthHeaders(),
            body: JSON.stringify({
              place_name: dest,
              country_name: countryName || "", // always present — backend requires the field
              check_in: cin, check_out: cout, radius_km: 20, user_id: USER_ID,
            }),
          });
          if (resp.ok) {
            const data = await resp.json();
            tboHotels = (data.hotels || []).sort((a, b) => (a.distance_km || 99) - (b.distance_km || 99));
            setAllHotels(tboHotels);
            setHotelSearchStatus("done");
          } else {
            setHotelSearchStatus("error");
          }
        } catch (e) { setHotelSearchStatus("error"); }
      }

      // backendStays from accommodation agent — use first stay's hotel_code
      const backendStays = s.stays || [];
      let finalHotel = null;

      if (tboHotels.length > 0) {
        // Pick closest TBO hotel with real rooms
        const stay = backendStays[0];
        // Try to match accommodation agent's hotel_code first
        const matched = stay?.hotel_code
          ? tboHotels.find(h => h.hotel_code === stay.hotel_code) || tboHotels[0]
          : tboHotels[0];
        const bestRoom = matched.rooms?.[0] || null;
        const hotelLabel = matched.hotel_name || `Hotel ${matched.hotel_code}`;
        const roomShort = bestRoom?.name?.split(",")[0] || "";
        finalHotel = {
          ...matched,
          hotel_name: hotelLabel,
          selected_room: bestRoom,
          price_per_night_inr: bestRoom?.total_fare || matched.price_per_night_inr || 0,
          location_label: matched.distance_km != null ? `${Number(matched.distance_km).toFixed(1)}km away` : "",
        };
      } else if (backendStays.length > 0) {
        // No TBO hotels (international/unsupported region) — use accommodation agent suggestion
        const stay = backendStays[0];
        finalHotel = {
          hotel_code: stay.hotel_code || `stay_0`,
          hotel_name: stay.hotel_name || stay.hotel_area || "Accommodation",
          hotel_rating: stay.stars || 3,
          price_per_night_inr: stay.price_per_night_inr || 0,
          city_name: stay.hotel_area || "",
          location_label: stay.hotel_area || "",
          distance_km: null,
          rooms: [],
          lat: stay.lat || 0, lng: stay.lng || 0,
        };
      }

      if (finalHotel) {
        setSelectedHotel(finalHotel);
        setHasStay(true);
      }

      // Save to DynamoDB with local vars — fire and forget
      if (USE_LAMBDA && USER_ID && finalHotel) {
        fetch(`${ACTIVE_API}/trip-state`, {
          method: "POST",
          headers: await freshAuthHeaders(),
          body: JSON.stringify({
            user_id: USER_ID,
            session_id: sessionId,
            trip_payload: {
              tripInfo,
              nights: s.num_days || nights,
              route: pickedRouteState,
              returnRoute: pickedReturnRouteState,
              selectedHotel: finalHotel,
              allHotels: [],  // full list saved by useEffect after allHotels state sets
              hasStay: true,
              liveRoutes: liveRoutes.slice(0, 10),
              returnRoutes: returnRoutes.slice(0, 10),
              tripState: { ...s, all_places: s.all_places || [], selected_place_ids: s.selected_place_ids || selectedIds },
            },
          }),
        }).catch(() => { });
      }

      setStage("done");
      setShowItinerary(true);
      addBot("Your itinerary is ready! Tap any day on the right to expand it 👉");
    } catch (e) {
      // console.error("[Build]", e);
      addBot("Itinerary build failed — check server connection. You can try again.");
      setStage("places");
      setItineraryStarted(false);
      setShowBuildBtn(true);
    }
    setBusy(false);
  }, [selectedIds, nights, sessionId, consumeSSE, fetchHotels, tripInfo]);

  // ── Smart itinerary refresh ───────────────────────────────────────────────
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshItinerary = useCallback(async (newNights, reason, routeOverride, returnRouteOverride) => {
    if (stage !== "done" || !tripState?.itinerary_complete) return;

    // Prefer tripState.selected_place_ids, fall back to selectedIds state
    const ids = (tripState?.selected_place_ids?.length > 0)
      ? tripState.selected_place_ids
      : selectedIds;

    // Prefer tripState.all_places, fall back to discoveredPlaces state
    const allPlaces = (tripState?.all_places?.length > 0)
      ? tripState.all_places
      : discoveredPlaces;

    if (ids.length === 0) {
      addBot("No places selected yet — please select places first before adjusting.");
      return;
    }

    // Use explicit overrides so we always use the NEWLY selected route, not stale closure
    const effectiveRoute = routeOverride !== undefined ? routeOverride : pickedRouteState;
    const effectiveReturnRoute = returnRouteOverride !== undefined ? returnRouteOverride : pickedReturnRouteState;

    setBusy(true);
    setIsRefreshing(true);
    setShowItinerary(false);
    // Take the user to the Itinerary tab so they watch it regenerate
    setActiveStep("itinerary");
    setPanelOpen(true);
    const msg = reason === "nights_changed"
      ? `Adjusting to ${newNights} night${newNights !== 1 ? "s" : ""}… rebuilding your itinerary 🔄`
      : reason === "places_changed"
        ? `Got it! Rebuilding your itinerary with the new place selection 🔄`
        : `Route changed — rebuilding your ${newNights}-night itinerary with updated timing 🔄`;
    addBot(msg);
    try {
      const result = await consumeSSE("/select-places", {
        place_ids: ids,
        trip_payload: {
          tripInfo,
          nights: newNights,
          route: effectiveRoute,
          returnRoute: effectiveReturnRoute,
          selectedHotel, hasStay,
          liveRoutes: liveRoutes.slice(0, 20),
          returnRoutes: returnRoutes.slice(0, 20),
          tripState: {
            ...(tripState || {}),
            all_places: allPlaces,
            selected_place_ids: ids,
          },
        },
      }, null);
      const s = result?.state;
      if (s?.itinerary_complete) {
        setTripState(s);
        setNights(s.num_days || newNights);
        setShowItinerary(true);
        // Keep discoveredPlaces in sync
        if (s.all_places?.length > 0) setDiscoveredPlaces(s.all_places);
        const placeCnt = s.selected_place_ids?.length || ids.length;
        addBot(`Done! Your ${s.num_days || newNights}-night itinerary with ${placeCnt} places is ready ✓`);

        // Route/days changed → stay dates may have shifted. Re-run the
        // accommodation call with check-in/out derived from the NEW routes'
        // arrival/departure times (Hotels tab shows its loader meanwhile).
        if (tripInfo?.destination) {
          const stay = deriveStayDates(effectiveRoute, effectiveReturnRoute, s.num_days || newNights);
          fetchHotels(tripInfo.destination, stay?.cin, stay?.cout);
        }
      }
    } catch (e) {
      addBot("Refresh failed — try asking me to rebuild your itinerary.");
      setShowItinerary(true);
    }
    setIsRefreshing(false);
    setBusy(false);
  }, [stage, tripState, tripInfo, pickedRouteState, pickedReturnRouteState,
    selectedHotel, hasStay, liveRoutes, returnRoutes, discoveredPlaces,
    selectedIds, consumeSSE, addBot, fetchHotels]);

  // When route changes — pass new route directly to refresh (bypass stale closure)
  const handleRouteChange = useCallback((newRoute, isReturn) => {
    if (isReturn) {
      setPickedReturnRouteState(newRoute);
    } else {
      // ── Outbound changed — check if arrival date shifted ──────────────────
      const oldArrival = routeArrival(pickedRouteState);
      const newArrival = routeArrival(newRoute);
      const oldDateStr = oldArrival ? new Date(oldArrival).toDateString() : null;
      const newDateStr = newArrival ? new Date(newArrival).toDateString() : null;
      const arrivalChanged = newDateStr && oldDateStr !== newDateStr;

      setPickedRouteState(newRoute);

      if (arrivalChanged) {
        // Compute new return departure date = new arrival + nights
        const arr = new Date(newArrival);
        const depDt = new Date(arr);
        depDt.setDate(depDt.getDate() + nights);
        const newReturnDate = [
          String(depDt.getDate()).padStart(2, "0"),
          String(depDt.getMonth() + 1).padStart(2, "0"),
          depDt.getFullYear(),
        ].join("-"); // dd-mm-yyyy

        // Check if return departure date also changed
        const oldReturnDep = pickedReturnRouteState
          ? routeDeparture(pickedReturnRouteState)
          : null;
        const oldReturnDateStr = oldReturnDep ? new Date(oldReturnDep).toDateString() : null;
        const returnDateChanged = !oldReturnDateStr || depDt.toDateString() !== oldReturnDateStr;

        if (returnDateChanged) {
          // Clear stale return route, show loader, re-search
          setPickedReturnRouteState(null);
          setReturnRoutes([]);
          setReturnDone(false);
          setReturnSearching(true);
          setRouteSheetInitTab("return");
          setRouteSheetNote(
            `Arrival shifted to ${new Date(newArrival).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} — searching return routes for ${newReturnDate}…`
          );

          // Keep route sheet open on return tab
          if (stage === "done") setStage("awaitingReturnChange");
          isReturnForRouteChangeRef.current = true; // signal: just refresh itinerary on pick

          addBot(`Arrival shifted to ${new Date(newArrival).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} — finding return routes for ${newReturnDate}…`);

          const returnInfo = {
            destination: tripInfo?.source || "",
            source: tripInfo?.destination || "",
            startDate: newReturnDate,
            numDays: 1,
          };
          const prefs = { maxStops: 1, trainClass: "2A" };

          ; (async () => {
            try {
              const routes = await fetchRoutesViaWS(returnInfo, prefs, route => {
                setReturnRoutes(prev => [...prev, route]);
              });
              setReturnDone(true);
              setReturnRoutes(routes);
              setReturnSearching(false);
              setRouteSheetNote(
                routes.length > 0
                  ? `Pick a return route for ${newReturnDate}.`
                  : `No return routes found for ${newReturnDate}.`
              );
              if (routes.length === 0) {
                addBot(`No return routes found for ${newReturnDate}. You can skip return or try different dates.`);
              } else {
                addBot(`Found ${routes.length} return route${routes.length !== 1 ? "s" : ""} for ${newReturnDate} — pick one below.`);
              }
            } catch {
              setReturnSearching(false);
              setReturnDone(true);
            }
          })();

          return; // Wait for user to pick return — itinerary refresh happens then
        }
      }
    }

    if (stage !== "done" || !tripState?.itinerary_complete) return;

    setShowItinerary(false);

    const outbound = isReturn ? pickedRouteState : newRoute;
    const ret = isReturn ? newRoute : pickedReturnRouteState;
    const prevDays = computeAvailableDays(pickedRouteState, pickedReturnRouteState, nights);
    const newDays = computeAvailableDays(outbound, ret, nights);
    const n = newDays !== prevDays ? newDays : nights;
    if (newDays !== prevDays) setNights(newDays);

    setTimeout(() => refreshItinerary(n, "route_changed", outbound, ret), 300);
  }, [stage, tripState, pickedRouteState, pickedReturnRouteState,
    nights, tripInfo, refreshItinerary, addBot]);


  // ── Nights change re-flow ─────────────────────────────────────────────────
  // ── Re-search return routes for a new date (when nights change) ──────────
  // pendingNightsRef holds the newNights value across async operations
  const pendingNightsRef = useRef(null);
  // When true, awaitingReturnChange is for outbound route change (not nights) — just refresh itinerary
  const isReturnForRouteChangeRef = useRef(false);

  /* ── v6: re-check seat availability for a different train class.
     Called from the seat-class bar under train segments in route cards.
     Updates the segment's seat_avail_ui in liveRoutes/returnRoutes and,
     when the affected route is a picked one, refreshes its price so the
     itinerary totals update everywhere without fail. ── */
  const handleSeatAvailCheck = useCallback(async (routeId, nodeId, seg, newClass) => {
    // Journey date in dd-mm-yyyy (prefer the existing avail record's date)
    let dateStr = seg.seat_avail_ui?.date || "";
    if (!dateStr && seg.origin_dep_dt) {
      const d = new Date(seg.origin_dep_dt);
      if (!isNaN(d)) {
        dateStr = [
          String(d.getDate()).padStart(2, "0"),
          String(d.getMonth() + 1).padStart(2, "0"),
          d.getFullYear(),
        ].join("-");
      }
    }
    const params = new URLSearchParams({
      train_num: String(seg.train_num || ""),
      src: String(seg.origin_code || ""),
      dest: String(seg.dest_code || ""),
      date: dateStr,
      pref_train_class: newClass,
      quota: "GN",
      api_key: WS_API_KEY,
      get_avail_only: "1",
    });

    // 20s timeout — on expiry or a null/invalid response the caller reverts
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 20000);
    let ui = null;
    try {
      const resp = await fetch(`${AVAIL_API}/search?${params.toString()}`, { signal: ctrl.signal });
      const data = await resp.json();
      ui = data?.data?.seat_avail_ui || null;
    } finally {
      clearTimeout(timer);
    }
    if (!ui || !ui.seat_class) throw new Error("avail_failed");

    // Update the segment inside route lists (immutable)
    const applyTo = (routes) => (routes || []).map(r => r.route_id !== routeId ? r : ({
      ...r,
      total_fare: null, // force per-segment fare recompute
      nodes: (r.nodes || []).map(n => n.node_id !== nodeId ? n : ({
        ...n,
        segment: { ...n.segment, seat_class_ui: ui.seat_class, seat_avail_ui: ui },
      })),
    }));
    const nextLive = applyTo(liveRoutes);
    const nextReturn = applyTo(returnRoutes);
    setLiveRoutes(nextLive);
    setReturnRoutes(nextReturn);

    // Propagate the new fare to picked routes → itinerary totals update
    const updatedRaw = [...nextLive, ...nextReturn].find(r => r.route_id === routeId);
    if (updatedRaw) {
      const newPrice = computeFare(updatedRaw) || 0;
      if (pickedRouteState?.id === routeId) {
        setPickedRouteState(p => p ? { ...p, price: newPrice, rawRoute: updatedRaw } : p);
      }
      if (pickedReturnRouteState?.id === routeId) {
        setPickedReturnRouteState(p => p ? { ...p, price: newPrice, rawRoute: updatedRaw } : p);
      }
    }
    return ui;
  }, [liveRoutes, returnRoutes, pickedRouteState, pickedReturnRouteState]);

  /* ── v5: convert a raw WS route into the picked-route shape.
     EXACT mirror of RouteSheet's onSelectOutbound/onSelectReturn conversion in
     ItineraryPanel — used by the right-panel Routing tab so changing a route
     there behaves identically to the Edit-route button in itinerary view. ── */
  const buildPickedRouteFromRaw = useCallback((r) => {
    const cls = classifyRoute(r);
    const fare = computeFare(r);
    const dur = computeDuration(r);
    const nodes = r.nodes || [];
    const first = nodes[0]?.segment;
    const last = nodes[nodes.length - 1]?.segment;
    const bookLink = r.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;
    return {
      id: r.route_id, icon: cls.icon, label: cls.label, badge: cls.badge,
      color: cls.color, price: fare || 0, bookingLink: bookLink,
      sub: `${first?.origin_name || first?.origin_code_iata || ""} → ${last?.dest_name || last?.dest_code_iata || ""}`,
      detail: `${fmtDuration(dur)} · ${r.num_stops || 0} stop${(r.num_stops || 0) !== 1 ? "s" : ""}`,
      rawRoute: r,
    };
  }, []);

  const searchReturnRoutes = useCallback(async (newNights) => {
    if (!tripInfo?.startDate || !tripInfo?.source || !tripInfo?.destination) return;

    pendingNightsRef.current = newNights; // store for use after async
    isReturnForRouteChangeRef.current = false; // this is nights-change, not route-change

    setReturnRoutes([]);
    setReturnDone(false);
    setReturnSearching(true);

    // Compute new return date
    const arrivalDt = routeArrival(pickedRouteState);
    let baseDate;
    if (arrivalDt) {
      baseDate = new Date(arrivalDt);
    } else {
      const [d, m, y] = tripInfo.startDate.split("-").map(Number);
      baseDate = new Date(y, m - 1, d);
    }
    baseDate.setDate(baseDate.getDate() + newNights);
    const newReturnDate = `${String(baseDate.getDate()).padStart(2, "0")}-${String(baseDate.getMonth() + 1).padStart(2, "0")}-${baseDate.getFullYear()}`;

    const returnInfo = {
      destination: tripInfo.source,
      source: tripInfo.destination,
      startDate: newReturnDate,
      numDays: 1,
    };

    const prefs = { maxStops: 1, trainClass: "2A" };

    setRouteSheetNote(`Nights changed to ${newNights}. Please pick your return route for ${newReturnDate}.`);
    setRouteSheetInitTab("return");

    addBot(`Searching return routes for ${newReturnDate}… 🔍`);
    try {
      const routes = await fetchRoutesViaWS(returnInfo, prefs, route => {
        setReturnRoutes(prev => [...prev, route]);
      });
      setReturnDone(true);
      setReturnRoutes(routes);
      setReturnSearching(false);

      if (routes.length === 0) {
        addBot(`No return routes found for ${newReturnDate}. Try a different date.`);
        return;
      }

      // Auto-pick best matching route from fresh results
      const newReturn = findMatchingReturnRoute(routes, pickedRouteState, newNights, null);
      let chosenReturn = newReturn || null;
      if (chosenReturn) setPickedReturnRouteState(chosenReturn);

      if (chosenReturn) {
        addBot(`Return route for ${newReturnDate} auto-selected: ${chosenReturn.label} — ${chosenReturn.sub}. Pick a different one from the list below if needed.`);
      } else {
        addBot(`Found ${routes.length} return routes for ${newReturnDate}. Please pick one below to continue.`);
      }

      // Only switch to awaitingReturnChange if we haven't already moved past it
      // (user may have already picked a route while search was still running)
      setStage(prev =>
        prev === "awaitingReturnChange" || prev === "done" || prev === "places" || prev === "building"
          ? prev  // don't override if already past this stage
          : "awaitingReturnChange"
      );

    } catch (e) {
      // console.error("[returnSearch]", e);
      addBot("Return route search failed. You can try editing the route manually.");
      setReturnDone(true);
      setReturnSearching(false);
      setStage("done");
    }
  }, [tripInfo, pickedRouteState, tripState, addBot]);

  // ── Nights change re-flow ─────────────────────────────────────────────────
  const handleNightsChangeOpenSheet = useCallback((newNights) => {
    // Immediately hide itinerary and switch to route-picking stage
    setShowItinerary(false);
    setStage("awaitingReturnChange");
    setShowBuildBtn(false);
    setItineraryStarted(false);
    addBot(`Changed to ${newNights} night${newNights !== 1 ? "s" : ""}. Pick your return route for the new date below — outbound routes are the same as before. Once you pick return, I'll rediscover places and rebuild your itinerary 🔄`);
    // Kick off return route search async
    searchReturnRoutes(newNights);
  }, [addBot, searchReturnRoutes]);

  // ── Return route picked during nights-change flow → run full pipeline ──────
  const handleReturnRouteForNightsChange = useCallback(async (route) => {
    const cls = classifyRoute(route);
    const fare = computeFare(route);
    const dur = computeDuration(route);
    const nodes = route.nodes || [];
    const first = nodes[0]?.segment;
    const last = nodes[nodes.length - 1]?.segment;
    const sub = `${first?.origin_name || first?.origin_code_iata || ""} → ${last?.dest_name || last?.dest_code_iata || ""}`;
    const bookLink = route.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;
    const newReturn = {
      id: route.route_id, icon: cls.icon, label: cls.label, badge: cls.badge,
      color: cls.color, sub, price: fare || 0, bookingLink: bookLink,
      detail: `${fmtDuration(dur)} · ${route.num_stops || 0} stop${(route.num_stops || 0) !== 1 ? "s" : ""}`,
      rawRoute: route,
    };
    setPickedReturnRouteState(newReturn);
    setRouteSheetNote(null);
    setRouteSheetInitTab("outbound");

    const n = pendingNightsRef.current || nights;
    pendingNightsRef.current = null; // clear immediately so late searchReturnRoutes doesn't reuse

    // Run discovery agent fresh for the destination + new nights
    addBot(`Return route confirmed ✓ Rediscovering places for ${n} nights in ${tripInfo?.destination || "destination"}…`);
    setBusy(true);
    setStage("places");
    discoveryFiredRef.current = false; // allow re-fire

    const bId = startBubble("discovery");
    stepBubble(bId, `Re-researching places for ${n} nights…`);

    try {
      const result = await consumeSSE("/chat", {
        message: `Discover places for: destination="${tripInfo.destination}" source="${tripInfo.source}" days=${n} start="${tripInfo.startDate}"`,
        reset: false,
      }, places => {
        if (places?.length > 0) setDiscoveredPlaces(orderPlacesForInterests(places, lastPrefsRef.current));
      });
      doneBubble(bId);

      const places = result?.places || result?.state?.all_places || discoveredPlaces;
      if (places?.length > 0) {
        setDiscoveredPlaces(orderPlacesForInterests(places, lastPrefsRef.current));
        setSelectedIds([]); // reset selection for fresh pick
        setDeckResetKey(k => k + 1);
        addBot(`Found ${places.length} great spots for your ${n}-night trip! Pick the ones you want to visit 👇`);
        setShowBuildBtn(false);
      } else {
        // No fresh places — use previous selection and go straight to build
        addBot("Couldn't rediscover places. Using your previous selection to rebuild.");
        const ids = tripState?.selected_place_ids || [];
        setStage("building");
        const res2 = await consumeSSE("/select-places", {
          place_ids: ids,
          trip_payload: { tripInfo, nights: n, route: pickedRouteState, returnRoute: newReturn, selectedHotel, hasStay, liveRoutes: liveRoutes.slice(0, 20), returnRoutes: returnRoutes.slice(0, 20) },
        }, null);
        const s = res2?.state;
        if (s?.itinerary_complete) {
          setTripState(s); setNights(s.num_days || n);
          setShowItinerary(true); setStage("done");
          addBot(`Done! Your ${s.num_days || n}-night itinerary is ready ✓`);
        }
      }
    } catch (e) {
      // console.error("[nights pipeline]", e);
      doneBubble(bId);
      addBot("Something went wrong. Try asking me to rebuild.");
      setStage("done"); setShowItinerary(true);
    }
    setBusy(false);
    pendingNightsRef.current = null;
  }, [nights, tripState, tripInfo, pickedRouteState, selectedHotel, hasStay,
    liveRoutes, returnRoutes, discoveredPlaces, consumeSSE, addBot,
    startBubble, stepBubble, doneBubble]);

  const runRoutingAgent = useCallback(async info => {
    addBot(`Got it! I'll search routes from ${info.source} to ${info.destination}. Want to change train class or stops, or should I use the defaults (2A, max 1 stop)?`);
    setShowRoutingConfig(true);
    setStage("routing_config");
  }, []);

  /* ── start WS routing after config ── */
  const startWsRouting = useCallback(async (info, prefs) => {
    setShowRoutingConfig(false);
    setLiveRoutes([]);
    setReturnRoutes([]);
    setNoRoutes(false);
    setOutboundDone(false);
    setReturnDone(false);
    setTripState(null);   // clear any stale itinerary
    setStage("routing");

    const bId = startBubble("routing");
    stepBubble(bId, `Connecting to RouteChef routing engine…`);
    stepBubble(bId, `Searching routes from ${info.source} → ${info.destination}…`);

    const returnInfo = {
      ...info,
      source: info.destination,
      destination: info.source,
      startDate: (() => {
        const [d, m, y] = info.startDate.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + (info.numDays || 3));
        return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
      })(),
    };

    try {
      let _outDone = false;
      const [outboundRoutes, returnRoutesList] = await Promise.all([
        fetchRoutesViaWS(info, prefs, route => {
          if (!_outDone) setLiveRoutes(prev => [...prev, route]);
        }).then(routes => { _outDone = true; setOutboundDone(true); return routes; }),
        fetchRoutesViaWS(returnInfo, prefs, route => {
          setReturnRoutes(prev => [...prev, route]);
        }).then(routes => { setReturnDone(true); return routes; }),
      ]);
      setLiveRoutes(outboundRoutes);
      setReturnRoutes(returnRoutesList);
      if (outboundRoutes.length === 0 && returnRoutesList.length === 0) {
        setNoRoutes(true);
        addBot("No routes found for these dates. You can skip both and plan places directly, or try different dates.");
        // Auto-proceed to prefs after 2s so user can still see the skip buttons
        setTimeout(() => {
          setStage("prefs");
          setNoRoutes(false);
        }, 2500);
      } else if (outboundRoutes.length === 0) {
        setNoRoutes(true);
        addBot("No outbound routes found — you can skip outbound and pick a return route, or skip both.");
      } else {
        addBot(`Found ${outboundRoutes.length} outbound route${outboundRoutes.length > 1 ? "s" : ""}! Pick the one that works best for you:`);
      }
      doneBubble(bId);
    } catch (e) {
      // console.error("[WS]", e);
      doneBubble(bId);
      addBot(`Route search failed: ${e.message}. Check your connection and try again.`);
      setStage("routing_config");
    }
  }, [startBubble, stepBubble, doneBubble]);

  /* ── route select ── */
  const handleRouteSelect = useCallback(route => {
    if (!route) {
      // Skip outbound — proceed directly to return tab / prefs
      setPickedRouteState(null);
      addBot("Skipping outbound route — you can book separately. Now pick your return route, or skip that too.");
      setStage("awaitingReturn");
      return;
    }
    if (selectedRouteId) return;
    setSelectedRouteId(route.route_id);
    setPickedRoute(route);

    const cls = classifyRoute(route);
    const fare = computeFare(route);
    const dur = computeDuration(route);
    const nodes = route.nodes || [];
    const first = nodes[0]?.segment;
    const last = nodes[nodes.length - 1]?.segment;
    const sub = `${first?.origin_name || first?.origin_code_iata || ""} → ${last?.dest_name || last?.dest_code_iata || ""}`;
    const detail = `${fmtDuration(dur)} · ${route.num_stops || 0} stop${(route.num_stops || 0) !== 1 ? "s" : ""}`;
    const bookLink = route.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;

    setPickedRouteState({
      id: route.route_id, icon: cls.icon, label: cls.label, badge: cls.badge, color: cls.color,
      sub, detail, price: fare || 0, bookingLink: bookLink, rawRoute: route,
    });

    const fareStr = fare ? `₹${fmtPrice(fare)}/person` : "fare varies — check booking link";
    addBot(`${cls.icon} Route selected — ${fareStr}. Now pick your return route below, or skip if not needed.`);
    setStage("awaitingReturn");
  }, [selectedRouteId]);

  /* ── return route handling ── */
  const handleReturnRouteSelect = useCallback(route => {
    const cls = classifyRoute(route);
    const fare = computeFare(route);
    const dur = computeDuration(route);
    const nodes = route.nodes || [];
    const first = nodes[0]?.segment;
    const last = nodes[nodes.length - 1]?.segment;
    const bookLink = route.booking_link || nodes.find(n => n.segment.booking_link)?.segment.booking_link;
    const sub = `${first?.origin_name || first?.origin_code_iata || first?.origin_code || ""} → ${last?.dest_name || last?.dest_code_iata || last?.dest_code || ""}`;
    setPickedReturnRouteState({
      id: route.route_id, icon: cls.icon, label: cls.label, badge: cls.badge, color: cls.color,
      sub, price: fare || 0, bookingLink: bookLink,
      detail: `${fmtDuration(dur)} · ${route.num_stops || 0} stop${(route.num_stops || 0) !== 1 ? "s" : ""}`,
      rawRoute: route,
    });
    addBot(`Return route set ✓. Any vibe preferences for places?`);
    setStage("prefs");
  }, []);

  const handleSkipReturn = useCallback(() => {
    addBot("No return route — noted. Any vibe preferences for places?");
    setStage("prefs");
  }, []);

  /* ── run discovery pipeline ── */
  const runDiscoveryPipeline = useCallback(async (info, prefs) => {
    if (discoveryFiredRef.current) return;
    discoveryFiredRef.current = true;
    lastPrefsRef.current = Array.isArray(prefs) ? prefs : [];
    setBusy(true);
    setDiscoveringPlaces(true);
    addBot(`Finding the best places in ${info.destination} for you…`);

    const bId = startBubble("discovery");
    stepBubble(bId, `Researching ${info.destination}…`);
    stepBubble(bId, "Analysing places by type, crowd level, visit duration…");

    try {
      const result = await consumeSSE("/chat", {
        message: `Discover places for: destination="${info.destination}" source="${info.source}" days=${info.numDays} start="${info.startDate}"`,
        reset: false,
      }, places => {
        if (places?.length > 0) {
          setDiscoveredPlaces(orderPlacesForInterests(places, lastPrefsRef.current));
          setDiscoveringPlaces(false); // places arrived — loader off, deck on
          setStage("places");
          addBot(`Found ${places.length} great spots! Swipe or tap ✓ / ✕ to build your list 👇`);
        }
      });

      doneBubble(bId);
      const s = result?.state || (USE_LAMBDA ? null : await fetch(`${ACTIVE_API}/state?session_id=${sessionId}`, { headers: await freshAuthHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null));
      const places = s?.all_places || [];
      if (places.length > 0 && discoveredPlaces.length === 0) {
        setDiscoveredPlaces(orderPlacesForInterests(places, lastPrefsRef.current));
        setStage("places");
        addBot(`Found ${places.length} great spots! Tap ✓ / ✕ to build your list 👇`);
      } else if (places.length === 0) {
        setStage("places");
        addBot("Discovery found no places — try rephrasing your destination.");
      }
    } catch (e) {
      // console.error("[Discovery]", e);
      doneBubble(bId);
      addBot("Discovery failed — please try again.");
      setStage("places");
    }
    setDiscoveringPlaces(false);
    setBusy(false);
  }, [consumeSSE, sessionId, startBubble, stepBubble, doneBubble, discoveredPlaces]);

  /* ── prefs done → discovery ── */
  const handlePrefsDone = useCallback(prefs => {
    runDiscoveryPipeline(tripInfo, prefs);
  }, [tripInfo, runDiscoveryPipeline]);

  /* ── place add ── */
  const handleAddPlace = p => {
    setSelectedIds(ids => ids.includes(p.id) ? ids : [...ids, p.id]);
    setShowBuildBtn(true);
  };

  /* ── intake handler ── */
  const handleIntake = useCallback(async text => {
    if (!text.trim()) return;
    addUser(text); setInputVal(""); setBusy(true);

    try {
      intakeHistRef.current = [
        ...intakeHistRef.current,
        { role: "user", content: text },
      ];

      const result = await claudeIntake(intakeHistRef.current, userId, sessionId, stage === "done" ? tripInfo : null, await freshIdToken());
      const reply = result.reply || "Could you tell me your destination and travel dates?";

      intakeHistRef.current = [
        ...intakeHistRef.current,
        { role: "assistant", content: reply },
      ];
      addBot(reply);

      if (result.destination) setTripInfo(t => ({ ...t, destination: result.destination }));
      if (result.source) setTripInfo(t => ({ ...t, source: result.source }));
      if (result.numDays) setTripInfo(t => ({ ...t, numDays: result.numDays }));
      if (result.startDate) setTripInfo(t => ({ ...t, startDate: result.startDate }));

      if (result.ready && result.destination && result.source && result.startDate) {
        const info = {
          destination: result.destination,
          source: result.source,
          numDays: result.numDays || 3,
          startDate: result.startDate,
        };
        setTripInfo(info);

        // ── Immediately collapse right panel by resetting stage ──
        // Must happen synchronously before any await so UI reflects new state
        setStage("intake");

        // ── Full reset of previous trip state ──
        setTripState(null);
        setPickedRouteState(null);
        setPickedReturnRouteState(null);
        setPickedRoute(null);
        setSelectedHotel(null);
        setHasStay(false);
        setAllHotels([]);
        setLiveRoutes([]);
        setReturnRoutes([]);
        setNoRoutes(false);
        setOutboundDone(false);
        setReturnDone(false);
        setDiscoveredPlaces([]);
        setSelectedIds([]);
        setShowBuildBtn(false);
        setItineraryStarted(false);
        setNights(info.numDays || 3);
        discoveryFiredRef.current = false;
        intakeHistRef.current = [];

        try {
          await consumeSSE("/chat", { message: `start trip: ${info.destination}`, reset: true }, null);
        } catch (e) {
          // console.warn("[intake] backend reset failed (non-fatal):", e.message);
        }
        setStage("routing_config");
        runRoutingAgent(info);
      } else if (!result.ready && stage === "done") {
        // Detect intent from Claude intake
        const intent = result.intent || "edit";

        if (intent === "change_date" && result.startDate) {
          // Date change — update date and trigger full re-routing + re-discovery
          const newStart = result.startDate; // dd-mm-yyyy
          const newInfo = { ...tripInfo, startDate: newStart };
          setTripInfo(newInfo);

          // Full reset — same as starting a new trip
          setTripState(null);
          setPickedRouteState(null);
          setPickedReturnRouteState(null);
          setPickedRoute(null);
          setSelectedHotel(null);
          setHasStay(false);
          setAllHotels([]);
          setLiveRoutes([]);
          setReturnRoutes([]);
          setDiscoveredPlaces([]);
          setSelectedIds([]);
          setShowBuildBtn(false);
          setItineraryStarted(false);
          setShowItinerary(false);
          setOutboundDone(false);
          setReturnDone(false);
          setNoRoutes(false);
          discoveryFiredRef.current = false;
          intakeHistRef.current = [];

          // Reset backend state (fire and forget — don't await)
          consumeSSE("/chat", { message: `start trip: ${newInfo.destination}`, reset: true }, null).catch(() => { });

          setBusy(false);
          runRoutingAgent(newInfo);
          return;
        }

        // Other edits — pass to backend edit handler
        setBusy(false);
        handleEdit(text);
        return;
      }
    } catch (e) {
      // console.error("[handleIntake]", e);
      addBot("Sorry, something went wrong — could you repeat your destination and travel dates?");
      intakeHistRef.current = intakeHistRef.current.slice(0, -1); // remove failed user msg
    }
    setBusy(false);
  }, [userId, sessionId, stage, tripInfo, consumeSSE, runRoutingAgent]);

  /* ── edit handler (post-done) — only for clear edit requests ── */
  const handleEdit = useCallback(async text => {
    if (!text.trim() || busy) return;
    addUser(text); setInputVal(""); setBusy(true);
    try {
      const result = await consumeSSE("/chat", { message: text }, null);
      const s = result?.state || (USE_LAMBDA ? null : await fetch(`${ACTIVE_API}/state?session_id=${sessionId}`, { headers: await freshAuthHeaders() }).then(r => r.ok ? r.json() : null).catch(() => null));
      if (s?.itinerary_complete && s?.itinerary?.length > 0) {
        setTripState(s);
      }
    } catch (e) {
      // console.error("[Edit]", e);
      addBot("Something went wrong — try rephrasing your request.");
    }
    setBusy(false);
  }, [busy, consumeSSE, sessionId]);

  /* ── unified send — always goes through intake when done, for new-trip detection ── */
  const onSend = useCallback(async () => {
    const text = inputVal.trim();
    if (!text || busy) return;
    // Always use intake when stage is done — Claude decides if it's a new trip or an edit
    if (stage === "intake" || stage === "done") return handleIntake(text);
    addUser(text); setInputVal("");
  }, [inputVal, busy, stage, handleIntake, handleEdit]);

  /* ── Hero handoff: send the queued initiator as a normal user message.
     Runs after the greeting renders; goes through the exact same intake
     path as a typed message (addUser → /api/intake → routing kickoff). ── */
  useEffect(() => {
    if (!pendingInitiator) return;
    const t = setTimeout(() => {
      const text = pendingInitiator;
      setPendingInitiator(null);
      handleIntake(text);
    }, 600);
    return () => clearTimeout(t);
  }, [pendingInitiator, handleIntake]);

  /* ── derived ── */
  const inputDisabled = busy || ["routing", "building", "places", "prefs", "routing_config", "awaitingReturn", "awaitingReturnChange"].includes(stage);
  const placeholder = {
    intake: "Where do you want to go?",
    routing_config: "Waiting for route config…",
    routing: "Searching routes…",
    awaitingReturn: "Pick your return route above…",
    prefs: "👆 Tell us your preferences above",
    places: "👆 Use the cards above to pick places",
    building: "Building your itinerary…",
    done: "Ask to change something, add places, or adjust dates…",
  }[stage] || "Type a message…";

  // Helper function to migrate chat history
  const migrateUserChatHistory = async (oldUserId, newUserId) => {
    try {

      const response = await fetch(`${ACTIVE_API}/migrate`, {
        method: "POST",
        headers: await freshAuthHeaders(),
        body: JSON.stringify({
          old_user_id: oldUserId,
          new_user_id: newUserId,
        }),
      });

      const result = await response.json();

      if (result.success) {
        return result;
      } else {
        return result;
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /* ── Login handlers ── */
  const handleGoogleLogin = async () => {
    setIsLoginLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;


      const email = user.email || "";
      const newUserId = `webbot_trip:${email}`;


      // Migrate chat history
      const migrationResult = await migrateUserChatHistory(USER_ID, newUserId);

      // Update localStorage
      localStorage.setItem("routechef_user_id", newUserId);

      setIsModalOpen(false);
      setMobileNumber("");
      setOtp("");
      setOtpSent(false);

      setTimeout(() => window.location.reload(), 500);

    } catch (error) {
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    try {
      if (!recaptchaVerifier) {
        const verifier = new RecaptchaVerifier(recaptchaContainerId, {
          size: "invisible",
          callback: () => { },
        }, auth);
        setRecaptchaVerifier(verifier);
      }

      const formattedNumber = mobileNumber.startsWith("+") ? mobileNumber : "+" + mobileNumber;
      const confirmationResult = await signInWithPhoneNumber(auth, formattedNumber, recaptchaVerifier);
      window.confirmationResult = confirmationResult;
      setOtpSent(true);
    } catch (error) {
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    try {
      const result = await window.confirmationResult.confirm(otp);
      const user = result.user;


      const phone = user.phoneNumber || "";
      const newUserId = `webbot_trip:${phone}`;


      // Migrate chat history
      const migrationResult = await migrateUserChatHistory(USER_ID, newUserId);

      // Update localStorage
      localStorage.setItem("routechef_user_id", newUserId);

      setIsModalOpen(false);
      setMobileNumber("");
      setOtp("");
      setOtpSent(false);

      setTimeout(() => window.location.reload(), 500);

    } catch (error) {
    } finally {
      setIsLoginLoading(false);
    }
  };

  /* ── Hotel booking handler — navigates to HotelBookingPage ── */
  const handleBookHotel = useCallback((hotel) => {
    if (!hotel || !hotel.hotel_code) return;

    const checkIn = (() => {
      const arrIso = pickedRouteState?.arrival_datetime;
      if (arrIso) {
        const arr = new Date(arrIso);
        return arr.toISOString().split("T")[0];
      }
      if (tripInfo?.startDate) {
        const [d, m, y] = tripInfo.startDate.split("-");
        return `${y}-${m}-${d}`;
      }
      return "";
    })();

    const checkOut = (() => {
      const arrIso = pickedRouteState?.arrival_datetime;
      const base = arrIso ? new Date(arrIso)
        : tripInfo?.startDate
          ? (() => { const [d, m, y] = tripInfo.startDate.split("-"); return new Date(y, m - 1, d); })()
          : null;
      if (!base) return "";
      const co = new Date(base);
      co.setDate(co.getDate() + (nights || 1));
      return co.toISOString().split("T")[0];
    })();

    // Stash hotel context in sessionStorage for HotelBookingPage to pick up
    stashHotelForNavigation(
      hotel,
      checkIn,
      checkOut,
      hotel.selectedRoom || null,
      hotel.images || []
    );

    // Navigate to HotelBookingPage with hotel_code in URL
    navigate(`/trip-planner/hotel/${encodeURIComponent(hotel.hotel_code)}?check_in=${checkIn}&check_out=${checkOut}&user_id=${userId}`);
  }, [pickedRouteState, tripInfo, nights, userId, navigate]);

  /* ── Mobile/Desktop body renderer ── */
  function renderBody() {
    const itineraryReady = tripState?.itinerary_complete === true
      && Array.isArray(tripState?.itinerary)
      && tripState.itinerary.length > 0
      && tripState.itinerary.some(d => d.area_summary || (d.place_visits?.length > 0));

    // Shortcut label — reflects the step that currently needs the user
    const shortcutLabel = itineraryReady ? "📋 View Itinerary"
      : ["prefs", "places"].includes(stage) ? "🗺️ Pick Places"
        : stage === "building" ? "⏳ Trip Panel"
          : "🛫 View Routes";

    // Single chat / side-by-side layout for both breakpoints
    return (
      /* ── Side-by-side (desktop) or chat + overlay (mobile) ── */
      <>
        {/* Chat column */}
        <div style={{
          display: "flex", flexDirection: "column", background: "#fff",
          ...((!isMobile && panelOpen)
            ? { width: "min(700px,55%)", borderRight: "1px solid #F0F0F0", flexShrink: 0 }
            : { flex: 1, maxWidth: 860, margin: "0 auto", width: "100%" }
          ),
        }}>

          {/* Messages + Agent bubbles — merged into one chronological timeline */}
          <div className="chat-messages" style={{
            flex: 1, overflowY: "auto", padding: "28px 32px 16px", paddingTop: "80px",
            display: "flex", flexDirection: "column", gap: 24
          }}>

            {[
              ...msgs.map(m => ({ kind: "msg", seq: m.seq || 0, data: m })),
              ...bubbles.map(b => ({ kind: "bubble", seq: b.seq || 0, data: b })),
            ]
              .sort((a, b) => a.seq - b.seq)
              .map((item, i) => item.kind === "msg" ? (
                <div key={`m${item.data.seq}-${i}`} className="fi">
                  {item.data.role === "bot" ? (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <Sparkle size={20} color={PURPLE} />
                      <div>
                        <div className="bot-text" style={{ fontSize: 15, color: "#1A1A1A", lineHeight: 1.7 }}>
                          {item.data.typing ? (
                            <TypingText
                              text={item.data.text}
                              speed={16}
                              onDone={() => setMsgs(m => m.map(x => x === item.data ? { ...x, typing: false } : x))}
                            />
                          ) : (
                            <span style={{ whiteSpace: "pre-line" }}>{item.data.text}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: "#C0C0C0", marginTop: 5 }}>{item.data.ts}</div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                      <div className="msg-bubble" style={{ background: "#F0F0F0", borderRadius: 20, padding: "11px 18px", maxWidth: "72%" }}>
                        <span style={{ fontSize: 15, color: "#111", lineHeight: 1.6 }}>{item.data.text}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#C0C0C0", marginTop: 5 }}>{item.data.ts}</div>
                    </div>
                  )}
                </div>
              ) : (
                <div key={`b${item.data.id}`} className="fi" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <Sparkle size={20} color={PURPLE} />
                  <AgentBubble agent={item.data.agent} steps={item.data.steps} done={item.data.done} />
                </div>
              ))}







            {busy && (
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <Sparkle size={20} color={PURPLE} />
                <div style={{ display: "flex", gap: 5, alignItems: "center", padding: "6px 0" }}>
                  {[0, 1, 2].map(i => (
                    <span key={i} style={{
                      width: 7, height: 7, borderRadius: "50%",
                      background: "#D0D0D0", display: "inline-block",
                      animation: `agentBounce 1.2s ${i * .2}s ease-in-out infinite`
                    }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input bar */}
          <div className="input-bar" style={{ padding: "12px 24px 20px", flexShrink: 0 }}>
            <div className="input-wrap">
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "12px 16px", background: "#fff", borderRadius: 97
              }}>
                <Sparkle size={18} color={PURPLE} />
                <input
                  value={inputVal}
                  onChange={e => setInputVal(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                  placeholder={placeholder}
                  disabled={inputDisabled}
                  style={{
                    flex: 1, border: "none", outline: "none",
                    fontSize: 15, color: "#111", background: "transparent",
                    fontFamily: "inherit", opacity: inputDisabled ? 0.5 : 1
                  }}
                />
                <button onClick={onSend} disabled={inputDisabled || !inputVal.trim()} style={{
                  width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                  background: inputVal.trim() && !inputDisabled ? ORANGE : "#E8E8E8",
                  border: "none", cursor: inputVal.trim() && !inputDisabled ? "pointer" : "default",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "background 0.15s",
                }}>
                  <svg width={15} height={15} viewBox="0 0 24 24" fill="none"
                    stroke="#fff" strokeWidth={2.5} strokeLinecap="round">
                    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </div>{/* chat column */}

        {/* Right panel — step-wise widget: desktop side panel / mobile fullscreen overlay */}
        {panelOpen && (() => {
          const routingReached = ["routing_config", "routing", "awaitingReturn", "awaitingReturnChange"].includes(stage);
          const routingDone = !!pickedRouteState || ["prefs", "places", "building", "done"].includes(stage);
          const discoveryDone = itineraryStarted || stage === "done";
          const STEPS = [
            { k: "routing", n: 1, icon: "🛫", label: "Routing", done: routingDone },
            { k: "discovery", n: 2, icon: "🗺️", label: "Discover", done: discoveryDone },
            { k: "hotels", n: 3, icon: "🏨", label: "Hotels", done: hasStay && !!selectedHotel },
            { k: "itinerary", n: 4, icon: "📋", label: "Itinerary", done: false },
          ];
          const AwaitCard = ({ icon, title, sub }) => (
            <div style={{
              border: "1px dashed #E4E4E4", borderRadius: 16,
              padding: "28px 20px", textAlign: "center", maxWidth: 380, margin: "24px auto 0",
            }}>
              <div style={{ fontSize: 30, marginBottom: 10 }}>{icon}</div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#333", marginBottom: 4 }}>{title}</div>
              <div style={{ fontSize: 12, color: "#999", lineHeight: 1.6 }}>{sub}</div>
            </div>
          );
          const closeBtn = (size = 28) => (
            <button
              onClick={() => setPanelOpen(false)}
              aria-label="Close trip panel"
              title="Close"
              style={{
                width: size, height: size, borderRadius: "50%", border: "none",
                background: "#F0F0F0", cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: size > 28 ? 18 : 15, color: "#555", fontWeight: 700, lineHeight: 1,
                WebkitTapHighlightColor: "transparent",
                transition: "background 0.15s",
              }}
            >×</button>
          );
          const panelInner = (
            <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
              {/* Step header — on mobile it collapses away once the itinerary
                  is scrolled, giving the day list the full screen */}
              <div style={{
                paddingTop: isMobile ? 0 : "80px",
                borderBottom: "1px solid #F0F0F0",
                background: "#fff",
                flexShrink: 0,
                display: (isMobile && itinScrolled && activeStep === "itinerary") ? "none" : "flex",
                alignItems: "flex-end",
              }}>
                {STEPS.map(s => {
                  const active = activeStep === s.k;
                  return (
                    <button key={s.k} onClick={() => setActiveStep(s.k)} style={{
                      flex: 1, padding: isMobile ? "11px 2px" : "12px 8px", border: "none", background: "transparent",
                      cursor: "pointer", fontFamily: "inherit",
                      borderBottom: active ? `2px solid ${ORANGE}` : "2px solid transparent",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: isMobile ? 5 : 8,
                      transition: "all 0.15s",
                      WebkitTapHighlightColor: "transparent",
                    }}>
                      <span style={{
                        width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 800,
                        background: s.done ? GREEN : active ? ORANGE : "#EDEDED",
                        color: s.done || active ? "#fff" : "#999",
                        transition: "background 0.2s",
                      }}>
                        {s.done ? "✓" : s.n}
                      </span>
                      <span style={{
                        fontSize: isMobile ? 12 : 13, fontWeight: 600, whiteSpace: "nowrap",
                        color: active ? ORANGE : s.done ? "#333" : "#999",
                      }}>
                        {isMobile ? s.label : `${s.icon} ${s.label}`}
                      </span>
                    </button>
                  );
                })}
                {/* Desktop corner close — mobile overlay has its own × in the header row */}
                {!isMobile && (
                  <div style={{ padding: "0 12px 9px 4px" }}>
                    {closeBtn(28)}
                  </div>
                )}
              </div>

              {/* ── STEP 1: ROUTING (InlineRouteSheet keeps its own Outbound/Return sub-tabs) ── */}
              {activeStep === "routing" && (
                <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                  {stage === "intake" && (
                    <AwaitCard icon="🛫" title="Routing starts here"
                      sub="Tell me your destination & dates in the chat — outbound and return routes will appear in this panel." />
                  )}
                  {stage === "routing_config" && showRoutingConfig && (
                    <div className="fi">
                      <RoutingConfig
                        defaults={routingPrefs}
                        onStart={prefs => { setRoutingPrefs(prefs); startWsRouting(tripInfo, prefs); }}
                        onSkip={() => startWsRouting(tripInfo, routingPrefs)}
                      />
                    </div>
                  )}
                  {(stage === "routing" || stage === "awaitingReturn") && (
                    <div className="fi">
                      <InlineRouteSheet
                        fullWidth
                        routes={liveRoutes}
                        returnRoutes={returnRoutes}
                        selectedRouteId={selectedRouteId}
                        outboundDone={outboundDone}
                        returnDone={returnDone}
                        onSelect={handleRouteSelect}
                        onSelectReturn={handleReturnRouteSelect}
                        onSkipReturn={handleSkipReturn}
                        noRoutes={noRoutes}
                        tripInfo={tripInfo}
                        onSeatAvail={handleSeatAvailCheck}
                      />
                    </div>
                  )}
                  {stage === "awaitingReturnChange" && (
                    <div className="fi">
                      {routeSheetNote && (
                        <div style={{
                          background: `${ORANGE}12`, border: `1px solid ${ORANGE}40`,
                          borderRadius: 10, padding: "10px 14px", marginBottom: 12,
                          display: "flex", alignItems: "flex-start", gap: 8,
                        }}>
                          <span style={{ fontSize: 16, flexShrink: 0 }}>📅</span>
                          <div style={{ fontSize: 12, color: "#7A3500", lineHeight: 1.5 }}>{routeSheetNote}</div>
                        </div>
                      )}
                      <InlineRouteSheet
                        fullWidth
                        routes={liveRoutes}
                        returnRoutes={returnRoutes}
                        selectedRouteId={pickedRouteState?.id || null}
                        outboundDone={outboundDone || liveRoutes.length > 0}
                        returnDone={returnDone}
                        returnSearching={returnSearching}
                        onSelect={() => { }} /* outbound already picked — ignore */
                        onSelectReturn={isReturnForRouteChangeRef.current
                          ? (r) => {
                            // Route-change mode: just set return + refresh itinerary
                            isReturnForRouteChangeRef.current = false;
                            handleRouteChange(r, true);
                            setStage("done");
                            setRouteSheetNote(null);
                          }
                          : handleReturnRouteForNightsChange}
                        onSkipReturn={() => {
                          isReturnForRouteChangeRef.current = false;
                          setStage("done");
                          setShowItinerary(true);
                          setRouteSheetNote(null);
                          addBot("Skipped return route — itinerary kept as-is.");
                        }}
                        noRoutes={false}
                        tripInfo={tripInfo}
                        defaultTab="return"
                        onSeatAvail={handleSeatAvailCheck}
                      />
                    </div>
                  )}
                  {/* Post-routing: routes stay visible — picking one behaves exactly like
                      the Edit-route button in itinerary view (handleRouteChange). */}
                  {routingDone && !routingReached && stage !== "intake" && (
                    (liveRoutes.length > 0 || returnRoutes.length > 0) ? (
                      <div className="fi" style={{
                        pointerEvents: (stage === "building" || busy) ? "none" : "auto",
                        opacity: (stage === "building" || busy) ? 0.55 : 1,
                        transition: "opacity 0.2s",
                      }}>
                        <div style={{ fontSize: 12, color: "#999", marginBottom: 10, lineHeight: 1.5 }}>
                          Tap any route to switch — your itinerary updates automatically.
                        </div>
                        <InlineRouteSheet
                          fullWidth
                          hideSkip
                          routes={liveRoutes}
                          returnRoutes={returnRoutes}
                          selectedRouteId={pickedRouteState?.id || null}
                          selectedReturnRouteId={pickedReturnRouteState?.id || null}
                          outboundDone={true}
                          returnDone={true}
                          returnSearching={returnSearching}
                          onSelect={r => { if (r) handleRouteChange(buildPickedRouteFromRaw(r), false); }}
                          onSelectReturn={r => handleRouteChange(buildPickedRouteFromRaw(r), true)}
                          onSkipReturn={() => { }}
                          noRoutes={noRoutes}
                          tripInfo={tripInfo}
                          onSeatAvail={handleSeatAvailCheck}
                        />
                      </div>
                    ) : (
                      <div className="fi" style={{
                        background: "#E8F7F2", border: `1px solid ${GREEN}33`, borderRadius: 14,
                        padding: "14px 16px",
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: GREEN, marginBottom: 6, letterSpacing: 0.5 }}>✓ ROUTING DONE</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
                          {tripInfo?.source || "—"} → {tripInfo?.destination || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>
                          {pickedRouteState ? "Outbound selected" : "Outbound skipped"}
                          {" · "}
                          {pickedReturnRouteState ? "return selected" : "return skipped"}
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}

              {/* ── STEP 2: DISCOVERY (prefs + swipeable place cards) ── */}
              {activeStep === "discovery" && (
                <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                  {!["prefs", "places", "building", "done"].includes(stage) && !discoveringPlaces && (
                    <AwaitCard icon="🗺️" title="Place discovery"
                      sub="Once your route is set, swipe through hand-picked places for your trip here — right to add, left to skip." />
                  )}
                  {stage === "prefs" && !discoveringPlaces && (
                    <div className="fi">
                      <PreferenceScreen destination={tripInfo.destination} onDone={handlePrefsDone} />
                    </div>
                  )}
                  {/* AI discovery in flight — preferences submitted, places on the way */}
                  {discoveringPlaces && discoveredPlaces.length === 0 && (
                    <div className="fi" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "44px 0", gap: 14, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 10, height: 10, borderRadius: "50%", background: PURPLE,
                            display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>
                        AI is discovering places in {tripInfo?.destination || "your destination"}…
                      </div>
                      <div style={{ fontSize: 12, color: "#999", maxWidth: 300, lineHeight: 1.6 }}>
                        Analysing attractions by type, crowd levels & visit time — your swipe deck will appear here in a moment.
                      </div>
                    </div>
                  )}
                  {stage === "places" && discoveredPlaces.length > 0 && (
                    <div className="fi">
                      <div style={{ fontSize: 14, color: "#1A1A1A", lineHeight: 1.6, marginBottom: 12, fontWeight: 600 }}>
                        Found {discoveredPlaces.length} great spots! Swipe or tap ✓ / ✕ to build your list 👇
                      </div>
                      <PlaceDeck
                        places={discoveredPlaces}
                        onAdd={handleAddPlace}
                        onSkip={() => { }}
                        selectedIds={selectedIds}
                        locked={itineraryStarted || busy}
                        resetKey={deckResetKey}
                        onRevisit={() => {
                          setSelectedIds([]);
                          setShowBuildBtn(false);
                          setDeckResetKey(k => k + 1); // remount deck with fresh decided state
                          addBot("Sure! Re-selecting places — swipe through again and tap Build when ready 🔄");
                        }}
                      />
                      {showBuildBtn && !itineraryStarted && (
                        <button onClick={buildItinerary} disabled={busy} style={{
                          marginTop: 16,
                          background: ORANGE, border: "none", borderRadius: 99,
                          padding: "13px 32px", color: "#fff", fontWeight: 700, fontSize: 15,
                          cursor: busy ? "default" : "pointer", fontFamily: "inherit",
                          opacity: busy ? 0.6 : 1, boxShadow: `0 4px 20px ${ORANGE}40`,
                        }}>
                          Build My Itinerary ({selectedIds.length} place{selectedIds.length !== 1 ? "s" : ""} selected) →
                        </button>
                      )}
                    </div>
                  )}
                  {stage === "places" && discoveredPlaces.length === 0 && !discoveringPlaces && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "36px 0", gap: 12 }}>
                      <Spinner color={PURPLE} />
                      <div style={{ fontSize: 13, color: "#888", fontWeight: 600 }}>Discovering places…</div>
                    </div>
                  )}
                  {stage === "building" && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "36px 0", gap: 12 }}>
                      <Spinner color={ORANGE} />
                      <div style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>
                        Building your itinerary with {selectedIds.length} place{selectedIds.length !== 1 ? "s" : ""}…
                      </div>
                    </div>
                  )}
                  {stage === "done" && (
                    <div className="fi" style={{
                      background: "#E8F7F2", border: `1px solid ${GREEN}33`, borderRadius: 14,
                      padding: "14px 16px", maxWidth: 420,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: GREEN, marginBottom: 6, letterSpacing: 0.5 }}>✓ PLACES SELECTED</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }}>
                        {tripState?.selected_place_ids?.length || selectedIds.length} place{(tripState?.selected_place_ids?.length || selectedIds.length) !== 1 ? "s" : ""} in your trip
                      </div>
                      <div style={{ fontSize: 12, color: "#666", marginTop: 3 }}>
                        Head to Step 4 to review the full plan
                      </div>
                      {/* Same behavior as legacy deck's "Change places" — reset selection,
                          remount deck, rewind to places stage for re-swiping */}
                      {discoveredPlaces.length > 0 && (
                        <button onClick={() => {
                          setSelectedIds([]);
                          setShowBuildBtn(false);
                          setItineraryStarted(false);
                          setDeckResetKey(k => k + 1); // remount deck with fresh decided state
                          setStage("places");
                          addBot("Sure! Re-selecting places — swipe through again and tap Build when ready 🔄");
                        }} style={{
                          marginTop: 12,
                          padding: "9px 20px", background: PURPLE, border: "none", borderRadius: 99,
                          cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13, color: "#fff",
                          boxShadow: `0 3px 12px ${PURPLE}33`,
                        }}>
                          ✏️ Change places
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── STEP 3: HOTELS — same list & selection behavior as the legacy
                     Edit-hotel sheet in itinerary view (HotelSheet), rendered inline.
                     Populates live as /hotels responds (manual search or during build). ── */}
              {activeStep === "hotels" && (
                <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                  {allHotels.length > 0 ? (
                    <div className="fi">
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>Choose Hotel & Room</div>
                        <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                          {allHotels.length} hotel{allHotels.length !== 1 ? "s" : ""} available
                          {selectedHotel ? ` · ${selectedHotel.hotel_name || "1 selected"} ✓` : ""}
                        </div>
                      </div>
                      {/* Accommodation call in progress — refreshing rates/dates */}
                      {hotelSearchStatus === "loading" && (
                        <div style={{
                          display: "flex", alignItems: "center", gap: 10,
                          background: "#FFF7F2", border: `1px solid ${ORANGE}33`,
                          borderRadius: 10, padding: "8px 12px", marginBottom: 12,
                        }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            {[0, 1, 2].map(i => (
                              <span key={i} style={{
                                width: 7, height: 7, borderRadius: "50%", background: ORANGE,
                                display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                              }} />
                            ))}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "#92400E" }}>
                            Updating hotels for your new dates…
                          </span>
                        </div>
                      )}
                      <HotelListInline
                        hotels={allHotels}
                        selectedHotel={selectedHotel}
                        onSelect={h => { setSelectedHotel(h); setHasStay(true); }}
                      />
                      {hasStay && (
                        <button onClick={() => setHasStay(false)} style={{
                          marginTop: 12, width: "100%", padding: "9px 0",
                          background: "#F5F5F7", border: "1.5px solid #E0E0E0", borderRadius: 10,
                          cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "#666",
                        }}>Remove stay from trip →</button>
                      )}
                    </div>
                  ) : hotelSearchStatus === "loading" ? (
                    /* Search in flight — loader + status */
                    <div className="fi" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0", gap: 14, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 10, height: 10, borderRadius: "50%", background: ORANGE,
                            display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>
                        Searching hotels near {tripInfo?.destination || "your destination"}…
                      </div>
                      <div style={{ fontSize: 12, color: "#999", maxWidth: 280, lineHeight: 1.6 }}>
                        Fetching live rooms & rates — results will appear here as they arrive.
                      </div>
                    </div>
                  ) : hotelSearchStatus === "done" ? (
                    /* Search finished, nothing found */
                    <div className="fi" style={{ textAlign: "center", padding: "32px 0" }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>🏨</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#333" }}>No hotels found</div>
                      <div style={{ fontSize: 12, color: "#888", marginTop: 6, marginBottom: 16 }}>
                        Nothing within 20km of {tripInfo?.destination || "your destination"} for these dates.
                      </div>
                      <button onClick={() => { if (tripInfo?.destination) fetchHotels(tripInfo.destination); }} style={{
                        padding: "9px 20px", background: "#fff", border: `1px solid ${ORANGE}`, color: ORANGE,
                        borderRadius: 99, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13,
                      }}>
                        ↺ Search again
                      </button>
                    </div>
                  ) : hotelSearchStatus === "error" ? (
                    /* Search failed */
                    <div className="fi" style={{ textAlign: "center", padding: "32px 0" }}>
                      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#333" }}>Couldn't load hotels</div>
                      <div style={{ fontSize: 12, color: "#888", marginTop: 6, marginBottom: 16 }}>
                        Something went wrong while searching. Give it another try.
                      </div>
                      <button onClick={() => { if (tripInfo?.destination) fetchHotels(tripInfo.destination); }} style={{
                        padding: "9px 20px", background: ORANGE, border: "none", color: "#fff",
                        borderRadius: 99, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13,
                        boxShadow: `0 3px 14px ${ORANGE}40`,
                      }}>
                        ↺ Retry search
                      </button>
                    </div>
                  ) : (
                    /* Idle — nothing searched yet */
                    <>
                      <AwaitCard icon="🏨" title="Hotels for your stay"
                        sub={tripInfo?.destination
                          ? `Hotels near your places in ${tripInfo.destination} will be listed here.`
                          : "Hotels appear here once your destination is set."} />
                      {tripInfo?.destination && ["prefs", "places", "building", "done"].includes(stage) && (
                        <div style={{ textAlign: "center", marginTop: 14 }}>
                          <button onClick={() => { if (tripInfo?.destination) fetchHotels(tripInfo.destination); }} style={{
                            padding: "10px 22px", background: ORANGE, border: "none", borderRadius: 99,
                            cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13, color: "#fff",
                            boxShadow: `0 3px 14px ${ORANGE}40`,
                          }}>
                            🏨 Search hotels in {tripInfo.destination}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── STEP 4: ITINERARY ── */}
              {activeStep === "itinerary" && (
                isRefreshing ? (
                  /* Route/nights/places changed — itinerary is being rebuilt */
                  <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                    <div className="fi" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 0", gap: 14, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 10, height: 10, borderRadius: "50%", background: ORANGE,
                            display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>
                        Regenerating your itinerary…
                      </div>
                      <div style={{ fontSize: 12, color: "#999", maxWidth: 300, lineHeight: 1.6 }}>
                        Re-planning your days around the updated trip — hotels refresh right after.
                      </div>
                    </div>
                  </div>
                ) : itineraryReady ? (
                  <>
                    <div style={{
                      padding: "12px 20px 10px",
                      borderBottom: "1px solid #F0F0F0",
                      background: "#fff",
                      flexShrink: 0,
                      display: (isMobile && itinScrolled) ? "none" : "block",
                    }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "#111" }}>Your Itinerary</div>
                      <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                        {`${nights} nights · ${(tripState?.selected_place_ids?.length ?? tripState?.selected_places?.length) || selectedIds.length || 0} places · tap any day to expand`}
                      </div>
                    </div>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <ItineraryPanel
                        tripState={tripState} nights={nights}
                        route={pickedRouteState} returnRoute={pickedReturnRouteState}
                        selectedHotel={selectedHotel} hasStay={hasStay} allHotels={allHotels}
                        tripInfo={tripInfo} wsRoutes={liveRoutes} returnRoutes={returnRoutes}
                        onHotelChange={h => { setSelectedHotel(h); setHasStay(true); }}
                        onFetchHotels={() => { if (allHotels.length <= 1 && tripInfo?.destination) fetchHotels(tripInfo.destination); }}
                        onBookHotel={() => handleBookHotel(selectedHotel)}
                        onRouteChange={r => handleRouteChange(r, false)}
                        onReturnRouteChange={r => handleRouteChange(r, true)}
                        onNightsChange={n => { setNights(n); }}
                        onRefreshItinerary={refreshItinerary}
                        isRefreshing={isRefreshing}
                        onNightsChangeOpenSheet={handleNightsChangeOpenSheet}
                        returnSearching={returnSearching}
                        routeSheetNote={routeSheetNote}
                        routeSheetInitTab={routeSheetInitTab}
                        onRouteSheetClose={() => { setRouteSheetNote(null); setRouteSheetInitTab("outbound"); }}
                        onRemoveStay={() => setHasStay(false)}
                        onAddStay={() => setHasStay(true)}
                        onRemoveRoute={() => setPickedRouteState(null)}
                        onEditRoute={() => setActiveStep("routing")}
                        onSeatAvail={handleSeatAvailCheck}
                        onCompactChange={setItinScrolled}
                        onEditHotel={() => {
                          setActiveStep("hotels");
                          // same behavior as legacy Edit-hotel button's onFetchHotels
                          if (allHotels.length <= 1 && tripInfo?.destination) fetchHotels(tripInfo.destination);
                        }}
                      />
                    </div>
                  </>
                ) : stage === "building" ? (
                  /* Building in progress — live loader */
                  <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                    <div className="fi" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "48px 0", gap: 14, textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[0, 1, 2].map(i => (
                          <span key={i} style={{
                            width: 10, height: 10, borderRadius: "50%", background: PURPLE,
                            display: "inline-block", animation: `agentBounce 1.2s ${i * 0.2}s ease-in-out infinite`,
                          }} />
                        ))}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#333" }}>
                        Building your day-by-day itinerary…
                      </div>
                      <div style={{ fontSize: 12, color: "#999", maxWidth: 300, lineHeight: 1.6 }}>
                        Placing {selectedIds.length > 0 ? `your ${selectedIds.length} place${selectedIds.length !== 1 ? "s" : ""}` : "your trip"} across {nights} night{nights !== 1 ? "s" : ""}, matching hotels & travel times. It'll appear here in a moment.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
                    <AwaitCard icon="📋" title="Final itinerary"
                      sub="Your day-by-day plan builds up here after Steps 1 & 2." />
                  </div>
                )
              )}
            </div>
          );

          // Mobile: fullscreen overlay over chat (chat keeps state underneath),
          // same pattern as the legacy itinerary overlay. Desktop: side panel.
          return isMobile ? (
            <div style={{
              position: "absolute", top: 60, left: 0, right: 0, bottom: 0, zIndex: 15,
              display: "flex", flexDirection: "column",
              background: "#fff",
            }}>
              {/* Overlay header with corner close */}
              <div style={{
                padding: "16px 16px 10px",
                borderBottom: "1px solid #F0F0F0",
                background: "#fff",
                flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>Plan your trip</div>
                  <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>
                    {tripInfo?.destination
                      ? `${tripInfo?.source || "Home"} → ${tripInfo.destination} · ${nights} night${nights !== 1 ? "s" : ""}`
                      : "Step-by-step trip builder"}
                  </div>
                </div>
                {closeBtn(32)}
              </div>
              {panelInner}
            </div>
          ) : panelInner;
        })()}

        {/* Reopen shortcut — floating pill when the panel is closed */}
        {!panelOpen && stage !== "intake" && (
          <button
            onClick={() => setPanelOpen(true)}
            style={{
              position: "absolute", bottom: 92, right: 16, zIndex: 12,
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 18px",
              background: ORANGE, border: "none", borderRadius: 99,
              cursor: "pointer", fontFamily: "inherit",
              fontWeight: 600, fontSize: 13, color: "#fff",
              boxShadow: `0 3px 14px ${ORANGE}40`,
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {shortcutLabel} →
          </button>
        )}
      </>
    );
  }


  /* ── RENDER ── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html,body{overscroll-behavior:none;background:#fff;-webkit-text-size-adjust:100%;height:100%;overflow:hidden}
        input,button,textarea{-webkit-tap-highlight-color:transparent;touch-action:manipulation}
        *{-webkit-font-smoothing:antialiased}
        body{font-family:'Inter',-apple-system,system-ui,sans-serif;background:#fff}

        @keyframes agentPulse  {0%,100%{box-shadow:0 0 0 0 rgba(107,63,160,.4)}50%{box-shadow:0 0 0 6px rgba(107,63,160,0)}}
        @keyframes agentBounce {0%,100%{transform:translateY(0);opacity:.4}50%{transform:translateY(-4px);opacity:1}}
        @keyframes rcspin      {to{transform:rotate(360deg)}}
        @keyframes sheetUp     {from{transform:translateY(100%)}to{transform:translateY(0)}}
        @keyframes fadeUp      {from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes gradPulse   {0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
        @keyframes cursorBlink  {0%,100%{opacity:1}50%{opacity:0}}

        .fi{animation:fadeUp .35s cubic-bezier(.22,.68,0,.99) both}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:#E8E8E8;border-radius:99px}

        .input-wrap{
          border-radius:99px;
          border:2px solid transparent;
          background:
            linear-gradient(#fff,#fff) padding-box,
            linear-gradient(90deg,${PURPLE},#C850C0,${ORANGE}) border-box;
          background-size:200% 200%;
          animation:gradPulse 4s ease infinite;
        }
        button:active{transform:scale(0.97)}

        /* ── Mobile responsive ── */
        @media (max-width: 600px) {
          .chat-messages  { padding: 16px 16px 12px !important; gap: 16px !important; }
          .chat-col-pre   { width: 100% !important; max-width: 100% !important; margin: 0 !important; }
          .input-bar      { padding: 10px 12px 16px !important; }
          .nav-bar        { padding: 10px 16px !important; }
          .msg-bubble     { max-width: 88% !important; }
          .chat-indent    { padding-left: 20px !important; }
          .bot-text       { font-size: 14px !important; }
          .place-deck-card      { width: calc(100vw - 64px) !important; max-width: 320px !important; }
          .route-card           { max-width: calc(100vw - 32px) !important; }
          .inline-route-sheet   { max-width: calc(100vw - 16px) !important; }
          /* chat strip compact mode */
          .chat-strip .chat-messages { padding: 8px 14px 4px !important; gap: 8px !important; }
          .chat-strip .input-bar     { padding: 6px 12px 10px !important; }
        }
        @media (max-width: 390px) {
          .chat-messages { padding: 12px 12px 10px !important; }
          .input-wrap { border-radius: 20px !important; }
        }
      `}</style>

      <div style={{
        position: "fixed", inset: 0,
        display: "flex", flexDirection: "column",
        fontFamily: "'Inter',-apple-system,system-ui,sans-serif",
        background: "#fff",
      }}>

        {/* Nav */}
        <Navbar />

        {/* Body — mobile stacked with drag, desktop side-by-side */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>
          {renderBody()}

        </div>{/* end body wrapper */}

      </div>{/* end height:100vh */}
      <div id={recaptchaContainerId}></div>

      <LoginModal
        isOpen={isModalOpen}
        onClose={() => { setUserClickSignIn(false); setIsModalOpen(false); }}
        onGoogleLogin={handleGoogleLogin}
        onSendOtp={handleSendOtp}
        onVerifyOtp={handleVerifyOtp}
        mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber}
        otp={otp}
        setOtp={setOtp}
        otpSent={otpSent}
        isLoginLoading={isLoginLoading}
        isUserClickSignIn={userClickSignIn}
      />

    </>
  );
}


/* ════════════════════════════════════════════════════════════════════
   CLAUDE INTAKE (proxied via backend /api/intake)
════════════════════════════════════════════════════════════════════ */
async function claudeIntake(conversationHistory, userId, sessionId, existingTrip = null, idToken = null) {
  try {
    const resp = await fetch(`${(LONG_RUNNING_API || ACTIVE_API)}/api/intake`, {
      method: "POST",
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({
        conversationHistory,
        user_id: userId || "",
        session_id: sessionId || "",
        existing_trip: existingTrip || {}, // {} not null — backend model requires a dict; {} is falsy in Python so semantics are unchanged  // tells Claude an itinerary already exists
      }),
    });
    if (!resp.ok) throw new Error(`API ${resp.status}`);
    const data = await resp.json();
    const text = data.text || "{}";
    const clean = text.replace(/```json|```/g, "").trim();
    return JSON.parse(clean);
  } catch (e) {
    // console.error("[claudeIntake]",e);
    return {
      reply: "Sorry, I had a hiccup — could you tell me your destination and travel dates?",
      destination: "", source: "", numDays: 0, startDate: "", ready: false
    };
  }
}


